// src/components/crm/JobFormModal.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Plus, Trash2, FileText, Link as LinkIcon, Lock, Save, Wrench, Package, Search, Loader2, Bell, BellOff, Users, User, MapPin, Clock, CheckCircle, AlertTriangle, AlertCircle, MessageSquare, ChevronDown, ChevronUp, Pencil, Ban } from 'lucide-react'; // Pencil, Ban: jobtimefix-4a9e2d73-b
import PerCrewScheduler from './PerCrewScheduler'; 
import { db, functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, addDoc, setDoc, updateDoc, serverTimestamp, query, where, onSnapshot, orderBy, arrayUnion, getDocs, getDoc, deleteField } from 'firebase/firestore'; // getDoc: noteleak-3a9f2c71; deleteField: allkeep-6c2d9e14
import { formatCurrency, toJsDate, formatLocalDate, formatJobNumber } from '../../utils/formatters'; // localstamp-2d6b9f43; formatJobNumber: jobnumdisp-6a1e9c34
import AddCompletionNoteModal from './AddCompletionNoteModal';
import { explainChecklistRefusal, isChecklistOfficeRole, attachChecklistsToNewJob, newJobChecklistFailureMessage } from '../../utils/checklistClient'; // chklstweb-3a7f1c58; the last three chklstnew-5a8c2e71
import NewJobChecklistsSection from '../checklists/NewJobChecklistsSection'; // chklstnew-5a8c2e71
import useJobChecklists from '../../hooks/useJobChecklists'; // chklstweb-3a7f1c58
import JobChecklistsPanel from '../checklists/JobChecklistsPanel'; // chklstweb-3a7f1c58
// r1-3i-jfm-imports-4f7b2c91 — Notes & Media section deps
import useOrgNotes from '../../hooks/useOrgNotes';
import NoteComposer from './NoteComposer';
import NoteCard from './NoteCard';
// wppromote-6a3f1e94 — Seam 3 (Promote): append into Work Performed, mark on save.
// wpauthor-5d3f8a26 — the promoteNote import is GONE: Work Performed and its
// Promote button both moved to the quick view, so nothing in this file appends,
// tests survival, or marks a note any more.
import CancellationReasonModal from './CancellationReasonModal';
import CustomerCombobox from './CustomerCombobox'; // custpick-8e5a2c71
import { isWithinInterval, addHours, subHours, format } from 'date-fns'; // format added: r1-rec-modal-exception-e8c3a7d5; startOfDay dropped: orgtime-7c2e4b19
import { advanceRecurrence } from '../../utils/recurrence'; // Custom recurrence: shared date-step helper (summary + end snapping)
import ResourceConflictModal from './ResourceConflictModal';
import DateTimePicker from '../common/DateTimePicker';
import { can, PERMISSIONS, canCorrectJobTime, canSeeAllCustomers as isOfficeOrAbove } from '../../utils/permissions'; // canCorrectJobTime: jobtimefix-4a9e2d73-b; isOfficeOrAbove (Office Staff + high-level): h1job-9c4e7b18
import JobPunchCorrectionModal from './JobPunchCorrectionModal'; // jobtimefix-4a9e2d73-b
import { canSeeLaborCost, rateForRow } from '../../utils/jobCosts'; // s6costing-4d8a2f63
import { useJobCosts } from '../../hooks/useJobCosts'; // s6costing-4d8a2f63
import { resolveBillLabor, priorBilledFromInvoices, computeLaborBilling } from '../../utils/laborBilling'; // laborbill-one-rule-6c2a9e47
import * as lineIds from '../../utils/lineIds'; // lineidweb-9e4a2d17 — permanent per-line IDs + the one template function
import { dropBlankLines } from '../../utils/jobLines'; // nolines-59a4771f — blank rows never block saving a Job
import { buildAddressString, validateAddressViaCF } from '../../utils/address';
import { buildBillToFromProperty, buildBillToFromCustomer, normalizeBillTo } from '../../utils/billTo'; // billto-jfm-import-5c2f9a64
import { getArrivalWindow, getArrivalWindows, resolveArrivalWindowKey, buildArrivalWindowSnapshot } from '../../utils/arrivalWindows'; // aw-jfm-4d5e6f70 // aw-seam4
import { classifyTechScheduleConflicts } from '../../utils/scheduleConflicts'; // dbl-book-seam1-7f3c9e21
import { resolveFirstAvailableDate, browserDates } from '../../utils/firstAvailableDate'; // fa-jfm-3a91b7e2
import { pickerItems } from '../../utils/catalogUi'; // catscreen-7a2c4e91 — pickers hide archived items
import { resolveOrgTimeZone, orgYmd, orgDayStart, orgDayEnd, orgEndOfDay } from '../../utils/orgTime'; // orgtime-7c2e4b19 — end dates and reminder days are COMPANY days
import { seriesTimesForAllEvents, recurrenceForSeriesWrite, storedNeverEnds } from '../../utils/seriesAllEvents'; // allkeep-6c2d9e14 — "All events" keeps the series' own start; a saved rule replaces the old one
import { calculateReminderDate, openReminderGuard, reminderFollowsChange } from '../../utils/reminderDate'; // orgtime-7c2e4b19 — one reminder rule, web + phone; guard: remopen-3f8b1d62
import { reconstructLaborEntriesFromAssignments, resolveSaveAssignees, newEventLaborSeed } from '../../utils/eventAssignees'; // apptassign-6e1c9b47 — ticked people ARE the assignee, Jobs and appointments alike; newEventLaborSeed: phone16-7c2e9a41
import LineDescriptionInput from '../documents/LineDescriptionInput'; // linetext-4d8b2e61 — Enter, indents and "- " bullets in line descriptions

// Custom-recurrence UI helpers (web). Both drive off the shared advanceRecurrence
// so there is never a second date-math implementation.
// orgtime-7c2e4b19 — both step the VISITS themselves (start time included) and compare COMPANY days
// (tz = companySettings.timezone), the same rule the server generators use: a series runs through
// its end date's company day, whatever zone the browser is in.
const enumerateOccurrences = (start, recurrence, { max = 500, tz } = {}) => {
    if (!start) return [];
    const out = [];
    let cur = new Date(start);
    const endYmd = recurrence.neverEnds || !recurrence.endDate ? null : orgYmd(recurrence.endDate, tz);
    let guard = 0;
    while (guard++ < 2000 && out.length < max) {
        if (endYmd && orgYmd(cur, tz) > endYmd) break;
        out.push(new Date(cur));
        const nx = advanceRecurrence(cur, recurrence, start);
        if (!nx) break;
        cur = nx;
    }
    return out;
};

// Snap a picked end date DOWN to the last on-cycle occurrence <= it, so the
// stored end never lies about when the series actually stops.
// orgtime-7c2e4b19 — returns the END of that visit's company day (how end dates are stored).
const snapEndToCycle = (start, recurrence, pickedEnd, tz) => {
    if (!start || !pickedEnd) return pickedEnd;
    const pickYmd = orgYmd(pickedEnd, tz);
    let cur = new Date(start);
    let last = new Date(cur);
    let guard = 0;
    while (guard++ < 2000) {
        const nx = advanceRecurrence(cur, recurrence, start);
        if (!nx || orgYmd(nx, tz) > pickYmd) break;
        cur = nx; last = new Date(cur);
    }
    return orgEndOfDay(last, tz);
};

// ─────────────────────────────────────────────────────────────────────────────
// REMINDER DATE UTILITY
// ─────────────────────────────────────────────────────────────────────────────
// orgtime-7c2e4b19 — the reminder default lives in src/utils/reminderDate.js (one rule, web + phone).
export { calculateReminderDate };

// ─────────────────────────────────────────────────────────────────────────────
// DATE HELPERS
// ─────────────────────────────────────────────────────────────────────────────
// orgtime-7c2e4b19 — a COMPANY day before the company's today.
const isDateInPast = (date, tz) => {
    if (!date || !(date instanceof Date)) return false;
    return orgYmd(date, tz) < orgYmd(new Date(), tz);
};

const toDateTimeLocal = (date) => {
    if (!date || !(date instanceof Date)) return '';
    const ten = (i) => (i < 10 ? '0' : '') + i;
    const YYYY = date.getFullYear();
    const MM = ten(date.getMonth() + 1);
    const DD = ten(date.getDate());
    const HH = ten(date.getHours());
    const II = ten(date.getMinutes());
    return `${YYYY}-${MM}-${DD}T${HH}:${II}`;
};

const toDateInput = (date) => {
    // localstamp-2d6b9f43 — LOCAL calendar day, not UTC. The old body
    // `date.toISOString().split('T')[0]` yielded the UTC day, so an evening job
    // (~8pm+ local) stamped D+1 on an `only_this` save while the exception recorded D —
    // the occurrence detached by one day. formatLocalDate uses local getters and never
    // shifts. Guard is unchanged, so non-Date callers still get ''.
    if (!date || !(date instanceof Date)) return '';
    return formatLocalDate(date);
};

const formatDateDisplay = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const JobFormModal = ({ 
    isOpen, 
    onClose, 
    currentUserProfile, 
    jobToEdit, 
    onSave, 
    employees, 
    services, 
    resources, 
    jobs, 
    onGenerateInvoice, 
    customers, 
    companySettings 
}) => {
    const recurrenceOptions = ['Never', 'Weekly', 'Bi-Weekly', 'Monthly', 'Custom'];
    const RECURRENCE_END_SENTINEL = new Date('2999-12-31T00:00:00'); // "never ends" storage; never shown
    // orgtime-7c2e4b19 — the company's zone, for every DAY this form saves or shows: the series end
    // date, the reminder day, and the days sent to generateJobsForDateRange.
    const orgTz = resolveOrgTimeZone(companySettings?.timezone);
    const toOrgDateInput = (d) => orgYmd(d, orgTz) || '';
    const statusOptions = ['Pending', 'Scheduled', 'In Progress', 'Completed', 'Canceled'];
    
    const [taxOptions, setTaxOptions] = useState([]);
    const [isLoadingTax, setIsLoadingTax] = useState(false);
    const [linkedExpenses, setLinkedExpenses] = useState([]);

    const [reminderEnabled, setReminderEnabled]               = useState(true);
    const [reminderDate, setReminderDate]                     = useState(null);
    const [reminderDateOverridden, setReminderDateOverridden]   = useState(false);
    const [reminderPastDateWarning, setReminderPastDateWarning] = useState(false);
    // r5-b2b-joblabor-state-7c3e1a95 — per-job labor-billing override (Increment 1, inert). null = inherit org default.
    const [billLaborToCustomer, setBillLaborToCustomer] = useState(null);
    // r3-b3-state-7e2a9c41 — B3 tracked-labor readout: which people's punch rows are expanded (pure UI state, no writes).
    const [expandedPunchUserIds, setExpandedPunchUserIds] = useState([]);

    // jobtimefix-4a9e2d73-b — SEAM B STALENESS FIX.
    // jobToEdit is a FROZEN prop: CrmApp holds `editingJob` in plain useState, set only from getDoc
    // one-shots, and nothing re-syncs it (JobQuickViewModal subscribes; this modal does not). And
    // correctJobPunch deliberately returns no punch array — untouched rows still hold Firestore
    // Timestamps while the rows it just wrote hold Dates, and the two serialize differently across
    // the callable boundary. So after a correction we re-read the job ourselves and the readout
    // reads off THIS when set. Safe because handleSave strips timePunches AND jobCosting before
    // writing (see _serverOwnedPunches below), so nothing here can ever be written back.
    const [correctedJob, setCorrectedJob] = useState(null);
    // { op, punch, punchIndex, otherClosedHours } while the correction modal is open; null otherwise.
    const [punchCorrection, setPunchCorrection] = useState(null);
    // userId -> hours of that person already billed on an invoice for this job. Lazily loaded.
    const [billedThroughByUser, setBilledThroughByUser] = useState({});

    const canManageJobs = can(currentUserProfile, PERMISSIONS.MANAGE_JOBS);
    // Cost / bill-out / markup readouts are financial data — gate on VIEW_FINANCIALS
    // (held by office/manager/consultant/owner/admin, NOT Field Technicians).
    const canViewFinancials = can(currentUserProfile, PERMISSIONS.VIEW_FINANCIALS);
    // s6costing-4d8a2f63 — S6b (security audit #2). Labour COST — cost rates and labour-cost
    // amounts, i.e. what people are paid — is Owner / Admin / Manager only (founder decision,
    // Marcus 2026-10-04). Office Staff and Sales Consultants keep VIEW_FINANCIALS for the
    // bill-out amounts and material cost; Field Technicians see neither.
    const canSeeLaborCostHere = canSeeLaborCost(currentUserProfile);
    // jobtimefix-4a9e2d73-b — SEAM B GATE SPLIT. Times are not money.
    //   canCorrectTime        — may actually correct. Six role strings shared with the callable and
    //                           firestore.rules. A CONVENIENCE gate: requireApproveJobTime is the
    //                           real boundary and re-reads the caller's user doc server-side.
    //   canSeeCorrectControls — the control renders at all. Office Staff / Sales Consultant get it
    //                           disabled with the server's own sentence on hover: they are the roles
    //                           who notice a bad row and need to know whom to escalate to.
    // A Field Technician gets neither, so no edit affordance appears on their own time — but they DO
    // now see the punch list itself (times and hours, never rates or amounts), which is strictly more
    // than the nothing they saw before.
    const canCorrectTime = canCorrectJobTime(currentUserProfile);
    const canSeeCorrectControls = canViewFinancials;
    // Org opt-in; the server-truth gate is in firestore.rules. A tech may fill the
    // form only to CREATE a new job — existing jobs stay read-only for them, so
    // techCanCreate is false whenever jobToEdit is set (collapsing canFillJobForm
    // back to canManageJobs for any existing job).
    const techJobCreationEnabled = companySettings?.techJobCreationEnabled === true;
    const techCanCreate  = techJobCreationEnabled && !jobToEdit;
    // x12techjob-5f2c8a91 — X5 #12 (founder decision 2026-10-16): a technician no longer edits an
    // EXISTING Job in this form (schedule, customer, who is assigned are office-only; firestore.rules
    // techJobFieldsSafe). They still CREATE jobs when the company allows it (techCanCreate above), and
    // on the quick view they still punch, complete, add items, write Work Performed and pick
    // equipment. canTechEditJob still gates those quick-view actions; it no longer opens this form.
    const techCanEdit    = false;
    const canFillJobForm = canManageJobs || techCanCreate || techCanEdit;
    // A tech editing an existing job gets a tighter surface than a manager:
    // Status stays read-only and the Delete button is hidden (below).
    const isTechEditing  = !!jobToEdit && canFillJobForm && !canManageJobs;

    // h1job-9c4e7b18 — H1 Seam 4 (mirrors jobCreateTrusted() in firestore.rules). A job CREATED by
    // anyone but an office role, or a Sales Consultant on their own customer, must start unworked:
    // every labour row at 0 hours, no punches / completedAt / invoiceId, and a Job's status
    // Pending / Scheduled / In Progress. The rows stay (a Job's assignedTo is derived from them).
    const createMustBeUnworked = (clientId) => {
        if (isOfficeOrAbove(currentUserProfile)) return false;
        const ownsClient = !!clientId && customers?.find(c => c.id === clientId)?.sales_person_id === currentUserProfile?.id;
        return !(currentUserProfile?.role === 'Sales Consultant' && ownsClient);
    };
    const asUnworkedCreate = (payload) => {
        if (!createMustBeUnworked(payload.clientId)) return payload;
        const { timePunches, completedAt, invoiceId, ...rest } = payload;
        if (!payload.jobCosting) return rest;
        const rows = (payload.jobCosting.laborEntries || []).map(r => ({ userId: r.userId, name: r.name || '', rate: null, hours: 0 }));
        return { ...rest, jobCosting: { laborEntries: rows, ...(rows.length === 0 ? { laborClearedAt: new Date() } : {}) } };
    };

    const isJobEvent = jobToEdit?.eventType === 'Job' || (!jobToEdit && canFillJobForm);
    const isReadOnly = isJobEvent && !canFillJobForm;
    const canEditResources = canFillJobForm || (jobToEdit && (jobToEdit.assignedTo || []).includes(currentUserProfile?.id));

    const eventTypes = useMemo(() => {
        const baseTypes = ['Appointment', 'Phone Appointment', 'Follow-Up'];
        if (canFillJobForm) {
            return ['Job', ...baseTypes];
        }
        return baseTypes;
    }, [canFillJobForm]);

    const availableCustomers = useMemo(() => {
        if (!customers || !currentUserProfile) return [];

        // PHASE FO-3b VERIFIER: r1-fo-jfm-customerfix-4f7b2c91
        // Option B: gate on the MANAGE_CUSTOMERS permission rather than a
        // hardcoded role list. This fixes the bug where Office Staff (who have
        // MANAGE_CUSTOMERS but were absent from the old ['Admin','Manager',
        // 'Owner/System Admin'] list) opened a job and saw the customer dropdown
        // fall back to "— Select a Customer —" because the job's customer was
        // filtered out of the options. Using the permission matrix is immune to
        // role-string drift and self-corrects for any role granted the permission.
        // A form-capable tech (org opted in) needs to pick ANY company customer,
        // same as a manager — so they land on the see-all branch too. This is a
        // local read-list widening for the job form only; it grants no
        // MANAGE_CUSTOMERS permission anywhere else.
        const canSeeAllCustomers = can(currentUserProfile, PERMISSIONS.MANAGE_CUSTOMERS) || canFillJobForm;

        // Helper: guarantee the job's CURRENT customer is always present in the
        // option list, regardless of role/filter — so the dropdown can never
        // fail to display (or silently drop) the customer already on the job.
        const ensureCurrentCustomer = (list) => {
            const cid = jobToEdit?.clientId;
            if (!cid) return list;
            if (list.find(c => c.id === cid)) return list;
            const current = customers.find(c => c.id === cid);
            return current ? [...list, current] : list;
        };

        if (canSeeAllCustomers) {
            return ensureCurrentCustomer(customers);
        }

        const owned = customers.filter(c => c.sales_person_id === currentUserProfile.id);
        if (jobToEdit?.linkedJobId && jobToEdit?.clientId) {
            const linkedCustomer = customers.find(c => c.id === jobToEdit.clientId);
            if (linkedCustomer && !owned.find(c => c.id === linkedCustomer.id)) {
                return ensureCurrentCustomer([...owned, linkedCustomer]);
            }
        }
        return ensureCurrentCustomer(owned);
    }, [customers, currentUserProfile, jobToEdit?.linkedJobId, jobToEdit?.clientId, canFillJobForm]);

    const cancellationReasons = [
        "Customer Rescheduled", 
        "Customer Cancelled", 
        "Weather", 
        "Technician Unavailable", 
        "No Longer Needed", 
        "Bought Elsewhere"
    ];

    const defaultLineItem = {
        serviceId: '',
        name: '',
        description: '',
        quantity: 1,
        price: 0,
        type: 'Service',       
        taxCodeId: '',         
        taxExempt: false,      
        addToCatalog: false,
        crewGroupId: ''      // which route group handles this line item
    };

    // reconstructLaborEntriesFromAssignments moved verbatim to utils/eventAssignees.js
    // (apptassign-6e1c9b47) so the reopen path is tested by the function the form runs.

    // jobedit-nostomp-7c3f1a58
    // Merge, don't overwrite. This modal holds a copy of jobCosting.laborEntries
    // frozen when it opened and never re-syncs it, so a punch-out landing while the
    // editor is open used to be reverted on save (hours back to 0, `source` and
    // `allocatedHours` gone) — that job's labour cost silently became $0.
    //
    // The split: a row is CLOCKED iff source === 'clocked', which only ever six
    // server sites write (punchOutJob, completeJob, closeJobPunchesOnDone). A manual
    // row has no `source` at all — there is no literal 'manual' value anywhere.
    // Clocked rows are taken from `liveRows` (the document as it stands at save
    // time); manual rows come from the form. Keyed on userId, which is how every
    // server upsert joins. Note a person's row is upgraded IN PLACE when they punch
    // out, so once someone is clocked there is no manual half of their row left for
    // this form to own — the whole row is server truth.
    const mergeLaborForSave = (formRows, liveRows) => {
        const liveClocked = (Array.isArray(liveRows) ? liveRows : []).filter(r => r?.source === 'clocked');
        const clockedUids = new Set(liveClocked.map(r => r.userId));
        const formManual = (Array.isArray(formRows) ? formRows : []).filter(r => r && !clockedUids.has(r.userId));
        return [...liveClocked, ...formManual];
    };

    const getInitialState = () => {
        const defaultType = canFillJobForm ? 'Job' : 'Appointment';
        // Auto-assign the creating tech: seed a self labor entry (assignedTo is
        // derived from laborEntries for Jobs). hours:0 so no phantom labor is
        // booked; the tech can add others, pick a route group, or remove
        // themselves via the form.
        const techSelfSeed = techCanCreate
            ? [{ userId: currentUserProfile.id, name: currentUserProfile.name, rate: null, hours: 0 }]
            : [];
        return {
            title: '',
            clientId: '',
            clientName: '',
            address: '',
            billTo: null, // billto-jfm-state-7d8e1b39 -- null/absent = bill the main customer; jobToEdit spread (line ~328) carries a loaded/converted value over this default
            startAt: new Date(),
            endAt: new Date(new Date().setHours(new Date().getHours() + 1)),
            // FIX 1: Pending is only the correct default for Job events.
            // Appointments, Phone Appointments, and Follow-Ups default to Scheduled.
            status: 'Scheduled',
            lineItems: [defaultLineItem], 
            invoiceTotal: 0,
            recurrence: {
                frequency: 'Never',
                endDate: orgEndOfDay(new Date(new Date().setMonth(new Date().getMonth() + 3)), orgTz), // orgtime-7c2e4b19
                interval: 1,        // Custom-only: "Every N units"
                unit: 'weeks',      // Custom-only: days|weeks|months|years
                neverEnds: false,   // Custom-only: forever toggle
            },
            jobCosting: { laborEntries: techSelfSeed },
            assignedTo: techSelfSeed.map(e => e.userId),
            eventType: defaultType,
        };
    };

    // phone16-7c2e9a41 — a NEW Appointment / Phone Appointment / Follow-Up starts with the person
    // booking it ticked, as on the phone. Applied only where the form opens with no jobToEdit at all
    // (the mount initialiser and the open effect below) — never inside getInitialState, which an
    // existing event also falls back to — so existing events open exactly as before. A Job's seed is
    // getInitialState's own (a tech's self-seed, else none), unchanged.
    const withNewEventSeed = (state) => {
        const laborEntries = newEventLaborSeed({ eventType: state.eventType, booker: currentUserProfile, jobSeed: state.jobCosting.laborEntries });
        return { ...state, jobCosting: { ...state.jobCosting, laborEntries }, assignedTo: laborEntries.map(e => e.userId) };
    };

    // PHASE 1.4 VERIFIER: r1-c5e8d139
    // Initialize jobData from jobToEdit at mount time, not via post-mount effect.
    // Previously the post-mount useEffect (below at line ~296) was the only place
    // that populated form fields from jobToEdit, which produced a blank flash on
    // initial open and — in deep-link/async open flows where prop timing differed
    // from kanban-click flows — sometimes left the form completely blank.
    // Reading jobToEdit lazily here guarantees a populated form on first paint.
    // The post-mount effect remains as the source of truth for prop-change updates
    // (e.g. user switches from one job to another without closing the modal).
    const [jobData, setJobData] = useState(() => {
        const base = getInitialState();
        if (!jobToEdit) return withNewEventSeed(base); // phone16-7c2e9a41
        const isTimestamp = (date) => date && typeof date.toDate === 'function';
        // r1-rec-instdate-init-d4b9f2a6
        // For a recurring OCCURRENCE, the opener puts the occurrence's real date in
        // _instanceStartAt/_instanceEndAt while jobToEdit.startAt is the TEMPLATE's
        // (first-occurrence) date. Prefer the instance date so an edited occurrence
        // keeps its own day; normal jobs (no _instance*) fall back unchanged.
        const baseStartSrc = jobToEdit._instanceStartAt || jobToEdit.startAt;
        const baseEndSrc = jobToEdit._instanceEndAt || jobToEdit.endAt;
        const loadedStartAt = isTimestamp(baseStartSrc)
            ? baseStartSrc.toDate()
            : (baseStartSrc || base.startAt);
        const loadedEndAt = isTimestamp(baseEndSrc)
            ? baseEndSrc.toDate()
            : (baseEndSrc || base.endAt);
        const ensuredLineItems = (jobToEdit.lineItems && jobToEdit.lineItems.length > 0
            ? jobToEdit.lineItems
            : base.lineItems
        ).map(item => ({
            ...defaultLineItem,
            ...item,
            type:      item.type || 'Service',
            taxCodeId: item.taxCodeId || '',
            taxExempt: item.taxExempt || false,
        }));
        return {
            ...base,
            ...jobToEdit,
            startAt: loadedStartAt,
            endAt:   loadedEndAt,
            lineItems: ensuredLineItems,
            invoiceTotal: jobToEdit.invoiceTotal || 0,
            recurrence: (() => {
                const storedEnd = isTimestamp(jobToEdit.recurrence?.endDate)
                    ? jobToEdit.recurrence.endDate.toDate()
                    : (jobToEdit.recurrence?.endDate || base.recurrence.endDate);
                const neverEnds = storedNeverEnds(jobToEdit.recurrence, storedEnd); // allkeep-6c2d9e14 — Custom only
                return {
                    frequency: jobToEdit.recurrence?.frequency || 'Never',
                    // For a never-ends series show a sane default date (the sentinel is
                    // hidden by the toggle; this is only revealed if they un-check it).
                    endDate: neverEnds ? base.recurrence.endDate : storedEnd,
                    interval: jobToEdit.recurrence?.interval || 1,
                    unit: jobToEdit.recurrence?.unit || 'weeks',
                    neverEnds,
                };
            })(),
            jobCosting: {
                laborEntries: jobToEdit.jobCosting?.laborEntries?.length > 0
                    ? jobToEdit.jobCosting.laborEntries
                    : (reconstructLaborEntriesFromAssignments(jobToEdit, employees).length > 0
                        ? reconstructLaborEntriesFromAssignments(jobToEdit, employees)
                        : base.jobCosting.laborEntries),
            },
        };
    });
    const [selectedResources, setSelectedResources] = useState([]);

    // jobedit-nostomp-7c3f1a58 — true only once a human has actually changed labour
    // in THIS editing session (assign, unassign, or an hours edit). When it is false
    // the save omits jobCosting entirely, so the overwhelming majority of saves — the
    // dispatcher fixing an address — can no longer touch the labour array at all.
    const laborDirty = useRef(false);
    // remopen-3f8b1d62 — holds a saved job's reminder while it opens (src/utils/reminderDate.js).
    const reminderGuardRef = useRef(null);

    // ── Address verification state (Phase 2b) ──────────────────────────────
    // Mirrors the pattern from CustomerFormModal. Status flow:
    //   idle      — no verification attempted yet (legacy job, or fresh open)
    //   inherited — copied from customer doc; no CF call needed
    //   checking  — CF call in flight
    //   ok        — verified, canonical matches typed
    //   ok_with_changes — verified, but Google adjusted something
    //   ambiguous — multiple plausible matches; user picks one
    //   not_found — Google couldn't find it
    //   error     — transient API/rate-limit failure
    const [addressGeocode, setAddressGeocode] = useState({
        status: 'idle',
        canonical: null,
        coords: null,
        placeId: null,
        alternatives: null,
        differences: null,
        message: null,
    });
    // PROPERTIES v2 — service-location selector for jobs. '' = unchosen (a choice is forced
    // when the customer has properties), 'main' = the customer's main address, else a property id.
    const [serviceLocation, setServiceLocation] = useState('');
    // billto-jfm-selstate-2b6c4f95 -- Bill To selector state (see billto-qfm-selstate-4a9c1e76).
    // billToTouched initializes TRUE when jobToEdit already carries a bill-to -- covers
    // both editing a saved job and the conversion seed passed via openJobEditorModal.
    const [pendingThirdParty, setPendingThirdParty] = useState(false);
    const [billToTouched, setBillToTouched] = useState(() => !!normalizeBillTo(jobToEdit?.billTo));

    // Debounce timer for blur-triggered verification.
    const verifyTimerRef = useRef(null); 
    const [isSaving, setIsSaving] = useState(false);
    const [showRecurrencePrompt, setShowRecurrencePrompt] = useState(false);
    const [isCompletionNoteModalOpen, setIsCompletionNoteModalOpen] = useState(false);
    const [completionNoteCallback, setCompletionNoteCallback] = useState(null);
    const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
    const [deleteOptions, setDeleteOptions] = useState({ isOpen: false, isRecurring: false });
    const [isDeleting, setIsDeleting] = useState(false);
    const [conflictDetails, setConflictDetails] = useState(null);
    // ── Job Templates ──────────────────────────────────────────────────────
    const [jobTemplates, setJobTemplates] = useState([]);
    const [orgTags, setOrgTags] = useState([]);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false); 

    // --- ROUTING & SCHEDULING STATE ---
    const [schedulingMode, setSchedulingMode]           = useState('window');   // 'window' | 'exact'
    const [arrivalWindow, setArrivalWindow]             = useState('morning');  // 'morning' | 'afternoon' | 'allday'
    const [windowDate, setWindowDate]                   = useState(toDateInput(new Date()));
    const [jobDurationHours, setJobDurationHours]       = useState(1);  // explicit duration for window mode
    const [jobDurationMins, setJobDurationMins]         = useState(0);
    const [durationMode, setDurationMode]               = useState('hours'); // 'hours' | 'days'
    const [jobDurationDays, setJobDurationDays]         = useState(1);
    // r2-cs-state-8a1f4d92 — continuous per-crew scheduling (lives inside Multi-day)
    const [perCrewScheduling, setPerCrewScheduling]     = useState(false);
    const [crewSchedule, setCrewSchedule]               = useState({});
    const [assignmentMode, setAssignmentMode]           = useState('individual'); // 'individual' | 'group'
    const [showAssignment, setShowAssignment]               = useState(false); // collapsed by default for new jobs
    const [selectedGroupId, setSelectedGroupId]         = useState('');
    const [routeGroups, setRouteGroups]                 = useState([]);
    const [fieldWorkers, setFieldWorkers]               = useState([]);
    const [assignedFieldWorkerIds, setAssignedFieldWorkerIds] = useState([]);
    const [showAdminOptions, setShowAdminOptions] = useState(false);
    // recurkey-7b2f9d34 — THE OCCURRENCE DOCUMENT ID.
    //
    // `jobToEdit.id` is NOT the id of the visit on screen when the visit belongs to a
    // recurring series: openJobEditorModal deliberately sets id = the TEMPLATE id and
    // carries the occurrence's real doc id in `_instanceId` (see the data-loss trap
    // described there — do NOT "fix" that by putting the instance id on jobToEdit.id).
    // Every consumer that needs the VISIT — its notes, its expenses, the invoice it
    // generates — must use this instead, or it silently addresses the whole series.
    //
    // Three cases, and the third is why this can be null:
    //   plain job              -> jobToEdit.id          (no _templateId, id is the doc)
    //   recurring, materialised-> jobToEdit._instanceId (id is the template)
    //   recurring, PROJECTED   -> null                  (no document exists at all)
    //
    // `_templateId` is the discriminator: openJobEditorModal sets it on BOTH recurring
    // paths (hydrated-from-instance and template-fallback) and never on a plain job.
    //
    // SECOND ENTRY POINT, and the one that actually created the template-keyed notes in
    // production: clicking a PROJECTED occurrence on the CALENDAR. CalendarView builds
    // those from `recurringJobs` docs and passes `resource: {...template}` — and a
    // template document carries NO `recurringJobId` field, so openJobEditorModal's
    // recurring branch never fires and `jobToEdit` IS the raw template, with no
    // `_templateId` to mark it. (The jobs LIST is different: its virtual rows do set
    // recurringJobId, so they take the recurring branch.) A job object that carries a
    // real recurrence rule but no recurringJobId is therefore a series template, not a
    // visit — and a template is a rule, not somewhere a note can live.
    // Read from jobToEdit (the SAVED doc), never jobData, so merely choosing a
    // recurrence in the form cannot make an existing plain job look like a template.
    //
    // The CONTRACT (matches mobile, which has shipped it since r1-3c-fieldtech-*):
    // a note attaches to an occurrence document that EXISTS. Null => no notes surface.
    const isRecurringOccurrence = !!jobToEdit?._templateId;
    const isSeriesTemplate = !isRecurringOccurrence
        && !jobToEdit?.recurringJobId
        && !!jobToEdit?.recurrence?.frequency
        && jobToEdit.recurrence.frequency !== 'Never';
    const occurrenceJobId = isRecurringOccurrence
        ? (jobToEdit?._instanceId || null)
        : (isSeriesTemplate ? null : (jobToEdit?.id || null));
    // s6costing-4d8a2f63 — this job's stored labour cost rates (jobCosts/{jobId}). Subscribes
    // only for roles allowed to read them; everyone else gets null and the readouts below fall
    // back to the row's legacy rate (absent once the job is migrated). Rates are looked up at
    // render time and NEVER copied onto jobData rows, which this editor saves from the browser.
    const { rates: jobCostRates } = useJobCosts(currentUserProfile?.organizationId, occurrenceJobId, canSeeLaborCostHere);

    // r1-3i-jfm-wire-6d2f9b41 — Notes & Media (web full editor). Reads job-attached
    // notes (job:<id>) for the live count + list; holds the collapse toggle. Only
    // meaningful once the job exists.
    // recurkey-7b2f9d34 — WAS `jobToEdit?.id`, which on a recurring occurrence is the
    // TEMPLATE id: the tech's note (written from the quick view against the real
    // occurrence doc) was invisible here, and notes written here landed on the series.
    const [jobNotesExpanded, setJobNotesExpanded] = useState(false);
    const { notes: allJobNotes } = useOrgNotes({ // chklstweb-3a7f1c58
        orgId: currentUserProfile?.organizationId || null,
        attachedKey: occurrenceJobId ? `job:${occurrenceJobId}` : null,
    });
    // chklstweb-3a7f1c58 — checklist photo/signature notes show their photos in the
    // Checklists panel only. Checklists live on the server (callables), never in jobData,
    // so this editor's Save cannot touch them. Existing Jobs only: a new job has no id
    // yet, and "every new job" checklists are attached server-side on create.
    const jobNotes = allJobNotes.filter(n => !n.checklistSource);
    const showChecklists = !!occurrenceJobId && (jobToEdit?.eventType || 'Job') === 'Job';
    const { checklists: jobChecklists, state: checklistState } = useJobChecklists({
        orgId: currentUserProfile?.organizationId || null,
        jobId: occurrenceJobId,
        enabled: !!isOpen && showChecklists,
    });
    // chklstnew-5a8c2e71 — Seam 6c: checklists CHOSEN on a new one-off Job ({ id, name,
    // blocksCompletion }), attached after the job is saved. Never part of the job's own payload.
    // Office roles only: the server lets a technician attach only on a job they are assigned to.
    const [newJobChecklists, setNewJobChecklists] = useState([]);
    const showNewJobChecklists = !jobToEdit && jobData.eventType === 'Job'
        && (jobData.recurrence?.frequency || 'Never') === 'Never' && isChecklistOfficeRole(currentUserProfile);
    const [attachingChecklists, setAttachingChecklists] = useState(false);

    // wpauthor-5d3f8a26 — PROMOTE WAS REMOVED FROM THIS FORM (it was
    // wppromote-6a3f1e94's handler, plus pendingPromotions/prePromoteTextRef).
    //
    // Promote appends a note's text into a Work Performed box so the reviewer can
    // reword it before the customer reads it. This form no longer has that box —
    // see the removal note beside the Notes & Media strip below — so there was
    // nothing left to promote INTO. A button that fills an invisible field is
    // worse than no button.
    //
    // Promote still works, in the quick view, where Work Performed now lives and
    // where the promoted text becomes an entry authored by the PROMOTER with the
    // source note recorded. The "Added to Work Performed" chip on NoteCard is
    // unaffected — it reads the marker on the note, which the quick view writes.
    // ─── legnotes9-6b4e8d12 — SEAM 9 ────────────────────────────────────────
    // The sourceTechNote resolver is GONE: two useState hooks, the useEffect that
    // read the parent job's techNotes[] and matched an element by addedAt, and the
    // derived sourceTechNoteDate. It served follow-ups created by the old calendar
    // quickview path (CrmApp handleAddTechNote, itself deleted in Seam 5), whose
    // source note was an ARRAY ELEMENT with no document to point at.
    //
    // Seam 8 moved those elements into organizations/{orgId}/notes and deleted the
    // array, so the lookup could only ever fail — and failing was not silent: it
    // rendered "The note behind this follow-up could not be loaded. It may have
    // been removed, or the original job may have been deleted." on every one of
    // those follow-ups. That sentence was untrue; nothing was removed or deleted.
    // Removing the resolver removes the false alarm. The banner keeps its accurate
    // first line ("Follow-up created by X from a field note", plus the flag type).
    //
    // Server-path follow-ups (onNoteCreated) are unaffected: they attach the real
    // note document, which the Notes & Media panel below already renders.

    // aw-seam5 — new jobs only: if the selected arrival window has been disabled in
    // settings, snap to the first enabled one so the <select> never shows a phantom
    // value. Existing jobs keep their historical window (preserved on save) even if it
    // was since disabled — we never silently rewrite a saved job's window.
    useEffect(() => {
        if (jobToEdit?.id) return;
        if (schedulingMode !== 'window') return;
        const enabled = getArrivalWindows(companySettings);
        if (!enabled.some(w => w.key === arrivalWindow)) {
            setArrivalWindow(enabled[0].key); // floor guarantees enabled[0] exists
        }
    }, [companySettings, arrivalWindow, schedulingMode, jobToEdit]);

    // --- 1. Fetch Tax Codes on Open ---
    useEffect(() => {
        if (isOpen && currentUserProfile?.organizationId) {
            const fetchTaxCodes = async () => {
                setIsLoadingTax(true);
                try {
                    const getOptions = httpsCallable(functions, 'getQuickBooksMappingOptions');
                    const result = await getOptions();
                    if (result.data?.taxCodes) {
                        setTaxOptions(result.data.taxCodes);
                    }
                } catch (error) {
                    console.error("Failed to load tax codes:", error);
                } finally {
                    setIsLoadingTax(false);
                }
            };
            fetchTaxCodes();
        }
    }, [isOpen, currentUserProfile]);

    // --- 2. Fetch Linked Expenses Real-Time ---
    useEffect(() => {
        // recurkey-7b2f9d34 — WAS jobToEdit.id. An expense is logged against the VISIT,
        // so on a recurring occurrence this was querying the series template id and
        // Job Costing's "Material Cost (from Expenses)" silently read 0. (No production
        // expense currently carries either id, so this corrects the query before the
        // first one does, rather than repairing anything.)
        if (isOpen && occurrenceJobId && currentUserProfile?.organizationId) {
            const expensesRef = collection(db, `organizations/${currentUserProfile.organizationId}/expenses`);
            const q = query(expensesRef, where("jobId", "==", occurrenceJobId));

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const exps = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setLinkedExpenses(exps);
            }, (error) => {
                console.error("Error fetching job expenses:", error);
            });

            return () => unsubscribe();
        } else {
            setLinkedExpenses([]);
        }

    // --- 2b. Fetch Route Groups + Field Workers for assignment ---
    }, [isOpen, occurrenceJobId, currentUserProfile?.organizationId]); // recurkey-7b2f9d34

    useEffect(() => {
        if (!isOpen || !currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        const unsubGroups = onSnapshot(
            query(collection(db, `organizations/${orgId}/routeGroups`), orderBy('name')),
            snap => setRouteGroups(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            err => console.error('Error fetching route groups:', err)
        );

        const unsubFW = onSnapshot(
            query(collection(db, `organizations/${orgId}/fieldWorkers`), where('status', '==', 'active')),
            snap => setFieldWorkers(snap.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))),
            err => console.error('Error fetching field workers:', err)
        );

        return () => { unsubGroups(); unsubFW(); };
    }, [isOpen, currentUserProfile?.organizationId]);

    // ── Fetch job templates ────────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen || !currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;
        return onSnapshot(
            query(collection(db, `organizations/${orgId}/jobTemplates`), orderBy('name')),
            snap => setJobTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
    }, [isOpen, currentUserProfile?.organizationId]);

    // ── Fetch customer tags (read-only chip display; one-time per open) ────
    useEffect(() => {
        if (!isOpen || !currentUserProfile?.organizationId) return;
        getDocs(collection(db, `organizations/${currentUserProfile.organizationId}/tags`))
            .then(snap => setOrgTags(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
            .catch(err => console.error('Error fetching tags:', err));
    }, [isOpen, currentUserProfile?.organizationId]);

    // --- 3. Load Job Data + Reminder State on Open ---
    useEffect(() => {
        if (isOpen) {
            // jobedit-nostomp-7c3f1a58 — a fresh open owns no labour change yet.
            laborDirty.current = false;
            // jobtimefix-4a9e2d73-b — a fresh open must fall back to the incoming prop, or a
            // corrected snapshot of the PREVIOUS job would render against this one.
            setCorrectedJob(null);
            setPunchCorrection(null);
            setBilledThroughByUser({});
            const initialState = getInitialState();
            const isTimestamp = (date) => date && typeof date.toDate === 'function';

            if (jobToEdit) { 
                const reconstructed = reconstructLaborEntriesFromAssignments(jobToEdit, employees);
                const laborEntries = jobToEdit.jobCosting?.laborEntries && jobToEdit.jobCosting.laborEntries.length > 0
                    ? jobToEdit.jobCosting.laborEntries
                    : (reconstructed.length > 0 ? reconstructed : initialState.jobCosting.laborEntries);
                
                const ensuredLineItems = (jobToEdit.lineItems && jobToEdit.lineItems.length > 0 ? jobToEdit.lineItems : initialState.lineItems).map(item => ({
                    ...defaultLineItem,
                    ...item,
                    type: item.type || 'Service',
                    taxCodeId: item.taxCodeId || '',
                    taxExempt: item.taxExempt || false 
                }));

                // r1-rec-instdate-init-d4b9f2a6
                // Same instance-date preference as the mount initializer (see above):
                // a recurring occurrence loads its own date from _instanceStartAt/EndAt,
                // not the template's start. Normal jobs fall back unchanged.
                const effBaseStartSrc = jobToEdit._instanceStartAt || jobToEdit.startAt;
                const effBaseEndSrc = jobToEdit._instanceEndAt || jobToEdit.endAt;
                const loadedStartAt = isTimestamp(effBaseStartSrc) ? effBaseStartSrc.toDate() : (effBaseStartSrc || initialState.startAt);

                setJobData({
                    ...initialState, 
                    ...jobToEdit, 
                    startAt: loadedStartAt,
                    endAt: isTimestamp(effBaseEndSrc) ? effBaseEndSrc.toDate() : (effBaseEndSrc || initialState.endAt),
                    lineItems: ensuredLineItems,
                    invoiceTotal: jobToEdit.invoiceTotal || 0,
                    recurrence: (() => {
                        const storedEnd = isTimestamp(jobToEdit.recurrence?.endDate) ? jobToEdit.recurrence.endDate.toDate() : jobToEdit.recurrence?.endDate || initialState.recurrence.endDate;
                        const neverEnds = storedNeverEnds(jobToEdit.recurrence, storedEnd); // allkeep-6c2d9e14 — Custom only (a preset left with an old Custom flag showed the 3-month default)
                        return {
                            frequency: jobToEdit.recurrence?.frequency || 'Never',
                            endDate: neverEnds ? initialState.recurrence.endDate : storedEnd,
                            interval: jobToEdit.recurrence?.interval || 1,
                            unit: jobToEdit.recurrence?.unit || 'weeks',
                            neverEnds,
                        };
                    })(),
                    jobCosting: { laborEntries },
                });
                // PROPERTIES v2 — restore the saved service location (a property id, or 'main' for legacy/main-address jobs).
                setServiceLocation(jobToEdit.propertyId ? jobToEdit.propertyId : 'main');
                setSelectedResources(jobToEdit.assignedResources || []);

                // --- Initialize scheduling mode from saved job ---
                setSchedulingMode(jobToEdit.schedulingMode || 'window');
                setArrivalWindow(resolveArrivalWindowKey(jobToEdit.arrivalWindow, 'morning'));
                if (jobToEdit.isAllDay) {
                    setDurationMode('days');
                    setJobDurationDays(jobToEdit.durationDays || 1);
                } else {
                    setDurationMode('hours');
                }
                // r2-cs-hydrate-6c2e9b47 — load a saved per-crew schedule (Timestamps -> Dates)
                if (jobToEdit.crewSchedule && Object.keys(jobToEdit.crewSchedule).length > 0) {
                    setDurationMode('days');
                    setPerCrewScheduling(true);
                    const hydrated = {};
                    Object.keys(jobToEdit.crewSchedule).forEach(crewId => {
                        const apps = (jobToEdit.crewSchedule[crewId]?.appearances || []).map(a => ({
                            ...a,
                            startAt: a.startAt?.toDate ? a.startAt.toDate() : (a.startAt instanceof Date ? a.startAt : new Date(a.startAt)),
                            endAt:   a.endAt?.toDate   ? a.endAt.toDate()   : (a.endAt   instanceof Date ? a.endAt   : new Date(a.endAt)),
                        }));
                        hydrated[crewId] = { appearances: apps };
                    });
                    setCrewSchedule(hydrated);
                } else {
                    setPerCrewScheduling(false);
                    setCrewSchedule({});
                }
                // Recover duration from endAt - startAt
                if (jobToEdit.schedulingMode === 'window' || !jobToEdit.schedulingMode) {
                    const s = jobToEdit.startAt?.toDate ? jobToEdit.startAt.toDate() : (jobToEdit.startAt instanceof Date ? jobToEdit.startAt : null);
                    const e = jobToEdit.endAt?.toDate   ? jobToEdit.endAt.toDate()   : (jobToEdit.endAt   instanceof Date ? jobToEdit.endAt   : null);
                    if (s && e && e > s) {
                        const totalMins = Math.round((e - s) / 60000);
                        // Cap at 12 hours for new jobs — calendar clicks often pass
                        // a full-day span (1440 mins) which would show as 24h.
                        // Existing saved jobs (have an id) always trust their stored value.
                        if (!jobToEdit.id && totalMins > 720) {
                            setJobDurationHours(1);
                            setJobDurationMins(0);
                        } else {
                            setJobDurationHours(Math.floor(totalMins / 60));
                            setJobDurationMins(totalMins % 60);
                        }
                    }
                }
                setWindowDate(toDateInput(loadedStartAt));
                setAssignmentMode(jobToEdit.assignmentMode || 'individual');
                // Show assignment section open if job already has assignment
                if (jobToEdit.assignmentMode === 'group' && jobToEdit.routeGroupId) setShowAssignment(true);
                if ((jobToEdit.assignedTo || []).length > 0) setShowAssignment(true);
                setSelectedGroupId(jobToEdit.routeGroupId || '');
                setAssignedFieldWorkerIds(jobToEdit.assignedFieldWorkerIds || []);
                setShowAdminOptions(
                    !!(jobToEdit.reminderEnabled && jobToEdit.reminderDate) ||
                    !!(jobToEdit.description?.trim())
                );

                const savedReminderEnabled = jobToEdit.reminderEnabled ?? (jobToEdit.eventType === 'Job');
                setReminderEnabled(savedReminderEnabled);
                // r5-b2b-joblabor-load-2e9f4b17 — inherit (null) unless the job explicitly overrode
                setBillLaborToCustomer(jobToEdit.billLaborToCustomer ?? null);

                if (isTimestamp(jobToEdit.reminderDate)) {
                    const savedReminderDate = jobToEdit.reminderDate.toDate();
                    setReminderDate(savedReminderDate);
                    const orgSettings = companySettings?.jobReminderSettings;
                    const autoCalculated = calculateReminderDate(loadedStartAt, orgSettings, orgTz);
                    const savedStr = orgYmd(savedReminderDate, orgTz); // orgtime-7c2e4b19
                    const calcStr  = autoCalculated ? orgYmd(autoCalculated, orgTz) : null;
                    setReminderDateOverridden(savedStr !== calcStr);
                } else {
                    const orgSettings = companySettings?.jobReminderSettings;
                    setReminderDate(calculateReminderDate(loadedStartAt, orgSettings, orgTz));
                    setReminderDateOverridden(false);
                }
                // remopen-3f8b1d62 — a saved reminder date, or a reminder turned OFF, is what the job
                // shows on opening: the start/type effects below leave it alone until the user changes
                // the start or the type. (They used to replace the date with the default on every open,
                // and the type effect turned an OFF reminder back ON.)
                reminderGuardRef.current = (isTimestamp(jobToEdit.reminderDate) || jobToEdit.reminderEnabled === false)
                    ? openReminderGuard(loadedStartAt, jobToEdit.eventType || initialState.eventType)
                    : null;

            } else { 
                setJobData(withNewEventSeed(initialState)); // phone16-7c2e9a41
                setSelectedResources([]);
                // Reset scheduling/assignment state for new job
                setSchedulingMode('window');
                setArrivalWindow('morning');
                setWindowDate(toDateInput(new Date()));
                setAssignmentMode('individual');
                setSelectedGroupId('');
                setAssignedFieldWorkerIds([]);

                const isNewJobType = initialState.eventType === 'Job';
                setNewJobChecklists([]); // chklstnew-5a8c2e71
                setShowAdminOptions(false);
                setDurationMode('hours');
                setJobDurationDays(1);
                setPerCrewScheduling(false);
                setCrewSchedule({});
                setReminderEnabled(isNewJobType);
                if (isNewJobType) {
                    const orgSettings = companySettings?.jobReminderSettings;
                    setReminderDate(calculateReminderDate(initialState.startAt, orgSettings, orgTz));
                } else {
                    setReminderDate(null);
                }
                setReminderDateOverridden(false);
                reminderGuardRef.current = null; // remopen-3f8b1d62 — a new job has nothing saved to keep
            }
        } else {
            setJobData(getInitialState());
            setSelectedResources([]); 
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobToEdit, isOpen, currentUserProfile]);

    // --- 4. Auto-recalculate reminder date when startAt changes ---
    useEffect(() => {
        if (!isOpen) return;
        if (!reminderFollowsChange(reminderGuardRef.current, jobData.startAt, jobData.eventType)) return; // remopen-3f8b1d62 — the job as opened
        if (jobData.eventType !== 'Job') return;
        if (reminderDateOverridden) return;

        const orgSettings = companySettings?.jobReminderSettings;
        const newCalc = calculateReminderDate(jobData.startAt, orgSettings, orgTz);

        if (newCalc && isDateInPast(newCalc, orgTz)) {
            setReminderEnabled(false);
            setReminderDate(null);
            setReminderPastDateWarning(true);
        } else {
            setReminderDate(newCalc);
            setReminderPastDateWarning(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobData.startAt, jobData.eventType, isOpen]);

    // --- 5. When event type changes, reset reminder toggle ---
    useEffect(() => {
        if (!isOpen) return;
        if (!reminderFollowsChange(reminderGuardRef.current, jobData.startAt, jobData.eventType)) return; // remopen-3f8b1d62 — opening is not a type change
        if (jobData.eventType === 'Job') {
            if (!reminderDateOverridden) {
                const orgSettings = companySettings?.jobReminderSettings;
                const calc = calculateReminderDate(jobData.startAt, orgSettings, orgTz);
                if (calc && isDateInPast(calc, orgTz)) {
                    setReminderEnabled(false);
                    setReminderDate(null);
                    setReminderPastDateWarning(true);
                } else {
                    setReminderEnabled(true);
                    setReminderDate(calc);
                    setReminderPastDateWarning(false);
                }
            } else {
                setReminderEnabled(true);
            }
        } else {
            setReminderEnabled(false);
            setReminderDate(null);
            setReminderDateOverridden(false);
            setReminderPastDateWarning(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobData.eventType, isOpen]);

    // --- CALCULATION LOGIC ---
    useEffect(() => {
        let subtotal = 0;
        let totalTax = 0;

        jobData.lineItems.forEach(item => {
            const qty = parseFloat(item.quantity) || 0;
            const price = parseFloat(item.price) || 0;
            const lineTotal = qty * price;
            
            subtotal += lineTotal;

            if (!item.taxExempt) {
                let specificTax = taxOptions.find(t => t.Id === item.taxCodeId);
                if (!specificTax && !item.taxCodeId && companySettings?.quickbooksSettings?.defaultTaxCodeId) {
                    specificTax = taxOptions.find(t => t.Id === companySettings.quickbooksSettings.defaultTaxCodeId);
                }

                let applicableRate = 0;
                if (specificTax) {
                    applicableRate = specificTax.Rate;
                } else {
                    applicableRate = companySettings?.taxRate || 0;
                }
                
                totalTax += lineTotal * (applicableRate / 100);
            }
        });

        const grandTotal = subtotal + totalTax;
        
        if (Math.abs(jobData.invoiceTotal - grandTotal) > 0.01) {
            setJobData(prev => ({ ...prev, invoiceTotal: grandTotal }));
        }
    }, [jobData.lineItems, companySettings, taxOptions, jobData.invoiceTotal]);

    const availableResources = useMemo(() => {
        if (isReadOnly) return [];
        if (!resources || !jobs) {
            return (resources || []).filter(r => r.status === 'Available').map(r => ({ ...r, conflict: null })); 
        }

        // ── Compute the actual time range to check against ──────────────────
        // In window mode, jobData.startAt/endAt are stale — use the live
        // windowDate + arrivalWindow state which reflects what the user sees.
        let jobStart, jobEnd;
        if (jobData.eventType === 'Job' && schedulingMode === 'window' && windowDate) {
            const base = new Date(windowDate + 'T00:00:00');
            const w = getArrivalWindow(arrivalWindow, companySettings); // aw-seam4
            jobStart = new Date(base); jobStart.setHours(w.startH, w.startM, 0, 0);
            jobEnd   = new Date(base); jobEnd.setHours(w.endH,   w.endM,   0, 0);
        } else {
            jobStart = jobData.startAt instanceof Date ? jobData.startAt : jobData.startAt?.toDate();
            jobEnd   = jobData.endAt   instanceof Date ? jobData.endAt   : jobData.endAt?.toDate();
        }

        if (!jobStart || !jobEnd) {
            return (resources || []).filter(r => r.status === 'Available').map(r => ({ ...r, conflict: null }));
        }
        const jobInterval = { start: jobStart, end: jobEnd };
        const equipmentBufferInterval = { start: subHours(jobStart, 1), end: addHours(jobEnd, 1) };
        const potentiallyConflictingJobs = jobs.filter(j => {
            // recurkey-7b2f9d34 — WAS jobToEdit?.id. `jobs` holds occurrence documents,
            // never templates, so on a recurring occurrence this self-exclusion matched
            // nothing and the visit was reported as conflicting with ITSELF for equipment.
            if (j.id === occurrenceJobId) return false;
            const otherStart = j.startAt?.toDate();
            const otherEnd = j.endAt?.toDate();
            if (!otherStart || !otherEnd) return false;
            return (
                isWithinInterval(otherStart, jobInterval) || isWithinInterval(otherEnd, jobInterval) ||
                isWithinInterval(jobStart, { start: otherStart, end: otherEnd }) || isWithinInterval(jobEnd, { start: otherStart, end: otherEnd }) ||
                isWithinInterval(otherStart, equipmentBufferInterval) || isWithinInterval(otherEnd, equipmentBufferInterval) ||
                isWithinInterval(jobStart, {start: subHours(otherStart,1), end: addHours(otherEnd, 1)}) ||
                isWithinInterval(jobEnd, {start: subHours(otherStart,1), end: addHours(otherEnd, 1)})
            );
        });
        const resourceConflicts = new Map(); 
        potentiallyConflictingJobs.forEach(conflictingJob => {
            // Exemption: same route group = crew intentionally shares this resource.
            // IMPORTANT: use selectedGroupId (live state) not jobData.routeGroupId
            // because jobData.routeGroupId is only set on jobs loaded from Firestore,
            // not on a new job being created where the group is in selectedGroupId state.
            const activeGroupId = selectedGroupId || jobData.routeGroupId;
            if (activeGroupId && conflictingJob.routeGroupId === activeGroupId) return;

            (conflictingJob.assignedResources || []).forEach(resourceId => {
                if (!resourceConflicts.has(resourceId)) {
                    const otherStart = conflictingJob.startAt.toDate();
                    const otherEnd = conflictingJob.endAt.toDate();
                    const directOverlap = isWithinInterval(otherStart, jobInterval) || isWithinInterval(otherEnd, jobInterval) || isWithinInterval(jobStart, { start: otherStart, end: otherEnd }) || isWithinInterval(jobEnd, { start: otherStart, end: otherEnd });
                    const resourceDetails = resources.find(r => r.id === resourceId);
                    const isEquipment = resourceDetails?.type === 'Equipment';
                    let conflictType = null;
                    if (directOverlap) { conflictType = 'inUse'; } 
                    else if (isEquipment) { conflictType = 'potentialConflict'; }
                    if (conflictType) {
                         resourceConflicts.set(resourceId, { type: conflictType, job: { id: conflictingJob.id, title: conflictingJob.title, customerName: conflictingJob.customerDetails?.name || conflictingJob.clientName || 'N/A', startAt: conflictingJob.startAt, endAt: conflictingJob.endAt, }});
                    }
                }
            });
        });
        return (resources || []).filter(r => r.status === 'Available').map(r => ({ ...r, conflict: resourceConflicts.get(r.id) || null })).sort((a, b) => a.name.localeCompare(b.name));
    }, [resources, jobs, jobData.startAt, jobData.endAt, jobData.eventType, jobData.routeGroupId, selectedGroupId, schedulingMode, windowDate, arrivalWindow, occurrenceJobId, isReadOnly]); // recurkey-7b2f9d34

    const calculatedMaterialCost = useMemo(() => {
        return linkedExpenses.reduce((total, expense) => total + (expense.amount || 0), 0);
    }, [linkedExpenses]);
    
    // Derive unique route groups assigned across line items
    const lineItemCrews = useMemo(() => {
        return [...new Set(
            jobData.lineItems.map(item => item.crewGroupId).filter(Boolean)
        )];
    }, [jobData.lineItems]);

    // r2-cs-conflict-6a4f2d81 — per-crew appearance conflict detection.
    // Reuses the ResourceConflictModal presentation (the conflictDetails
    // state); the DETECTION is a crew-keyed sibling of availableResources,
    // because resources and crews are different things. Keyed by
    // `${crewGroupId}|${appearanceId}` -> a pre-shaped conflict object.
    // Two shapes for an OTHER job: (1) it is per-crew-scheduled -> only THIS
    // crew's appearances there count (never its envelope, which spans all
    // crews); (2) it is a regular booking -> this crew is on it via crews[]
    // or routeGroupId, checked against its one [startAt,endAt]. Exclusive
    // overlap: touching endpoints are a packed schedule, not a double-book.
    // Recomputes as the user authors (crewSchedule dep) -> live indicators.
    const crewAppearanceConflicts = useMemo(() => {
        const map = {};
        if (isReadOnly) return map;
        if (!crewSchedule || !jobs) return map;

        const currentJobId = occurrenceJobId; // recurkey-7b2f9d34 — self-exclusion needs the occurrence, not the series
        // exclusive overlap: end == start does NOT conflict
        const overlaps = (as, ae, bs, be) => !!(as && ae && bs && be) && as < be && bs < ae;

        Object.keys(crewSchedule).forEach((crewGroupId) => {
            const myAppearances = crewSchedule[crewGroupId]?.appearances || [];
            if (myAppearances.length === 0) return;

            myAppearances.forEach((appt) => {
                const myStart = toJsDate(appt.startAt);
                const myEnd = toJsDate(appt.endAt);
                if (!myStart || !myEnd) return;

                for (const j of jobs) {
                    if (j.id === currentJobId) continue;
                    let hit = null;

                    if (j.crewSchedule && Object.keys(j.crewSchedule).length > 0) {
                        // Shape 1: other job schedules crews per-day.
                        const theirApps = j.crewSchedule[crewGroupId]?.appearances || [];
                        for (const oa of theirApps) {
                            if (overlaps(myStart, myEnd, toJsDate(oa.startAt), toJsDate(oa.endAt))) {
                                hit = { start: oa.startAt, end: oa.endAt };
                                break;
                            }
                        }
                    } else {
                        // Shape 2: other job is a single-window booking.
                        const crewOnJob =
                            (Array.isArray(j.crews) && j.crews.some((c) => c.crewGroupId === crewGroupId)) ||
                            j.routeGroupId === crewGroupId;
                        if (crewOnJob && overlaps(myStart, myEnd, toJsDate(j.startAt), toJsDate(j.endAt))) {
                            hit = { start: j.startAt, end: j.endAt };
                        }
                    }

                    if (hit) {
                        map[`${crewGroupId}|${appt.id}`] = {
                            kind: 'crew',
                            type: 'inUse',
                            conflictingJob: {
                                id: j.id,
                                title: j.title,
                                customerName: j.customerDetails?.name || j.clientName || 'N/A',
                                startAt: hit.start,
                                endAt: hit.end,
                            },
                        };
                        break; // first conflicting job wins
                    }
                }
            });
        });

        return map;
    }, [crewSchedule, jobs, occurrenceJobId, isReadOnly]); // recurkey-7b2f9d34

    // PHASE FO-3 VERIFIER: r1-fo-jfm-pendingmemo-4f7b2c91
    // Indices of line items still awaiting on-site pricing review. Drives the
    // top banner (count + "jump to first") and the per-item highlight.
    const pendingReviewIndices = useMemo(() => {
        return (jobData.lineItems || [])
            .map((item, index) => (item.reviewStatus === 'pending_review' ? index : -1))
            .filter(index => index !== -1);
    }, [jobData.lineItems]);

    const totalLaborCost = useMemo(() => {
        return jobData.jobCosting.laborEntries.reduce((total, entry) => total + ((parseFloat(entry.hours) || 0) * (parseFloat(rateForRow(entry, jobCostRates)) || 0)), 0); // s6costing-4d8a2f63
    }, [jobData.jobCosting.laborEntries, jobCostRates]);

    // jobtimefix-4a9e2d73-b — read punches and labour rows off the re-read job once a correction has
    // landed, and off the frozen prop until then.
    const punchSource = correctedJob || jobToEdit;

    // jobtimefix-4a9e2d73-b — this person's CLOSED hours EXCLUDING one row, so the correction modal
    // can project what their total becomes and compare it against hours already billed. Mirrors the
    // server's sumClockedHours filter (closed rows only) and its 2dp rounding, so the projection and
    // the figure the server will actually write cannot disagree.
    const closedHoursExcluding = (userId, excludeIdx) => parseFloat(
        (punchSource?.timePunches || [])
            .filter((p, i) => p && p.userId === userId && i !== excludeIdx && p.punchOut != null && p.hours != null)
            .reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0)
            .toFixed(2)
    );

    // jobtimefix-4a9e2d73-b — re-read the job after a correction and refresh BOTH readouts.
    // jobData.jobCosting feeds totalLaborCost, so without the second setState the Job Cost summary
    // would disagree with the Tracked Labor block on the same screen.
    const refreshAfterCorrection = async () => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId || !occurrenceJobId) return;
        const fresh = await getDoc(doc(db, `organizations/${orgId}/jobs`, occurrenceJobId));
        if (fresh.exists()) {
            const data = { ...fresh.data(), id: fresh.id }; // s6costing-docid: doc id wins over a stale stored `id` field
            setCorrectedJob(data);
            setJobData(prev => ({ ...prev, jobCosting: data.jobCosting || { laborEntries: [] } }));
        }
        setPunchCorrection(null);
    };

    // jobtimefix-4a9e2d73-b — hours of this job already billed per person, for the already-billed
    // warning. Same query and the same high-water-mark scan the labor bridge runs at
    // CrmApp.js (`where('linkedJobId','==',source.id)`) — single-field equality, so no index — and
    // deliberately NOT job.invoiceTotal, which is inherited from a recurring template and is not
    // proof that anything was invoiced. Lazy: only runs when a correction is actually opened.
    const loadBilledThrough = async () => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId || !occurrenceJobId) return;
        try {
            const invSnap = await getDocs(query(
                collection(db, `organizations/${orgId}/invoices`),
                where('linkedJobId', '==', occurrenceJobId)
            ));
            // laborbill-one-rule-6c2a9e47 — the same high-water-mark helper the invoice builder uses.
            setBilledThroughByUser(priorBilledFromInvoices(invSnap.docs.map(d => d.data())));
        } catch {
            // A failed read must not block the correction. It degrades to "no warning shown",
            // which is the same state as a job with no labour billed — never a false warning.
            setBilledThroughByUser({});
        }
    };

    const openPunchCorrection = (op, punch, punchIndex) => {
        loadBilledThrough();
        setPunchCorrection({
            op,
            punch: punch || null,
            punchIndex: punchIndex === undefined ? null : punchIndex,
            otherClosedHours: punch ? closedHoursExcluding(punch.userId, punchIndex) : 0,
        });
    };

    // r3-b3-memo-7e2a9c41 — B3: group the SAVED job's time punches per person for the tracked-labor
    // readout. Pure display of jobToEdit (independent of the editable form state), no writes. Collapsed
    // hours + cost + billed come from the same data Increment 2 bills from, so the preview matches the invoice.
    const punchReadout = useMemo(() => {
        const punches = punchSource?.timePunches || [];
        if (punches.length === 0) return [];
        const entries = punchSource?.jobCosting?.laborEntries || [];
        const byUser = new Map();
        // jobtimefix-4a9e2d73-b — carry `idx`, the index into the job's REAL timePunches array.
        // correctJobPunch addresses a punch by that index; the per-person grouping below would
        // otherwise lose it, and a per-person index would silently correct the wrong row.
        punches.forEach((p, i) => {
            if (!p || !p.userId) return;
            if (!byUser.has(p.userId)) byUser.set(p.userId, []);
            byUser.get(p.userId).push({ ...p, idx: i });
        });
        const rows = [];
        byUser.forEach((userPunches, uid) => {
            const entry = entries.find(e => e.userId === uid);
            const emp = employees?.find(u => u.id === uid);
            // Canonical billable hours = the clocked entry's cumulative hours (what Increment 2 bills as
            // "accrued"); fall back to this user's summed CLOSED punches if there's no entry yet (a still-open
            // first session has no laborEntry). Open time is never counted toward the billable total.
            const closedHours = userPunches
                .filter(p => p.punchOut != null && p.hours != null)
                .reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0);
            const hours = entry?.hours != null ? (parseFloat(entry.hours) || 0) : parseFloat(closedHours.toFixed(2));
            const costRate = parseFloat(rateForRow(entry, jobCostRates)) || 0; // s6costing-4d8a2f63 — cost rate from jobCosts (legacy: the row)
            const billRate = Number(emp?.billableRate) || 0;   // charge-out rate (mirrors Increment 2's rate<=0 skip)
            rows.push({
                userId: uid,
                name: entry?.name || emp?.name || 'Employee',
                hours,
                costAmount: hours * costRate,
                billRate,
                billAmount: hours * billRate,
                punches: userPunches,
                // jobtimefix-4a9e2d73-b — surfaced on the COLLAPSED row so a capped punch is
                // visible without expanding. This is the mark that has been written into the dark
                // since the cap shipped: nothing in either app read needsReview.
                needsReviewCount: userPunches.filter(p => p.needsReview === true).length,
            });
        });
        rows.sort((a, b) => a.name.localeCompare(b.name));
        return rows;
    }, [punchSource, employees, jobCostRates]);

    // laborbill-one-rule-6c2a9e47 — WHAT THE NEXT INVOICE WILL CHARGE, per person, computed by the
    // SAME function the invoice builder runs (CrmApp.createNewInvoiceFromSource) on the SAME inputs:
    // the form's labour rows (jobData — what "Create invoice" hands over), each person's bill-out rate,
    // the hours already on this job's invoices, and the job's CURRENT labour-billing selection
    // (also handed over with the job, so an unsaved change can't make the two disagree).
    const laborBillingPreview = useMemo(() => {
        const billableRateByUid = {};
        const nameByUid = {};
        (employees || []).forEach(u => { billableRateByUid[u.id] = u.billableRate; nameByUid[u.id] = u.name; });
        return computeLaborBilling({
            laborEntries: jobData.jobCosting?.laborEntries || [],
            billableRateByUid,
            nameByUid,
            priorBilledByUid: billedThroughByUser,
            billLabor: resolveBillLabor(billLaborToCustomer, companySettings?.automationSettings),
        }).byUser;
    }, [jobData.jobCosting, employees, billedThroughByUser, billLaborToCustomer, companySettings]);
    // The already-invoiced hours feed that preview, so load them whenever the readout is on screen
    // for a role that sees bill-out amounts (it used to load only when a correction opened).
    const hasPunchReadout = punchReadout.length > 0;
    useEffect(() => {
        if (hasPunchReadout && canViewFinancials && occurrenceJobId) loadBilledThrough();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPunchReadout, canViewFinancials, occurrenceJobId]);

    // ── Apply a job template to the form ─────────────────────────────────────
    // Hour-unit items get quantity calculated from the job's current duration.
    // All other units keep their template quantity.
    const applyTemplate = (template) => {
        if (!template) return;
        if (template.durationType === 'days') {
            setDurationMode('days');
            setJobDurationDays(template.durationDays || 1);
            setSchedulingMode('exact'); // multi-day uses exact internally
        } else {
            setDurationMode('hours');
            const durationMins = template.estimatedDuration || 60;
            setJobDurationHours(Math.floor(durationMins / 60));
            setJobDurationMins(durationMins % 60);
        }
        const durationMins = template.durationType === 'days' ? 0 : (template.estimatedDuration || 60);
        // lineidweb-9e4a2d17 — the one shared template function (target 'job'): Hour lines priced by
        // this job's duration, unit and crew carried, Tax Exempt (tmplexempt-5c9d1f38) and the tax code
        // carried — exactly what this block built before — plus a fresh lineId per line.
        const processedItems = lineIds.templateLinesFor(template.lineItems, { target: 'job', newId: lineIds.newLineId, durationMins, routeGroups });
        setJobData(prev => {
            // Look up the selected customer's sticky note
            const selectedCustomer = customers.find(c => c.id === prev.clientId);
            const stickyNote = (selectedCustomer?.jobNoteEnabled && selectedCustomer?.jobNote?.trim())
                ? selectedCustomer.jobNote.trim()
                : '';
            // Build description: template notes → sticky note → any existing text
            const parts = [template.defaultNotes, stickyNote, prev.description].filter(Boolean);
            // PHASE FU-DUR VERIFIER: r1-fu-dur-3c8e7a91
            // Extend endAt by the template's duration so exact-mode users
            // see a coherent time block immediately after applying a 4hr
            // template (instead of stale endAt = startAt + 1hr that pre-fix
            // behavior produced). Multi-day templates (durationType==='days')
            // skip this — their durationDays handling is separate.
            let nextEndAt = prev.endAt;
            if (template.durationType !== 'days') {
                const prevStartAt = prev.startAt instanceof Date ? prev.startAt : new Date(prev.startAt);
                const computedEnd = new Date(prevStartAt.getTime() + durationMins * 60 * 1000);
                if (!isNaN(computedEnd.getTime())) {
                    nextEndAt = computedEnd;
                }
            }
            return {
                ...prev,
                title: template.name || prev.title,
                description: parts.join('\n'),
                lineItems: processedItems.length > 0 ? processedItems : prev.lineItems,
                arrivalWindow: template.defaultWindow !== 'anytime' ? template.defaultWindow : prev.arrivalWindow,
                endAt: nextEndAt,
            };
        });
        setShowTemplatePicker(false);
    };

    // ── Find first available date ─────────────────────────────────────────────
    // Scans upcoming work days and finds the first where the selected arrival
    // window has enough free time to fit the job, accounting for travel buffer.
    // Travel buffer comes from payrollDefaults.travelBufferMinutes (configurable
    // in Settings → Payroll). Work days come from payrollDefaults.workDays.
    // Never suggests same-day — always starts from tomorrow.
    // fa-jfm-3a91b7e2 — delegates to the shared resolver (Seam 3a). Behaviour byte-for-byte
    // the prior inline loop: browser-local walk, UTC-ISO day key, no terminal fallback (a
    // null result sets nothing — the preserved silent no-op when workDays is empty).
    const findFirstAvailable = () => {
        const travelBuffer  = Number(companySettings?.payrollDefaults?.travelBufferMinutes ?? 30);
        const workDays      = companySettings?.payrollDefaults?.workDays ??
            ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
        const jobDurationMinsTotal = (jobDurationHours * 60 + jobDurationMins) || 60;
        const d = resolveFirstAvailableDate({
            jobs, arrivalWindow, durationMins: jobDurationMinsTotal, travelBuffer, workDays,
            dates: browserDates, companySettings, // aw-seam4
        });
        if (d) setWindowDate(browserDates.dateStr(d));
    };

    const handleChange = (e) => {
        if (isReadOnly) return;
        const { name, value } = e.target;

        // Invalidate the address verification state if the user edits the
        // address — whatever was there (inherited, ok, etc.) is now stale.
        if (name === 'address' && addressGeocode.status !== 'idle') {
            setAddressGeocode({
                status: 'idle', canonical: null, coords: null,
                placeId: null, alternatives: null, differences: null, message: null,
            });
        }

        // phone16-7c2e9a41 — on a NEW event nobody has ticked or unticked yet, the rows follow the type:
        // a non-Job gets the booker ticked, a Job gets its own seed back (so a Job is saved as before).
        // Once someone touches the tick-boxes (laborDirty), the type no longer changes them.
        const seedForType = (type) => (!jobToEdit && !laborDirty.current)
            ? (prev) => withNewEventSeed({ ...prev, eventType: type, jobCosting: { ...prev.jobCosting, laborEntries: getInitialState().jobCosting.laborEntries } })
            : (prev) => prev;

        // When switching away from Job event type, sync startAt/endAt from
        // the current window date state so the exact-time inputs show a
        // sensible value instead of a stale or midnight timestamp.
        if (name === 'eventType' && value !== 'Job' && jobData.eventType === 'Job') {
            const base = windowDate ? new Date(windowDate + 'T00:00:00') : new Date();
            const awWin = getArrivalWindow(arrivalWindow, companySettings); // aw-seam4
            const syncedStart = new Date(base);
            syncedStart.setHours(awWin.startH, awWin.startM, 0, 0);
            const syncedEnd = new Date(syncedStart.getTime() + (jobDurationHours * 60 + jobDurationMins) * 60000 || 3600000);
            setJobData(prev => seedForType(value)({ ...prev, eventType: value, startAt: syncedStart, endAt: syncedEnd }));
            return;
        }

        if (name === 'eventType') { setJobData(prev => seedForType(value)({ ...prev, eventType: value })); return; } // phone16-7c2e9a41
        setJobData(prev => ({ ...prev, [name]: value }));
    };

    const handleLineItemChange = (index, field, value) => {
        if (isReadOnly) return;
        const updatedLineItems = [...jobData.lineItems];
        const item = { ...updatedLineItems[index] };

        if (field === 'addToCatalog' || field === 'taxExempt') {
            item[field] = value; 
        }
        else if (field === 'service') {
            const selectedService = services.find(s => s.id === value);
            if (selectedService) {
                item.serviceId = selectedService.id;
                item.name = selectedService.name;
                item.description = selectedService.description;
                item.price = selectedService.price;
                item.type = selectedService.type || 'Service'; 
                item.taxCodeId = selectedService.taxCodeId || ''; 
                item.addToCatalog = false; 
            } else {
                item.serviceId = '';
                item.addToCatalog = false;
                item.taxCodeId = '';
            }
        } 
        else {
            item[field] = value;
        }
        
        updatedLineItems[index] = item;
        setJobData(prev => ({ ...prev, lineItems: updatedLineItems }));
    };

    // PHASE FO-3 (Field Ops Routing Cut, Piece C) VERIFIER: r1-fo-jfm-markreviewed-4f7b2c91
    // Immediate-persist mark-reviewed (Decision 1a): the moment the admin clicks
    // "Mark as reviewed", we (1) flip that item's reviewStatus to 'reviewed'
    // PERSISTING the item's current price, (2) resolve the linked price_review
    // flag via the existing resolveNoteFlag callable so it leaves the assignee's
    // inbox, all in one write — not waiting on the modal's Save. Once no items
    // remain pending_review, the tech's mobile completion gate clears
    // automatically (it is data-driven on hasPendingReviewItems).
    //
    // Graceful null handling (Decision 3): an item may be pending_review with
    // reviewFlagNoteId === null (e.g. the flag creation failed under the mobile
    // Option-1 policy). In that case we still flip the status; there is simply
    // no flag to resolve.
    const [reviewingIndex, setReviewingIndex] = useState(null);

    const handleMarkItemReviewed = async (index) => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId) return;
        const target = jobData.lineItems[index];
        if (!target) return;

        setReviewingIndex(index);
        try {
            // 1. Build the updated lineItems array with this item flipped to reviewed.
            //    Persist whatever price is currently in the field for this item.
            const updatedLineItems = jobData.lineItems.map((li, i) =>
                i === index ? { ...li, reviewStatus: 'reviewed' } : li
            );

            // 2. Persist the lineItems change immediately to the job doc.
            // recurkey-7b2f9d34 — WAS jobToEdit.id. On a recurring occurrence that is the
            // TEMPLATE id, which has no jobs/ document, so this updateDoc threw into the
            // catch below and the review silently never persisted. Addressing the
            // occurrence makes it land on the visit the operator is actually reviewing.
            if (!occurrenceJobId) throw new Error('This visit has no job record yet.');
            await updateDoc(doc(db, `organizations/${orgId}/jobs`, occurrenceJobId), {
                lineItems: updatedLineItems,
                updatedAt: serverTimestamp(),
            });

            // 3. Resolve the linked flag, if there is one.
            if (target.reviewFlagNoteId) {
                try {
                    const resolveFlag = httpsCallable(functions, 'resolveNoteFlag');
                    await resolveFlag({
                        orgId,
                        noteId: target.reviewFlagNoteId,
                        resolutionNote: `Pricing reviewed for "${target.name || 'item'}".`,
                    });
                } catch (flagErr) {
                    // Non-fatal: the item is already marked reviewed in the job.
                    // The flag may linger in the inbox; log but don't roll back.
                    console.warn('resolveNoteFlag failed (item still marked reviewed):', flagErr?.code || flagErr);
                }
            }

            // 4. Reflect the change in local state so the UI updates immediately.
            setJobData(prev => ({ ...prev, lineItems: updatedLineItems }));
        } catch (err) {
            console.error('Failed to mark item reviewed:', err);
            alert('Could not mark this item as reviewed. Please try again.');
        } finally {
            setReviewingIndex(null);
        }
    };

    const addLineItem = () => {
        if (isReadOnly) return;
        setJobData(prev => ({
            ...prev,
            lineItems: [...prev.lineItems, lineIds.newLine(defaultLineItem, lineIds.newLineId)] // lineidweb-9e4a2d17 — a fresh object with its own lineId (was the one shared default object)
        }));
    };

    const removeLineItem = (index) => {
        if (isReadOnly) return;
        const updatedLineItems = [...jobData.lineItems];
        updatedLineItems.splice(index, 1);
        setJobData(prev => ({ ...prev, lineItems: updatedLineItems }));
    };

    const handleEmployeeAssignToggle = (employee) => {
        if (isReadOnly) return;

        const isAlreadyAssigned = jobData.jobCosting.laborEntries.some(e => e.userId === employee.id);

        // jobedit-nostomp-7c3f1a58 — a person with clocked time cannot be removed here.
        // Their time was really worked, so it stays on the job's cost; taking it off is a
        // deliberate correction and belongs to the audited correction seam, not to a save
        // of something unrelated. The lock is in the handler, not only on the checkbox,
        // for the same reason mobile's shift editor puts `if (!canEdit) return;` at the
        // top of handleSaveShift rather than trusting the door. Bails before the
        // time-off/window checks below, which have nothing to warn about on a no-op.
        const isClocked = jobData.jobCosting.laborEntries.some(
            e => e.userId === employee.id && e.source === 'clocked'
        );
        if (isAlreadyAssigned && isClocked) return;

        // jobedit-nostomp-7c3f1a58 — a real labour change; the save may now write the array.
        laborDirty.current = true;

        // timeoff-dblbook-sharedwindow — Recompute THIS job's window ONCE from live
        // state and share it between BOTH assignment-time checks below. In window
        // mode jobData.startAt/endAt are stale (the create-time "now") until save, so
        // reading them directly made the Time-Off block test against today and
        // silently pass for any future-dated window job. Mirrors the save path
        // (COMPUTE startAt / endAt block) and availableResources' staleness fix.
        const isJob = jobData.eventType === 'Job';
        let curStart = null, curEnd = null, curWindowKey = null, curDayKey = null;

        if (isJob && durationMode === 'days' && windowDate) {
            // Multi-day: midnight of day 1 → 23:59:59 on the last day. Window
            // key stays null (multi-day is never a same-window sibling).
            curStart = new Date(windowDate + 'T00:00:00'); curStart.setHours(0, 0, 0, 0);
            curEnd = new Date(windowDate + 'T00:00:00');
            curEnd.setDate(curEnd.getDate() + (jobDurationDays - 1));
            curEnd.setHours(23, 59, 59, 999);
        } else if (isJob && schedulingMode === 'window' && windowDate) {
            const base = new Date(windowDate + 'T00:00:00');
            const w = getArrivalWindow(arrivalWindow, companySettings); // aw-seam4
            curStart = new Date(base); curStart.setHours(w.startH, w.startM, 0, 0);
            const totalMins = (jobDurationHours * 60) + jobDurationMins;
            const safeMins = totalMins > 0 ? totalMins : 60; // min 1h, mirrors save path
            curEnd = new Date(curStart.getTime() + safeMins * 60 * 1000);
            curWindowKey = arrivalWindow || null;
            curDayKey = windowDate; // already a 'YYYY-MM-DD' local string
        } else {
            curStart = jobData.startAt instanceof Date ? jobData.startAt : jobData.startAt?.toDate?.();
            curEnd   = jobData.endAt   instanceof Date ? jobData.endAt   : jobData.endAt?.toDate?.();
        }

        const validCur = curStart instanceof Date && !isNaN(curStart) && curEnd instanceof Date && !isNaN(curEnd);

        // Time-Off HARD BLOCK — runs FIRST, no override (unchanged in every respect
        // except the window it compares against, which is now the recomputed
        // curStart/curEnd instead of the stale jobData.startAt/endAt). If no usable
        // window could be computed (validCur false) the block skips and assignment
        // proceeds: a date-less event has no schedule for time off to overlap, this
        // matches the block's original skip-when-no-dates behavior, and it mirrors
        // availableResources' `if (!jobStart || !jobEnd) return`.
        if (!isAlreadyAssigned && validCur && jobs) {
            const jobStart = curStart;
            const jobEnd = curEnd;
            const jobInterval = { start: jobStart, end: jobEnd };

            const timeOffConflict = jobs.find(j => {
                if (j.eventType !== 'Time Off') return false;
                // timeoff-fieldworker-membership — This block only checks app users
                // (employee.id is a users uid, from the app-user checkbox list).
                // Field workers are a SEPARATE population with their own id space
                // (fieldWorkers doc ids in assignedFieldWorkerIds) and their own
                // assignment path (the field-worker checkbox list, which never calls
                // this handler). Their scheduling is NOT checked here. See
                // crew-resources-audit/12-timeoff-teammember-failure.md.
                const onTimeOff = (j.assignedTo || []).includes(employee.id);
                if (!onTimeOff) return false;
                const offStart = j.startAt instanceof Date ? j.startAt : j.startAt?.toDate?.();
                const offEnd = j.endAt instanceof Date ? j.endAt : j.endAt?.toDate?.();
                if (!offStart || !offEnd) return false;
                return (
                    isWithinInterval(offStart, jobInterval) ||
                    isWithinInterval(offEnd, jobInterval) ||
                    isWithinInterval(jobStart, { start: offStart, end: offEnd }) ||
                    isWithinInterval(jobEnd, { start: offStart, end: offEnd })
                );
            });

            if (timeOffConflict) {
                const offStart = timeOffConflict.startAt instanceof Date ? timeOffConflict.startAt : timeOffConflict.startAt?.toDate?.();
                const offEnd = timeOffConflict.endAt instanceof Date ? timeOffConflict.endAt : timeOffConflict.endAt?.toDate?.();
                const fmt = (d) => d?.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
                alert(`${employee.name} is on ${timeOffConflict.timeOffType || 'Time Off'} from ${fmt(offStart)} to ${fmt(offEnd)} and cannot be assigned to this job.`);
                return;
            }
        }

        // dbl-book-seam1-7f3c9e21 — individual-tech double-booking check. Runs
        // STRICTLY AFTER the Time-Off hard block above (unchanged) — so it only
        // reaches here when Time Off did not fire. WARN, never block: the user can
        // always proceed. Uses the SHARED recomputed window (curStart/curEnd/
        // curWindowKey/curDayKey/validCur) built once above the Time-Off block.
        if (!isAlreadyAssigned && jobs) {
            // Consumes the SHARED window (curStart/curEnd/curWindowKey/curDayKey/
            // validCur) computed once above — identical to the computation this
            // block used to do inline; the Time-Off block now shares it too.
            if (validCur) {
                // Map the already-loaded jobs into the pure helper's shape. No new
                // Firestore query. Derive each job's windowKey/dayKey from its
                // persisted schedulingMode/arrivalWindow/startAt.
                const mapped = jobs.map(j => {
                    const s = toJsDate(j.startAt);
                    const e = toJsDate(j.endAt);
                    return {
                        id: j.id,
                        eventType: j.eventType,
                        status: j.status,
                        assignedTo: j.assignedTo || [],
                        start: s,
                        end: e,
                        windowKey: j.schedulingMode === 'window' ? (j.arrivalWindow || null) : null,
                        dayKey: s ? format(s, 'yyyy-MM-dd') : null,
                        title: j.title,
                        customerName: j.customerDetails?.name || j.clientName || 'N/A',
                    };
                });

                const { sameWindow, clashes } = classifyTechScheduleConflicts({
                    technicianId: employee.id,
                    currentJob: { start: curStart, end: curEnd, windowKey: curWindowKey, dayKey: curDayKey },
                    jobs: mapped,
                    excludeJobId: occurrenceJobId, // recurkey-7b2f9d34 — ditto, for technician schedule clashes
                });

                // At most ONE warning and ONE notice per assignment; when both apply
                // the warning wins and the notice is suppressed.
                if (clashes.length > 0) {
                    const first = clashes[0];
                    const fmtDT = (d) => d instanceof Date && !isNaN(d)
                        ? d.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                        : '';
                    const jobLabel = `${first.title || 'a job'}${first.customerName && first.customerName !== 'N/A' ? ` for ${first.customerName}` : ''}`;
                    const more = clashes.length - 1;
                    const extra = more > 0 ? ` (and ${more} other${more > 1 ? 's' : ''})` : '';
                    const proceed = window.confirm(
                        `${employee.name} is already on ${jobLabel} (${fmtDT(first.start)} – ${fmtDT(first.end)}), which overlaps this job${extra}. Assign anyway?`
                    );
                    if (!proceed) return; // Cancel → not assigned
                    // Confirm → fall through to the setJobData append below
                } else if (sameWindow.length > 0) {
                    const wDef = getArrivalWindows(companySettings).find(x => x.key === curWindowKey);
                    const label = wDef?.shortLabel || wDef?.label || curWindowKey;
                    const count = sameWindow.length + 1; // siblings + the new assignment
                    const dayLabel = curStart.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
                    alert(
                        `${employee.name} now has ${count} jobs in the ${label} window on ${dayLabel}. The routing tool will sequence these into successive stops. (Informational — no action needed.)`
                    );
                    // Informational only — assignment proceeds regardless.
                }
            }
        }

        setJobData(prev => {
            const entries = prev.jobCosting.laborEntries;
            const isAssigned = entries.some(entry => entry.userId === employee.id);
            if (isAssigned) {
                return { ...prev, jobCosting: { ...prev.jobCosting, laborEntries: entries.filter(e => e.userId !== employee.id) } };
            } else {
                // r7-b2a-stamp-4b8e1f60 — rate stays null; stampJobLaborRates resolves it server-side
                // via getLaborCostRate. Never seed from employee.hourlyRate — that's the payroll wage,
                // not the job-cost rate, and it disagreed with the clocked rows on the same job.
                const newEntry = { userId: employee.id, name: employee.name, rate: null, hours: 1 };
                return { ...prev, jobCosting: { ...prev.jobCosting, laborEntries: [...entries, newEntry] } };
            }
        });
    };

    const handleLaborHoursChange = (employeeId, hours) => {
        if (isReadOnly) return;
        // jobedit-nostomp-7c3f1a58 — currently unrendered (no hours input exists in this
        // form today), but flagged anyway so it is already correct if one is ever wired up.
        laborDirty.current = true;
        setJobData(prev => {
            const updatedEntries = prev.jobCosting.laborEntries.map(entry => {
                if (entry.userId === employeeId) { return { ...entry, hours: hours === '' ? '' : parseFloat(hours) || 0 }; }
                return entry;
            });
            return { ...prev, jobCosting: { ...prev.jobCosting, laborEntries: updatedEntries } };
        });
    };

    const handleDateChange = (e) => {
        if (isReadOnly) return;
        const { name, value } = e.target;
        const newDate = new Date(value);
        if (name === 'startAt') {
            const newEndDate = new Date(newDate.getTime());
            newEndDate.setHours(newEndDate.getHours() + 1);
            setJobData(prev => ({ ...prev, startAt: newDate, endAt: newEndDate }));
        } else {
            setJobData(prev => ({ ...prev, [name]: newDate }));
        }
    };

    // PROPERTIES v2 — shared helper: inherit a verified address's geocode onto the job,
    // or reset to idle. `source` is a customer OR a property (both carry the same fields).
    const inheritAddressGeocode = (source) => {
        if (source && source.addressVerified === true && source.addressCoords) {
            setAddressGeocode({
                status: 'inherited',
                canonical: source.addressCanonical || null,
                coords:    source.addressCoords,
                placeId:   source.addressPlaceId || null,
                alternatives: null,
                differences: null,
                message: null,
            });
        } else {
            setAddressGeocode({
                status: 'idle', canonical: null, coords: null,
                placeId: null, alternatives: null, differences: null, message: null,
            });
        }
    };

    const handleCustomerChange = (e) => {
        if (isReadOnly) return;
        const selectedCustomerId = e.target.value;
        const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
        // Append customer sticky note to description if enabled
        const stickyNote = (selectedCustomer?.jobNoteEnabled && selectedCustomer?.jobNote?.trim())
            ? selectedCustomer.jobNote.trim()
            : '';
        // PROPERTIES v2 — if the customer has multiple service locations, force an explicit
        // choice: clear the address until one is picked. Otherwise inherit the main address.
        const hasProperties = (selectedCustomer?.properties || []).length > 0;
        setJobData(prev => ({
            ...prev,
            clientId: selectedCustomerId,
            clientName: selectedCustomer ? selectedCustomer.name : '',
            address: (!selectedCustomer || hasProperties) ? '' : buildAddressString(selectedCustomer),
            billTo: null, // billto-jfm-custreset-4a8d2e71 -- bill-to never survives a customer switch
            // Append sticky note to existing description (don't replace)
            description: stickyNote
                ? (prev.description ? `${prev.description}\n${stickyNote}` : stickyNote)
                : (prev.description || ''),
        }));

        setServiceLocation(hasProperties ? '' : 'main');
        setPendingThirdParty(false); // billto-jfm-custreset2-8f1c6b25
        setBillToTouched(false);
        // No properties → inherit the customer's main address; otherwise reset (idle) until
        // the user chooses a location via the Service location selector.
        inheritAddressGeocode(hasProperties ? null : selectedCustomer);
    };

    // PROPERTIES v2 — user picked a service location: '' unchosen | 'main' | property id.
    const handleServiceLocationChange = (e) => {
        if (isReadOnly) return;
        const value = e.target.value;
        setServiceLocation(value);
        const selectedCustomer = customers.find(c => c.id === jobData.clientId);
        const source = value === 'main'
            ? selectedCustomer
            : (selectedCustomer?.properties || []).find(p => p.id === value);
        const prop = (value && value !== 'main') ? (source || null) : null;
        // billto-jfm-handlers-9c3b7d58 -- apply the property's default bill-to when the user
        // has not touched the Bill To selector; a propertyContact bill-to never outlives
        // its property. Blocks are built ONLY by the shared builder (src/utils/billTo.js).
        setJobData(prev => {
            let billTo = prev.billTo;
            if (!billToTouched) {
                const def = prop?.defaultBillTo;
                if (def?.mode === 'propertyContact') billTo = buildBillToFromProperty(prop, selectedCustomer);
                else if (def?.mode === 'thirdParty') billTo = buildBillToFromCustomer(customers.find(c => c.id === def.thirdPartyCustomerId));
                else billTo = null;
            } else if (billTo?.mode === 'propertyContact' && (!prop || prop.id !== billTo.propertyId)) {
                billTo = null;
            }
            return {
                ...prev,
                address: (value && source) ? buildAddressString(source) : '',
                billTo,
            };
        });
        if (!billToTouched) setPendingThirdParty(false);
        inheritAddressGeocode((value && source) ? source : null);
    };

    const handleBillToChoiceChange = (e) => {
        if (isReadOnly) return;
        const choice = e.target.value;
        setBillToTouched(true);
        if (choice === 'thirdParty') {
            setPendingThirdParty(true);
            setJobData(prev => ({ ...prev, billTo: prev.billTo?.mode === 'thirdParty' ? prev.billTo : null }));
            return;
        }
        setPendingThirdParty(false);
        if (choice === 'propertyContact') {
            const cust = customers.find(c => c.id === jobData.clientId);
            const p = (cust?.properties || []).find(x => x.id === serviceLocation);
            setJobData(prev => ({ ...prev, billTo: buildBillToFromProperty(p, cust) }));
        } else {
            setJobData(prev => ({ ...prev, billTo: null }));
        }
    };

    const handleBillToThirdPartyChange = (e) => {
        if (isReadOnly) return;
        const third = customers.find(c => c.id === e.target.value);
        setBillToTouched(true);
        setPendingThirdParty(!third);
        setJobData(prev => ({ ...prev, billTo: buildBillToFromCustomer(third) }));
    };

    // ── Address verification — debounced blur trigger ──────────────────────
    // Same shape as CustomerFormModal but operates on a single freeform
    // address string rather than the 6-field breakdown.

    const runJobAddressVerification = async (addressString) => {
        const trimmed = (addressString || '').trim();
        if (!trimmed) return;
        setAddressGeocode({
            status: 'checking', canonical: null, coords: null,
            placeId: null, alternatives: null, differences: null, message: null,
        });
        try {
            const result = await validateAddressViaCF(functions, null, trimmed);
            if (result.status === 'ok') {
                setAddressGeocode({
                    status: 'ok',
                    canonical: result.canonical,
                    coords:    result.coords,
                    placeId:   result.placeId,
                    alternatives: null,
                    differences: null,
                    message: null,
                });
            } else if (result.status === 'ambiguous') {
                setAddressGeocode({
                    status: 'ambiguous',
                    canonical: null, coords: null, placeId: null,
                    alternatives: result.alternatives || [],
                    differences: null,
                    message: null,
                });
            } else if (result.status === 'not_found') {
                setAddressGeocode({
                    status: 'not_found',
                    canonical: null, coords: null, placeId: null,
                    alternatives: null, differences: null,
                    message: "We couldn't find this address. You can still save the job.",
                });
            } else {
                setAddressGeocode({
                    status: 'error',
                    canonical: null, coords: null, placeId: null,
                    alternatives: null, differences: null,
                    message: 'Verification temporarily unavailable.',
                });
            }
        } catch (err) {
            const code = err?.code || '';
            const message = code === 'functions/resource-exhausted'
                ? 'Address verification rate limit reached.'
                : 'Verification temporarily unavailable.';
            setAddressGeocode({
                status: 'error',
                canonical: null, coords: null, placeId: null,
                alternatives: null, differences: null,
                message,
            });
        }
    };

    const scheduleJobAddressVerification = (addressString) => {
        if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
        verifyTimerRef.current = setTimeout(() => {
            verifyTimerRef.current = null;
            runJobAddressVerification(addressString);
        }, 300);
    };

    // Cleanup the timer on unmount so a late-fire doesn't set state on an
    // unmounted component if the user closes the modal during debounce.
    useEffect(() => {
        return () => {
            if (verifyTimerRef.current) {
                clearTimeout(verifyTimerRef.current);
                verifyTimerRef.current = null;
            }
        };
    }, []);

    const handleAddressBlur = () => {
        if (isReadOnly) return;
        // Skip if we already have a result and the address hasn't been edited
        // since (handleChange invalidates to 'idle' on edit). This avoids
        // re-verifying when the user just clicks in/out without changing
        // anything.
        if (addressGeocode.status !== 'idle') return;
        scheduleJobAddressVerification(jobData.address);
    };

    // Called when user picks one of the alternatives from an 'ambiguous' result.
    // For the job form's single-field address, we replace the entire string
    // with Google's canonical — it's the cleanest UX.
    const handlePickJobAddressAlternative = (alt) => {
        setJobData(prev => ({ ...prev, address: alt.canonical }));
        setAddressGeocode({
            status: 'ok',
            canonical: alt.canonical,
            coords:    alt.coords,
            placeId:   alt.placeId,
            alternatives: null,
            differences: null,
            message: null,
        });
    };

    const handleRecurrenceChange = (e) => {
        if (isReadOnly) return;
        const { name, value, checked } = e.target;
        setJobData(prev => {
            const rec = { ...prev.recurrence };
            if (name === 'endDate') rec.endDate = orgDayEnd(value, orgTz) || new Date(value); // orgtime-7c2e4b19 — the END of the picked company day
            else if (name === 'neverEnds') rec.neverEnds = checked;
            else rec[name] = value; // frequency / unit / interval (interval sanitized on save)

            // Switching to Custom: default the end to one interval out (on-cycle),
            // so the user starts from a real occurrence date, not the 3-month default.
            if (name === 'frequency' && value === 'Custom') {
                rec.interval = rec.interval || 1;
                rec.unit = rec.unit || 'weeks';
                const firstNext = advanceRecurrence(new Date(prev.startAt), rec, prev.startAt);
                if (firstNext) rec.endDate = orgEndOfDay(firstNext, orgTz); // orgtime-7c2e4b19
            } else if (rec.frequency === 'Custom' && !rec.neverEnds) {
                const oneCycleVisit = advanceRecurrence(new Date(prev.startAt), rec, prev.startAt);
                const oneCycle = oneCycleVisit ? orgEndOfDay(oneCycleVisit, orgTz) : null; // orgtime-7c2e4b19
                if (name === 'interval' || name === 'unit') {
                    // New cadence: keep the end on-cycle, but never below one full cycle.
                    const snapped = snapEndToCycle(prev.startAt, rec, rec.endDate, orgTz);
                    rec.endDate = (oneCycle && snapped < oneCycle) ? oneCycle : snapped;
                } else if (name === 'endDate') {
                    // Explicit pick: snap down to the last real occurrence <= their choice.
                    rec.endDate = snapEndToCycle(prev.startAt, rec, rec.endDate, orgTz);
                }
            }
            return { ...prev, recurrence: rec };
        });
    };

    const handleReminderDateChange = (e) => {
        // orgtime-7c2e4b19 — the company's midnight on the picked day (was the UTC parse, which
        // saved the day before in every Canadian zone).
        const newDate = orgDayStart(e.target.value, orgTz);
        if (!newDate) return;
        setReminderDate(newDate);
        setReminderDateOverridden(true);
    };

    const handleResetReminderDate = () => {
        const orgSettings = companySettings?.jobReminderSettings;
        const calculated = calculateReminderDate(jobData.startAt, orgSettings, orgTz);
        setReminderDate(calculated);
        setReminderDateOverridden(false);
    };

    const handleSave = async () => {
        if (isReadOnly) return;
        // billto-jfm-guard-1d7f3b46 -- "Another customer..." chosen but nobody picked yet.
        if (pendingThirdParty && !jobData.billTo) {
            alert('Choose which customer to bill, or set Bill to back to Main customer.');
            return;
        }
        const isNonJobEvent = jobData.eventType !== 'Job';
        const isBeingCompleted = jobData.status === 'Completed' && jobToEdit?.status !== 'Completed';
        const isBeingCancelled = jobData.status === 'Canceled' && jobToEdit?.status !== 'Canceled';

        // PHASE FU-DUR VERIFIER: r1-fu-dur-5b9a1c30
        // Soft warning: when a Job is in exact mode and the chosen time block
        // (endAt - startAt) differs from the Estimated Duration by more than
        // 15 minutes, ask the user to confirm. Captures the three-way mismatch
        // problem: template duration, Estimated Duration, and exact-time block
        // can drift apart silently. Window mode skips (no user-set endAt).
        // Multi-day mode skips (durationDays uses different semantics).
        // Non-Job events skip (no Estimated Duration field).
        if (!isNonJobEvent && schedulingMode === 'exact' && durationMode !== 'days') {
            const startMs = (jobData.startAt instanceof Date ? jobData.startAt : new Date(jobData.startAt)).getTime();
            const endMs   = (jobData.endAt   instanceof Date ? jobData.endAt   : new Date(jobData.endAt)).getTime();
            if (!isNaN(startMs) && !isNaN(endMs) && endMs > startMs) {
                const blockMins    = Math.round((endMs - startMs) / 60000);
                const estimateMins = (jobDurationHours * 60 + jobDurationMins) || 60;
                const driftMins    = Math.abs(blockMins - estimateMins);
                if (driftMins > 15) {
                    const blockH = Math.floor(blockMins / 60), blockM = blockMins % 60;
                    const estH   = Math.floor(estimateMins / 60), estM = estimateMins % 60;
                    const fmt = (h, m) => `${h}h ${m}m`;
                    const proceed = window.confirm(
                        `Heads up — your time block (${fmt(blockH, blockM)}) doesn't match the Estimated Duration (${fmt(estH, estM)}).\n\n` +
                        `These should usually match. Save anyway?`
                    );
                    if (!proceed) return;
                }
            }
        }

        if (isBeingCancelled) {
            setIsCancellationModalOpen(true);
            return;
        }

        // ── Multi-crew completion guard ────────────────────────────────────────
        // If this is a Job being manually set to Completed via the status dropdown,
        // and it has multiple crews with some still pending, warn the user.
        if (!isNonJobEvent && isBeingCompleted && jobToEdit?.id) {
            const crewsTotal = jobToEdit.crewsTotal || 0;
            const crewsCompleted = jobToEdit.crewsCompleted || 0;
            if (crewsTotal > 1 && crewsCompleted < crewsTotal) {
                const pendingCrewNames = (jobToEdit.crews || [])
                    .filter(c => !c.completedAt)
                    .map(c => c.crewGroupName)
                    .filter(Boolean)
                    .join(', ');
                const confirmed = window.confirm(
                    `This job has ${crewsTotal} crews assigned and only ${crewsCompleted} have marked their portion complete.\n\nPending: ${pendingCrewNames || 'some crews'}\n\nOverride and mark all crews complete?`
                );
                if (!confirmed) return;
                // Mark all crew entries as complete via manager override
                jobData.crewsCompleted = crewsTotal;
                jobData.crews = (jobToEdit.crews || []).map(crew => ({
                    ...crew,
                    completedAt: crew.completedAt || new Date(),
                    completedBy: crew.completedBy || currentUserProfile.id,
                    managerOverride: !crew.completedAt,
                }));
            }
        }

        if (isNonJobEvent && isBeingCompleted) {
            setCompletionNoteCallback(() => (noteText) => {
                executeSave({ completionNote: noteText });
            });
            setIsCompletionNoteModalOpen(true);
            return;
        }

        // jobdup-3e7b1c52 — a job with NO repeat setting is a one-off ("never repeats"), not a series.
        // `undefined !== 'Never'` used to be true, so a job saved without the field got the
        // "this visit only / all visits" question, and "this visit only" then created a COPY of the job
        // at the new time (2026-10-16, test company #1930 → #1931). Census 2026-10-16: 0 such jobs in any
        // company — every real creator writes { frequency: 'Never' }; the trigger was a test seed.
        const savedRepeats = !!jobToEdit?.recurrence?.frequency && jobToEdit.recurrence.frequency !== 'Never';
        if (jobToEdit?.id && (savedRepeats || jobToEdit.recurringJobId) && jobData.eventType === 'Job') {
            setShowRecurrencePrompt(true);
            return;
        }
        await executeSave({});
    };
    
    const executeSave = async (options = {}) => {
        if (isReadOnly) return;
        const { completionNote = null, cancellationInfo = null } = options;
        // jobdup-3e7b1c52 — "this visit only" / "this and future" exist ONLY for a real series: an
        // occurrence (opened through its template — _templateId), a generated instance
        // (recurringJobId), or a series template (a saved repeat rule). For anything else the choice
        // is dropped and the save lands on the job itself — it can never create a copy or a series.
        const isRealSeries = !!jobToEdit?._templateId || !!jobToEdit?.recurringJobId
            || (!!jobToEdit?.recurrence?.frequency && jobToEdit.recurrence.frequency !== 'Never');
        const editChoice = (options.editChoice && options.editChoice !== 'all' && !isRealSeries) ? null : (options.editChoice ?? null);
        if (options.editChoice && editChoice === null) {
            console.warn('JobFormModal [jobdup-3e7b1c52]: a series choice on a one-off job was ignored; saving the job itself.');
        }

        if (!jobData.clientId && jobToEdit?.clientId) {
            jobData.clientId = jobToEdit.clientId;
            jobData.clientName = jobToEdit.clientName;
        }
        if (!jobData.clientId || !jobData.title) {
            alert("Please select a customer and enter a title.");
            return;
        }
        // PROPERTIES v2 — force a service-location choice when the customer has properties.
        const _selCustForLoc = customers.find(c => c.id === jobData.clientId);
        if ((_selCustForLoc?.properties || []).length > 0 && serviceLocation === '') {
            alert("This customer has multiple service locations. Please choose which one this job is for.");
            return;
        }

        // nolines-59a4771f — a Job saves with no line items (Alberni): untouched blank rows are dropped
        // here, so the default row on a new job (or the one a reopened empty job gets back) never blocks
        // the save. A row with anything typed in it is kept, and still needs a name. Other event types
        // save their rows exactly as before.
        const linesToSave = jobData.eventType === 'Job' ? dropBlankLines(jobData.lineItems || []) : jobData.lineItems;

        // Validate line items have names
        if (jobData.eventType === 'Job') {
            const unnamedIndex = linesToSave.findIndex(item => !(item.name || '').trim());
            if (unnamedIndex !== -1) {
                alert(`Line item ${unnamedIndex + 1} needs a name before saving.`);
                return;
            }
        }

        setIsSaving(true);
        setShowRecurrencePrompt(false);

        const potentialConflicts = selectedResources.filter(resourceId => {
            const resource = availableResources.find(r => r.id === resourceId);
            return resource?.conflict?.type === 'potentialConflict';
        });

        if (potentialConflicts.length > 0) {
            const conflictNames = potentialConflicts.map(id => availableResources.find(r => r.id === id)?.name).join(', ');
            if (!window.confirm(`Warning: The following resources might have scheduling conflicts: ${conflictNames}. Assign anyway?`)) {
                setIsSaving(false); 
                return; 
            }
        }

        const orgId = currentUserProfile.organizationId;
        const isRecurring = jobData.recurrence.frequency !== 'Never';
        // 'only_this' always saves to the individual jobs collection, never the template
        const isEditingInstance = !!jobToEdit?.recurringJobId; // true when editing a generated instance
        const saveAsInstance = editChoice === 'only_this' || (editChoice === null && isEditingInstance && jobToEdit?.id && !isRecurring);
        const collectionName = (isRecurring && editChoice !== 'only_this') ? 'recurringJobs' : 'jobs';
        // r2fix-rec-templateid-carry-5d1c9a72
        // The real recurring-series template id, carried explicitly from hydration
        // (openJobEditorModal sets jobToEdit._templateId to the series template's own
        // doc id). Every recurring save path uses THIS, never jobToEdit.id: a
        // materialized instance stores its own doc id in an `id` field that the Seam 2
        // instance hydration spreads over the form's id, so jobToEdit.id can be the
        // instance's id, not the template's. Falls back to the old inference for
        // non-recurring / directly-opened jobs that carry no _templateId.
        const recurringTemplateId = jobToEdit?._templateId
            || (isEditingInstance ? jobToEdit?.recurringJobId : jobToEdit?.id);
        const jobsPath = `organizations/${orgId}/${collectionName}`;
        let checklistsNotAdded = []; // chklstnew-5a8c2e71 — names of picked checklists the server didn't add
        const servicesPath = `organizations/${orgId}/services`;

        // lineidweb-9e4a2d17 — every line saved with a unique lineId (older jobs and recurring
        // templates get theirs here). The recurring branches below re-point them per target document.
        const processedLineItems = lineIds.ensureLineIds(await Promise.all(linesToSave.map(async (item) => { // nolines-59a4771f — blank rows already dropped
            if (item.addToCatalog && !item.serviceId && item.name) {
                try {
                    const newServiceRef = await addDoc(collection(db, servicesPath), {
                        name: item.name,
                        description: item.description || '',
                        price: Number(item.price),
                        type: item.type || 'Service',
                        unit: 'Unit',
                        taxCodeId: item.taxCodeId || '', 
                        createdAt: serverTimestamp()
                    });
                    return { ...item, serviceId: newServiceRef.id, addToCatalog: false };
                } catch (e) {
                    console.error("Failed to save new catalog item:", e);
                    return item; 
                }
            }
            const { addToCatalog, ...cleanItem } = item;
            return cleanItem;
        })), lineIds.newLineId);

        const isNonJobSave = jobData.eventType !== 'Job';

        // jobedit-nostomp-7c3f1a58 — re-read this job's labour rows as they stand NOW,
        // immediately before the write, so a punch-out that landed while this modal was
        // open survives the save. Clocked rows only ever live on a job document, so for a
        // recurring occurrence that is the instance doc (_instanceId), never the template.
        //
        // canWriteLabor decides whether the payload carries jobCosting at all:
        //   new job            -> yes, the form's rows are the seed
        //   recurring template -> yes when dirty (a template has no clocked time)
        //   existing job, read OK  -> only when a human actually changed labour
        //   existing job, read FAILED -> NO. We cannot prove what the server holds, so we
        //     send nothing and leave the array exactly as the server has it. A labour edit
        //     is then silently not applied, which is why it is called out to the operator.
        // A recurring occurrence's instance doc is written by BOTH the 'only_this' and the
        // 'this_and_future' branches, and this_and_future runs with collectionName ===
        // 'recurringJobs' — so the test is "is there an instance/job doc we might write",
        // not the collection name alone. Missing that leaves the clobber live on
        // this_and_future.
        const liveJobId = jobToEdit?.id ? (jobToEdit._instanceId || jobToEdit.id) : null;
        const readsFromJobDoc = !!liveJobId && (collectionName === 'jobs' || !!jobToEdit?._instanceId);
        let liveLaborEntries = null;
        let liveDocExisted = false;
        let liveReadFailed = false;
        if (readsFromJobDoc) {
            try {
                const freshSnap = await getDoc(doc(db, `organizations/${orgId}/jobs`, liveJobId));
                liveDocExisted = freshSnap.exists();
                liveLaborEntries = liveDocExisted
                    ? (freshSnap.data()?.jobCosting?.laborEntries || [])
                    : [];
            } catch (e) {
                console.error('jobedit-nostomp-7c3f1a58: could not re-read job labour at save time', e);
                liveReadFailed = true;
            }
        }
        const mergedLaborEntries = mergeLaborForSave(jobData.jobCosting.laborEntries, liveLaborEntries);
        // !readsFromJobDoc  = a new job, or a template-only write — the form's rows are the
        //                     truth there, and the template write is diffed below anyway.
        // !liveDocExisted   = the doc is about to be CREATED (a projected occurrence being
        //                     materialised as an exception), so it needs its seed.
        const canWriteLabor = liveReadFailed
            ? false
            : (!readsFromJobDoc || !liveDocExisted || laborDirty.current);
        if (liveReadFailed && laborDirty.current) {
            // Worded so it cannot claim a save that has not happened yet — this runs
            // BEFORE the writes below, which may still fail on their own.
            alert("Heads up: your change to who's assigned can't be applied right now — the job's current labour couldn't be read, and writing over it could erase clocked time. Your other changes will still save. Please reopen the job and try that change again.");
        }

        // --- COMPUTE startAt / endAt from arrival window for Jobs in window mode ---
        let finalStartAt = jobData.startAt;
        let finalEndAt   = jobData.endAt;

        if (!isNonJobSave && durationMode === 'days') {
            // Multi-day: midnight start → 23:59:59 on last day
            finalStartAt = new Date(windowDate + 'T00:00:00');
            finalStartAt.setHours(0, 0, 0, 0);
            finalEndAt = new Date(windowDate + 'T00:00:00');
            finalEndAt.setDate(finalEndAt.getDate() + (jobDurationDays - 1));
            finalEndAt.setHours(23, 59, 59, 999);
        } else if (!isNonJobSave && schedulingMode === 'window') {
            const base = new Date(windowDate + 'T00:00:00');
            const w = getArrivalWindow(arrivalWindow, companySettings); // aw-seam4
            finalStartAt = new Date(base);
            finalStartAt.setHours(w.startH, w.startM, 0, 0);
            // endAt = startAt + explicit job duration set by the user
            const totalDurationMins = (jobDurationHours * 60) + jobDurationMins;
            const safeDuration = totalDurationMins > 0 ? totalDurationMins : 60; // minimum 1h fallback
            finalEndAt = new Date(finalStartAt.getTime() + safeDuration * 60 * 1000);
        }

        // apptassign-6e1c9b47 — the ticked people ARE the assignee, for Jobs and for
        // Appointment / Phone Appointment / Follow-Up alike. Jobs derive from the MERGED
        // list exactly as before (jobedit-nostomp-7c3f1a58 — a stale row used to rewrite
        // the assignment as a side effect of any save, the failure
        // functions/scripts/detectAssignmentWipe.js was written to catch). A non-Job with
        // nobody ticked keeps FIX 3 (stored value, else the creator) so it is never saved
        // invisible on the calendar. Rules and reasons: utils/eventAssignees.js.
        const { assignedTo, assignedToNames } = resolveSaveAssignees({
            isNonJobSave,
            mergedLaborEntries,
            liveReadFailed,
            jobToEdit,
            jobData,
            currentUserProfile,
        });

        // Resolve route group members if group mode is active
        const selectedGroup = routeGroups.find(g => g.id === selectedGroupId);
        // r3-cs-visunion-7c2e9a41 — assignedGroupMemberIds must include EVERY crew's
        // members, not just the primary selected group's. The mobile field-tech query
        // is `assignedGroupMemberIds array-contains uid` and job access is gated on the
        // same field (functions/index.js), so a per-crew job's non-primary crews would
        // be invisible to — and unable to open — their own jobs without this union.
        // Additive only: never removes anyone; adds members already assigned via a
        // line-item crew. The common "assign whole job to one group" case (no line-item
        // crews) is unchanged, since lineItemCrews is then empty there.
        const groupMemberIds = Array.from(new Set([
            ...((assignmentMode === 'group' && selectedGroup) ? (selectedGroup.memberIds || []) : []),
            ...lineItemCrews.flatMap(crewId => (routeGroups.find(g => g.id === crewId)?.memberIds || [])),
        ]));

        const existingReminderDate = jobToEdit?.reminderDate;
        const newReminderDateStr   = reminderEnabled && reminderDate ? orgYmd(reminderDate, orgTz) : null; // orgtime-7c2e4b19
        let existingReminderDateStr = null;
        if (existingReminderDate && typeof existingReminderDate.toDate === 'function') {
            existingReminderDateStr = orgYmd(existingReminderDate, orgTz);
        }
        const reminderDateChanged = newReminderDateStr !== existingReminderDateStr;
        const reminderSent = reminderEnabled && reminderDate && !reminderDateChanged
            ? (jobToEdit?.reminderSent || false)
            : false;

        // Recurrence sanitization + canonicalization (Seam 5).
        //  - Non-Custom: store only { frequency, endDate } so preset docs stay byte-identical.
        //  - "Never ends": store a far-future sentinel endDate so the server's
        //    endDate>=today queries still match; the sentinel is never shown.
        //  - A BOUNDED Custom that equals a preset collapses to that preset (one
        //    encoding per schedule -> no duplicate-lattice bugs). Never-ends stays
        //    Custom (presets have no forever option, so nothing to collide with, and
        //    it keeps the neverEnds flag that hides the sentinel in the UI).
        const buildCleanRecurrence = (rec) => {
            const freq = rec.frequency;
            if (freq !== 'Custom') return { frequency: freq, endDate: rec.endDate };
            const interval = Math.max(1, parseInt(rec.interval, 10) || 1);
            const unit = ['days', 'weeks', 'months', 'years'].includes(rec.unit) ? rec.unit : 'weeks';
            const neverEnds = !!rec.neverEnds;
            const endDate = neverEnds ? RECURRENCE_END_SENTINEL : rec.endDate;
            if (!neverEnds) {
                if (interval === 1 && unit === 'weeks')  return { frequency: 'Weekly', endDate };
                if (interval === 2 && unit === 'weeks')  return { frequency: 'Bi-Weekly', endDate };
                if (interval === 1 && unit === 'months') return { frequency: 'Monthly', endDate };
            }
            return { frequency: 'Custom', interval, unit, neverEnds, endDate };
        };
        const cleanRecurrence = buildCleanRecurrence(jobData.recurrence);

        // jobedit-nostomp-7c3f1a58 — jobData was built as { ...initialState, ...jobToEdit },
        // so it carries a copy of EVERY field of the job frozen at open, including the two
        // this form does not own. A merge:true write deep-merges maps but replaces arrays
        // whole, so both used to be reverted by a save of something unrelated:
        //   timePunches — the raw time record itself. This form has no input for it and
        //     there is no case where sending it is right. Server doors only (punchOutJob,
        //     completeJob, closeJobPunchesOnDone). Stripped unconditionally, every path.
        //   jobCosting  — re-attached below only when canWriteLabor, carrying the merged
        //     array (clocked rows from the document as it stands at save time).
        // wpauthor-5d3f8a26 — workPerformed is STRIPPED HERE, and the strip is the
        // load-bearing half of removing the field from this form.
        //
        // Deleting the textarea alone would have been a silent data-loss bug:
        // jobData is seeded from the job document, so `...jobDataForSave` carried
        // workPerformed along on EVERY save. Rescheduling a job, reassigning it or
        // editing a line item would have rewritten the mirror from whatever the
        // form loaded — clobbering entries other people added since the form
        // opened, under no author, with nothing to show it had happened.
        //
        // Work Performed now lives ONLY in the quick view's entry list, where
        // every write is attributed. Same strip pattern as the two server-owned
        // fields beside it.
        //
        // legnotes9-6b4e8d12 — SEAM 9 adds techNotes, for the same reason and as the
        // client half of the rules guard. The array is gone from every job today, so
        // this strips nothing now. It matters if a phone that has not yet taken the
        // Seam 5 OTA re-creates it: without the strip, this form would spread that
        // array straight back on the next save and become a second writer of it. The
        // rules guard deliberately still PERMITS removal, so the cleanup path stays open.
        const {
            timePunches: _serverOwnedPunches,
            jobCosting: _frozenJobCosting,
            workPerformed: _entriesOwnMirror,
            techNotes: _legacyNotesGone,
            ...jobDataForSave
        } = jobData;

        const dataToSave = {
            ...jobDataForSave,
            ...(canWriteLabor
                ? { jobCosting: {
                    ...(_frozenJobCosting || {}),
                    laborEntries: mergedLaborEntries,
                    // laborheal-3b7e9c25 — a deliberate "nobody on this job" save carries a marker
                    // beside the empty list, so the server can tell it from the old mobile app's
                    // wipe (which leaves the bare { laborEntries: [] }) and won't put the labour back.
                    ...(mergedLaborEntries.length === 0 ? { laborClearedAt: new Date() } : {}),
                } }
                : {}),
            recurrence: cleanRecurrence,
            // PROPERTIES v2 — stamp the chosen service location onto the job (null = main address).
            propertyId: (serviceLocation && serviceLocation !== 'main') ? serviceLocation : null,
            propertyName: (serviceLocation && serviceLocation !== 'main')
                ? ((customers.find(c => c.id === jobData.clientId)?.properties || []).find(p => p.id === serviceLocation)?.name || '')
                : null,
            startAt: finalStartAt,
            endAt:   finalEndAt,
            lineItems: processedLineItems, 
            assignedTo,
            assignedToNames,
            assignedResources: selectedResources, 
            updatedAt: serverTimestamp(),
            status: cancellationInfo ? 'Canceled' : jobData.status,
            reminderEnabled: jobData.eventType === 'Job' ? reminderEnabled : false,
            billLaborToCustomer: jobData.eventType === 'Job' ? billLaborToCustomer : null, // r5-b2b-joblabor-save-6d1a8f42 — null = inherit org default
            reminderDate: jobData.eventType === 'Job' && reminderEnabled && reminderDate
                ? reminderDate
                : null,
            reminderSent,
            // --- ROUTING / SCHEDULING FIELDS ---
            schedulingMode:          !isNonJobSave ? schedulingMode : null,
            arrivalWindow:           (!isNonJobSave && schedulingMode === 'window' && durationMode !== 'days') ? arrivalWindow : null,
            arrivalWindowSnapshot:   (!isNonJobSave && schedulingMode === 'window' && durationMode !== 'days') // aw-seam4
                                        ? buildArrivalWindowSnapshot(arrivalWindow, companySettings) : null,
            // r2-cs-save-9d4a2f18 — per-crew scheduling supersedes the all-day block.
            // When per-crew is ON, suppress isAllDay/durationDays (this job is NOT a
            // single all-day span) and write crewSchedule + a derived envelope below.
            isAllDay:                (!isNonJobSave && durationMode === 'days' && !perCrewScheduling) || false,
            durationDays:            (!isNonJobSave && durationMode === 'days' && !perCrewScheduling) ? jobDurationDays : null,
            assignmentMode:          !isNonJobSave ? assignmentMode : null,
            routeGroupId:            (!isNonJobSave && assignmentMode === 'group') ? selectedGroupId : null,
            routeGroupName:          (!isNonJobSave && assignmentMode === 'group') ? (selectedGroup?.name || null) : null,
            assignedGroupMemberIds:  groupMemberIds,
            assignedFieldWorkerIds:  assignedFieldWorkerIds,
            // Multi-crew: if line items have crew assignments, build crews array
            ...(lineItemCrews.length > 0 ? {
                crews: lineItemCrews.map(crewId => {
                    const group = routeGroups.find(g => g.id === crewId);
                    const existingCrew = jobToEdit?.crews?.find(c => c.crewGroupId === crewId);
                    return {
                        crewGroupId: crewId,
                        crewGroupName: group?.name || '',
                        completedAt: existingCrew?.completedAt || null,
                        completedBy: existingCrew?.completedBy || null,
                        managerOverride: existingCrew?.managerOverride || false,
                    };
                }),
                crewsTotal: lineItemCrews.length,
                crewsCompleted: jobToEdit?.crewsCompleted || 0,
            } : {}),
            // r2-cs-save-9d4a2f18 — continuous per-crew schedule + derived envelope.
            // Appearances carry JS Dates; the Firebase SDK auto-converts them to
            // Timestamps on write (same as startAt/endAt/reminderDate above). When
            // per-crew is active, the top-level startAt/endAt become the envelope
            // (MIN start .. MAX end) so the calendar and every date-reader get a
            // sensible span via getJobDateSpan.
            ...((!isNonJobSave && durationMode === 'days' && perCrewScheduling &&
                 Object.values(crewSchedule || {}).some(c => (c?.appearances || []).length > 0)) ? (() => {
                const cleaned = {};
                let minStart = null, maxEnd = null;
                Object.keys(crewSchedule).forEach(crewId => {
                    const apps = (crewSchedule[crewId]?.appearances || []).map(a => {
                        const s = a.startAt instanceof Date ? a.startAt : (a.startAt?.toDate ? a.startAt.toDate() : new Date(a.startAt));
                        const e = a.endAt instanceof Date ? a.endAt : (a.endAt?.toDate ? a.endAt.toDate() : new Date(a.endAt));
                        if (s && (!minStart || s < minStart)) minStart = s;
                        if ((e || s) && (!maxEnd || (e || s) > maxEnd)) maxEnd = (e || s);
                        return {
                            id: a.id,
                            date: a.date,
                            startAt: s,
                            endAt: e,
                            schedulingMode: a.schedulingMode || 'window',
                            arrivalWindow: a.arrivalWindow ?? null,
                        };
                    });
                    cleaned[crewId] = { appearances: apps };
                });
                return {
                    crewSchedule: cleaned,
                    ...(minStart ? { startAt: minStart } : {}),
                    ...(maxEnd ? { endAt: maxEnd } : {}),
                };
            })() : {}),
            // ── Address verification fields (Phase 2b) ─────────────────────
            // Persist verification result. We only WRITE these fields when
            // the user actively triggered geocoding in this session (or
            // inherited it from a verified customer). If status is 'idle'
            // (legacy/untouched), preserve the existing doc fields.
            ...(addressGeocode.status === 'ok' || addressGeocode.status === 'inherited' ? {
                addressVerified:   true,
                addressVerifiedAt: serverTimestamp(),
                addressCanonical:  addressGeocode.canonical,
                addressCoords:     addressGeocode.coords,
                addressPlaceId:    addressGeocode.placeId,
            } : {}),
            ...(addressGeocode.status === 'not_found' || addressGeocode.status === 'ambiguous' ? {
                addressVerified:   false,
                addressVerifiedAt: serverTimestamp(),
                addressCanonical:  null,
                addressCoords:     null,
                addressPlaceId:    null,
            } : {}),
        };
        
        if (cancellationInfo) {
            dataToSave.cancellationReason = cancellationInfo.reason;
            dataToSave.cancellationDetails = cancellationInfo.details;
        }

        // recprop-surgical-diff-4b8e2f10 — Seam 1: make the series-wide broadcast
        // ("this and future" / "all") write only the fields the operator actually
        // changed, diffed against the object the form was hydrated from (`jobToEdit`),
        // then merged onto the template. `merge:true` leaves every untouched template
        // field at its own value instead of overwriting the whole template with the
        // form. TODAY the form is hydrated from the template, so the baseline IS the
        // template and this is behavior-identical to the old `{ ...dataToSave }` write
        // (untouched fields were no-op self-writes). It becomes load-bearing only once
        // Seam 2 hydrates the form from the instance.
        //
        // Comparator: normalize both sides (Firestore Timestamps + JS Dates -> ms;
        // arrays/objects -> stable canonical serialization) so a re-serialized-but-equal
        // field never registers a false change. Biased safe — an uncomparable field is
        // treated as UNCHANGED (not broadcast): a missed edit is visible and re-saveable,
        // a stale value silently propagated is the corruption this guards against.
        const normalizeForDiff = (val) => {
            if (val === null || val === undefined) return null;
            if (typeof val?.toDate === 'function') return val.toDate().getTime(); // Firestore Timestamp
            if (val instanceof Date) return val.getTime();                        // JS Date
            if (Array.isArray(val)) return val.map(normalizeForDiff);             // order-sensitive
            if (typeof val === 'object') {
                const out = {};
                for (const k of Object.keys(val).sort()) out[k] = normalizeForDiff(val[k]);
                return out;
            }
            return val;
        };
        const fieldChanged = (a, b) => {
            try { return JSON.stringify(normalizeForDiff(a)) !== JSON.stringify(normalizeForDiff(b)); }
            catch { return false; } // uncomparable -> treat as unchanged (safe bias)
        };
        // Control fields are written explicitly by each branch (startAt/endAt/recurrence/
        // updatedAt) or are transient form overlays that must never reach the template
        // (_instance*); everything else is content diffed against the baseline. `undefined`
        // values are skipped so Firestore never rejects the write (matches the old
        // undefined-filter on the "all" path).
        const SURGICAL_CONTROL_FIELDS = new Set([
            '_instanceId', '_instanceStartAt', '_instanceEndAt', '_templateId',
            'startAt', 'endAt', 'recurrence', 'updatedAt',
        ]);
        const buildSurgicalTemplateContent = (payload, baseline) => {
            const out = {};
            for (const key of Object.keys(payload)) {
                if (SURGICAL_CONTROL_FIELDS.has(key)) continue;
                if (payload[key] === undefined) continue;
                // jobedit-nostomp-7c3f1a58 — a recurring TEMPLATE never carries clocked
                // time: clocked rows belong to the single occurrence they were worked on,
                // and the server owns template rows via rebuildRecurringLaborEntries.
                // Diff and write the manual rows only, so a punch on one occurrence can
                // never propagate across the series. This helper is used by the two
                // template writes only, which is exactly where that rule applies.
                if (key === 'jobCosting') {
                    const manualOnly = (payload.jobCosting?.laborEntries || []).filter(r => r?.source !== 'clocked');
                    const baseManual = (baseline?.jobCosting?.laborEntries || []).filter(r => r?.source !== 'clocked');
                    if (fieldChanged(manualOnly, baseManual)) {
                        out.jobCosting = { ...payload.jobCosting, laborEntries: manualOnly };
                    }
                    continue;
                }
                // lineidweb-9e4a2d17 — line IDs added by this save are not an operator edit: compare the
                // lines without them, so they never make lines go out that would not have gone before.
                if (key === 'lineItems') {
                    if (fieldChanged(lineIds.stripLineIds(payload.lineItems), lineIds.stripLineIds(baseline?.lineItems))) out.lineItems = payload.lineItems;
                    continue;
                }
                if (fieldChanged(payload[key], baseline?.[key])) out[key] = payload[key];
            }
            return out;
        };

        // lineidweb-9e4a2d17 — the series template's lines as stored NOW (read once, only when a
        // recurring branch below needs them). A visit's lines must be copies of the template's
        // (own lineId + sourceLineId) and the template must keep its own IDs, whichever of the two the
        // form was filled from. null if the read fails: the lines then go out as ensured above.
        let templateLinesCache;
        const readTemplateLines = async () => {
            if (templateLinesCache !== undefined) return templateLinesCache;
            templateLinesCache = null;
            try {
                if (recurringTemplateId) {
                    const tSnap = await getDoc(doc(db, `organizations/${orgId}/recurringJobs`, recurringTemplateId));
                    templateLinesCache = tSnap.exists() ? (tSnap.data().lineItems || []) : null;
                }
            } catch (e) {
                console.error('lineidweb-9e4a2d17: could not read the series template lines', e);
            }
            return templateLinesCache;
        };
        // lineidweb-9e4a2d17 — a template write keeps the template's own line IDs: lines going out
        // (an operator edit) are put back onto the template's IDs; and when no lines are going out but
        // the template's stored lines still lack IDs and the form was filled from the template itself
        // (its lines, without IDs, equal what is stored), the form's lines go out to give them IDs —
        // adding nothing but IDs. Without the stored lines (read failed) nothing changes.
        const applyTemplateLineIds = (write, formLines, storedLines) => {
            if (!storedLines) return;
            if (Array.isArray(write.lineItems)) {
                write.lineItems = lineIds.linesOntoSource(write.lineItems, storedLines, lineIds.newLineId);
            } else if (Array.isArray(formLines)
                && lineIds.trustedLineIds(storedLines).some(id => !id)
                && !fieldChanged(lineIds.stripLineIds(jobToEdit?.lineItems || []), lineIds.stripLineIds(storedLines))) {
                write.lineItems = lineIds.linesOntoSource(formLines, storedLines, lineIds.newLineId);
            }
        };

        try {
            if (jobToEdit?.id && editChoice === 'only_this') {
                // ── Edit this instance only ──────────────────────────────────
                // _instanceId is set by openJobEditorModal when a recurring instance is opened
                // (the template is loaded for editing but we preserve the original instance ID)
                const instanceId = jobToEdit._instanceId || (isEditingInstance ? jobToEdit.id : null);
                const templateId = recurringTemplateId; // r2fix-rec-templateid-carry-5d1c9a72 — real series template, not jobToEdit.id
                // r3-rec-strip-body-id-1d3f7a92 — also drop `id` so the form's held id (a template
                // id on projected days) is never persisted into the job body; the Firestore doc id
                // must stay authoritative on read (see CrmApp jobs listener r3-rec-jobs-docwins-*).
                const { recurrence, id, _instanceId, _instanceStartAt, _instanceEndAt, _templateId, ...instanceData } = dataToSave;
                if (Array.isArray(instanceData.lineItems)) { // lineidweb-9e4a2d17 — this visit's lines are copies of the template's
                    const tl = await readTemplateLines();
                    if (tl) instanceData.lineItems = lineIds.linesOntoCopy(instanceData.lineItems, tl, lineIds.newLineId);
                }

                if (instanceId) {
                    // Update the existing individual job doc
                    const cleanInstance = Object.fromEntries(
                        Object.entries({ ...instanceData, recurrence: { frequency: 'Never' }, recurringJobId: templateId, recurringException: true })
                        .filter(([, v]) => v !== undefined)
                    );
                    await setDoc(doc(db, `organizations/${orgId}/jobs`, instanceId), cleanInstance, { merge: true });
                } else {
                    // No instance doc yet — create one as an exception for this date
                    await addDoc(collection(db, `organizations/${orgId}/jobs`), asUnworkedCreate({ // h1job-9c4e7b18
                        ...instanceData,
                        recurrence: { frequency: 'Never' },
                        recurringJobId: recurringTemplateId, // r2fix-rec-templateid-carry-5d1c9a72
                        recurringException: true,
                        createdAt: serverTimestamp(),
                        creatorId: currentUserProfile.id,
                    }));
                }

                // r1-rec-modal-exception-e8c3a7d5
                // Mark the ORIGINAL occurrence date as an exception on the template,
                // so the projection stops generating a ghost for that date. Without
                // this, "edit this only" materialized the job but left the projected
                // occurrence un-suppressed -> a duplicate row for that day (one real,
                // one projected). Mirrors the calendar path (handleCreateRecurringException).
                // Use the occurrence's ORIGINAL start (jobToEdit._instanceStartAt),
                // since the projection is keyed to the original slot; fall back to the
                // saved startAt if the overlay is absent. arrayUnion is idempotent, so
                // re-editing an already-excepted date is harmless.
                try {
                    const exceptionSource =
                        jobToEdit._instanceStartAt ||
                        jobToEdit.startAt ||
                        dataToSave.startAt;
                    const exceptionDate = exceptionSource?.toDate
                        ? exceptionSource.toDate()
                        : (exceptionSource instanceof Date ? exceptionSource : null);
                    if (exceptionDate) {
                        const exceptionDateStr = format(exceptionDate, 'yyyy-MM-dd');
                        const exceptionTemplateId = recurringTemplateId; // r2fix-rec-templateid-carry-5d1c9a72 — exception must land on the real template
                        if (exceptionTemplateId) {
                            await updateDoc(
                                doc(db, `organizations/${orgId}/recurringJobs`, exceptionTemplateId),
                                { exceptions: arrayUnion(exceptionDateStr) }
                            );
                        }
                    }
                } catch (excErr) {
                    console.error('Failed to write recurring exception for edited occurrence:', excErr);
                }
            } else if (jobToEdit?.id && editChoice === 'this_and_future') {
                // ── Edit this and all future instances ───────────────────────
                // Update the individual job doc
                if (isEditingInstance) {
                    // r3-rec-strip-body-id-2e4c8b03 — drop `id` here too so the this_and_future
                    // instance write never persists the form's held id into the job body.
                    const { recurrence, id, _instanceId, _instanceStartAt, _instanceEndAt, _templateId, ...instanceData } = dataToSave;
                    if (Array.isArray(instanceData.lineItems)) { // lineidweb-9e4a2d17 — this visit's lines are copies of the template's
                        const tl = await readTemplateLines();
                        if (tl) instanceData.lineItems = lineIds.linesOntoCopy(instanceData.lineItems, tl, lineIds.newLineId);
                    }
                    await setDoc(doc(db, `organizations/${orgId}/jobs`, jobToEdit._instanceId || jobToEdit.id), {
                        ...instanceData,
                        recurrence: { frequency: 'Never' },
                        recurringJobId: recurringTemplateId, // r2fix-rec-templateid-carry-5d1c9a72
                        recurringException: true,
                    }, { merge: true });
                }
                // Update the recurring template's startAt to this date so future
                // generated instances start from here
                const templateId = recurringTemplateId; // r2fix-rec-templateid-carry-5d1c9a72 — real series template, not jobToEdit.id
                const templateRef = doc(db, `organizations/${orgId}/recurringJobs`, templateId);
                // recprop-marker-tf-9d4a2c81 — Seam 2: stamp the propagation marker so
                // propagateRecurringJobEdit fans this template edit onto already-created
                // upcoming occurrences. Boundary = the OPENED occurrence's date
                // (_instanceStartAt, always populated for a recurring occurrence via
                // openJobEditorModal), so only that occurrence and later ones are touched.
                // Formatted with the same date-fns 'yyyy-MM-dd' the server boundary compares.
                const _propBoundarySrc = jobToEdit._instanceStartAt || jobToEdit.startAt || dataToSave.startAt;
                const _propBoundaryDate = _propBoundarySrc?.toDate
                    ? _propBoundarySrc.toDate()
                    : (_propBoundarySrc instanceof Date ? _propBoundarySrc : null);
                const templateWrite = {
                    // recprop-surgical-diff-4b8e2f10 — only the operator's real edits
                    // (vs the hydration baseline); merge:true keeps every untouched
                    // template field as its own. Behavior-identical to the old
                    // `{ ...dataToSave }` write while the form is template-hydrated.
                    ...buildSurgicalTemplateContent(dataToSave, jobToEdit),
                    startAt: dataToSave.startAt,       // preserved exactly as today (Symptom-B machinery)
                    endAt: dataToSave.endAt,           // preserved exactly as today
                    recurrence: recurrenceForSeriesWrite(cleanRecurrence, deleteField), // rule re-asserted; allkeep-6c2d9e14 — the whole rule, no stale Custom fields
                    updatedAt: serverTimestamp(),
                };
                applyTemplateLineIds(templateWrite, dataToSave.lineItems, await readTemplateLines()); // lineidweb-9e4a2d17
                if (_propBoundaryDate) {
                    templateWrite._propagate = {
                        scope: 'this_and_future',
                        fromDate: format(_propBoundaryDate, 'yyyy-MM-dd'),
                        requestId: `${templateId}_tf_${Date.now()}`,
                    };
                }
                await setDoc(templateRef, templateWrite, { merge: true });
            } else if (jobToEdit?.id) {
                // ── Edit all instances (default) or new save ─────────────────
                const { _instanceId, _instanceStartAt, _instanceEndAt, _templateId, ...rawClean } = dataToSave;
                // recprop-marker-all-2f7b6e30 — Seam 2: on an "All events" recurring-template
                // save, stamp the propagation marker. Boundary = start of today so "All" never
                // reaches into the past (past occurrences stay protected, consistent with the
                // server skip rules). Guarded on collectionName so a plain one-off job edit
                // (which also lands in this branch) is never marked.
                let cleanDataToSave;
                if (editChoice === 'all' && collectionName === 'recurringJobs') {
                    // recprop-surgical-diff-4b8e2f10 — Seam 1: recurring "All events" broadcast.
                    // Surgical merge (same rationale/helper as the this_and_future branch above):
                    // write only the operator's real edits vs the hydration baseline, plus the
                    // control fields the old full write also wrote. merge:true keeps every
                    // untouched template field. Behavior-identical while template-hydrated.
                    // Scoped to recurringJobs ONLY — a plain one-off job edit keeps its full write.
                    // allkeep-6c2d9e14 — the form shows the OPENED visit's date and time. Writing
                    // them as the series' start re-anchored the series at that visit (since
                    // 2026-07-01, 3492c9a) and every earlier visit vanished. "All events" keeps the
                    // series' own start: only a change the operator made to the visit's times moves
                    // the series' times, by the same amount (src/utils/seriesAllEvents.js). The
                    // series' own start is read fresh — a form filled from a created visit doesn't
                    // carry it. If it can't be read, the series' start and end are left as they are.
                    // The "opened" times are whatever the form was opened with, on EVERY path: the
                    // occurrence overlay (_instanceStartAt — Jobs list, a created visit), or the
                    // start a surface put on the series itself (the Calendar passes
                    // { ...series, startAt: <that visit> } with no overlay), or the series' own
                    // start when it was opened directly. 2026-10-18 retest: the first version used
                    // the overlay only, so a visit opened from the Calendar still re-anchored.
                    let seriesNow = null;
                    try {
                        const tSnap = await getDoc(doc(db, `organizations/${orgId}/recurringJobs`, recurringTemplateId));
                        seriesNow = tSnap.exists() ? tSnap.data() : null;
                    } catch (e) {
                        console.error('allkeep-6c2d9e14: could not read the series before an All events save', e);
                    }
                    const seriesTimes = seriesTimesForAllEvents({
                        seriesStart: seriesNow?.startAt, seriesEnd: seriesNow?.endAt,
                        openedStart: jobToEdit._instanceStartAt || jobToEdit.startAt,
                        openedEnd: jobToEdit._instanceEndAt || jobToEdit.endAt,
                        formStart: dataToSave.startAt, formEnd: dataToSave.endAt,
                    }) || {};
                    cleanDataToSave = {
                        ...buildSurgicalTemplateContent(rawClean, jobToEdit),
                        ...(seriesTimes.startAt ? { startAt: seriesTimes.startAt } : {}),
                        ...(seriesTimes.endAt ? { endAt: seriesTimes.endAt } : {}),
                        recurrence: recurrenceForSeriesWrite(cleanRecurrence, deleteField), // allkeep-6c2d9e14 — the whole rule, no stale Custom fields
                        updatedAt: serverTimestamp(),
                        _propagate: {
                            scope: 'all',
                            fromDate: format(new Date(), 'yyyy-MM-dd'),
                            requestId: `${recurringTemplateId}_all_${Date.now()}`, // r2fix-rec-templateid-carry-5d1c9a72
                        },
                    };
                    applyTemplateLineIds(cleanDataToSave, rawClean.lineItems, seriesNow ? (seriesNow.lineItems || []) : null); // lineidweb-9e4a2d17 — seriesNow = the series as stored now
                } else {
                    // Plain one-off / non-recurring edit — unchanged full write.
                    cleanDataToSave = Object.fromEntries(Object.entries(rawClean).filter(([, v]) => v !== undefined));
                }
                // r2fix-rec-templateid-carry-5d1c9a72 — a recurring "All events" save targets
                // the real series template (jobToEdit.id may be a materialized instance's own
                // id, see hydration note above); a plain one-off edit keeps its own doc id.
                const allSaveDocId = collectionName === 'recurringJobs' ? recurringTemplateId : jobToEdit.id;
                await setDoc(doc(db, `organizations/${orgId}/${collectionName}`, allSaveDocId), cleanDataToSave, { merge: true });
            } else {
                const cleanNew = asUnworkedCreate(Object.fromEntries( // h1job-9c4e7b18
                    Object.entries({ ...dataToSave, createdAt: serverTimestamp(), creatorId: currentUserProfile.id })
                    .filter(([, v]) => v !== undefined)
                ));
                if (isRecurring && !saveAsInstance) {
                    // New recurring template: await the write so the server can see it,
                    // then materialize from the START date (mobile parity). Without this,
                    // web never materializes a series — the start occurrence is only ever a
                    // projection, and the cron/planner (which advance to their window before
                    // generating) can skip it, so no real job ever lands on the start date.
                    await addDoc(collection(db, jobsPath), cleanNew);
                    try {
                        const generateJobs = httpsCallable(functions, 'generateJobsForDateRange');
                        await generateJobs({
                            orgId,
                            startDate: toOrgDateInput(finalStartAt), // orgtime-7c2e4b19 — company days
                            endDate: toOrgDateInput(cleanRecurrence.endDate),
                        });
                    } catch (err) {
                        console.warn('generateJobsForDateRange non-blocking fail:', err);
                    }
                } else if (showNewJobChecklists && newJobChecklists.length) {
                    // chklstnew-5a8c2e71 — checklists were chosen: the server attaches them, so the job
                    // must be ON the server first. Await this one write for its id, then attach each
                    // pick. A failed attach never undoes the job (one message after the save).
                    const newRef = await addDoc(collection(db, jobsPath), cleanNew);
                    setAttachingChecklists(true);
                    checklistsNotAdded = await attachChecklistsToNewJob(newRef.id, newJobChecklists);
                } else {
                    // Fire and forget for non-recurring — Firestore commits locally immediately.
                    // Don't await server ACK.
                    addDoc(collection(db, jobsPath), cleanNew)
                        .catch(err => console.error('Server failed to confirm new job save:', err));
                }
            }

            // wpauthor-5d3f8a26 — the wppromote-6a3f1e94 marker write that stood
            // here is GONE with this form's Promote button. Nothing here can
            // promote a note any more, so there is nothing to tag after the save.
            // The quick view writes the marker on its own entry save.

            if (cancellationInfo) {
                const notesCollectionPath = `organizations/${orgId}/customers/${jobData.clientId}/notes`;
                await addDoc(collection(db, notesCollectionPath), {
                    contactDate: serverTimestamp(),
                    contactType: 'Event Canceled',
                    noteText: `Reason: ${cancellationInfo.reason}${cancellationInfo.details ? ` - ${cancellationInfo.details}` : ''}\nEvent: ${jobData.title}`,
                    status: 'Canceled',
                });
            }

            if (completionNote !== null) {
                const notesCollectionPath = `organizations/${orgId}/customers/${jobData.clientId}/notes`;
                const completedByName = currentUserProfile?.name || 'Unknown';
                await addDoc(collection(db, notesCollectionPath), {
                    contactDate: serverTimestamp(),
                    contactType: jobData.eventType,
                    noteText: `Completed: ${jobData.title}\nCompleted by: ${completedByName}\nNote: ${completionNote || 'No additional notes.'}`,
                    status: 'Contacted',
                    completedBy: currentUserProfile?.id,
                    completedByName,
                });
            }

            if (cancellationInfo) {
                const wantToRebook = window.confirm("Job has been marked as Canceled and logged to the timeline. Would you like to re-book this event for a different day?");
                
                if (wantToRebook) {
                    const { id, status, createdAt, updatedAt, cancellationReason, cancellationDetails, startAt, endAt, ...rebookData } = dataToSave;
                    
                    setJobData({
                        ...getInitialState(),
                        ...rebookData,
                        title: `RE-BOOK: ${rebookData.title}`,
                        status: 'Scheduled',
                        startAt: new Date(),
                        endAt: new Date(new Date().setHours(new Date().getHours() + 1))
                    });

                    setReminderEnabled(true);
                    setReminderDateOverridden(false);
                    const orgSettings = companySettings?.jobReminderSettings;
                    setReminderDate(calculateReminderDate(new Date(), orgSettings, orgTz));
                    
                    setIsSaving(false);
                    return; 
                }
            }

            // --- Fire booking confirmation email for new Jobs only ---
            // Silently fires — never blocks saving if notification fails
            if (!jobToEdit?.id && jobData.eventType === 'Job' && jobData.clientId && !cancellationInfo) {
                try {
                    const sendJobNotification = httpsCallable(functions, 'sendJobNotification');
                    sendJobNotification({
                        type: 'booking_confirmation',
                        clientId: jobData.clientId,
                        orgId,
                        jobTitle: jobData.title,
                        schedulingMode,
                        arrivalWindow: schedulingMode === 'window' ? arrivalWindow : null,
                        startAt: finalStartAt.toISOString(),
                        isRecurring: isRecurring || false,
                    });
                } catch (notifErr) {
                    console.warn('Booking confirmation notification failed (non-blocking):', notifErr);
                }
            }

            onSave();
            // chklstnew-5a8c2e71 — the job IS saved; say plainly which picked checklists didn't attach.
            if (checklistsNotAdded.length) alert(newJobChecklistFailureMessage(checklistsNotAdded));

        } catch (error) {
            console.error("Error saving job:", error);
            // chklstweb-3a7f1c58 — a move to Completed refused by the checklist gate reads in plain words.
            const checklistMsg = (jobData.status === 'Completed' && jobToEdit?.status !== 'Completed')
                ? await explainChecklistRefusal({ orgId: currentUserProfile?.organizationId, jobId: jobToEdit?.id, error })
                : null;
            alert(checklistMsg || ("An error occurred: " + error.message));
        } finally {
            setIsSaving(false);
            setAttachingChecklists(false); // chklstnew-5a8c2e71
            setIsCompletionNoteModalOpen(false);
        }
    };
    
    const handleDeleteClick = () => {
        if (isReadOnly) return;
        const isRecurring = jobData.recurrence?.frequency !== 'Never';
        if (!isRecurring) {
            setIsCancellationModalOpen(true);
        } else {
            setDeleteOptions({ isOpen: true, isRecurring: true });
        }
    };

    // CONVERT-FIX VERIFIER: r1-cvt-jfm-deletejob-7e2b9a14
    // Hard-delete for non-recurring jobs (parity with mobile, which can
    // delete a standalone job). Distinct from Cancel Event (handleDeleteClick
    // → cancellation modal → status:'Canceled', doc survives). This opens the
    // delete-confirm modal in non-recurring mode; "Yes, Delete" calls
    // executeDelete('this') → deleteEvent CF → eventRef.delete() → fires
    // onJobDeleted → if this job was converted from a quote, that quote
    // reverts to convertible. The isQuoteConverted flag drives the
    // quote-linkage confirmation copy below. isRecurring:false is REQUIRED so
    // deleteEvent takes the hard-delete branch (not the soft instance-cancel).
    const handleDeleteJobClick = () => {
        if (isReadOnly) return;
        setDeleteOptions({ isOpen: true, isRecurring: false, isQuoteConverted: !!jobData.quoteId });
    };

    const executeDelete = async (scope) => {
        if (isReadOnly) return;
        if (!jobToEdit?.id) return;
        setIsDeleting(true);
        try {
            const deleteEventFn = httpsCallable(functions, 'deleteEvent');
            // For recurring scopes other than 'this', pass the recurringJobId
            // so the CF can look up the template in the recurringJobs collection.
            // Also pass the specific event's startAt for 'future' scope.
            const isRecurringScope = deleteOptions.isRecurring && scope !== 'this';
            // Use recurringJobId if available (generated instances), otherwise fall back
            // to jobToEdit.id (covers template documents and legacy jobs).
            const resolvedEventId = isRecurringScope
                ? (jobToEdit.recurringJobId || jobToEdit.id)
                : jobToEdit.id;
            const eventDate = jobToEdit.startAt
                ? (jobToEdit.startAt.toDate ? jobToEdit.startAt.toDate().toISOString() : new Date(jobToEdit.startAt).toISOString())
                : null;
            await deleteEventFn({
                eventId: resolvedEventId,
                scope: scope,
                isRecurring: deleteOptions.isRecurring,
                eventDate,
            });
            onSave(); 
        } catch (error) {
            console.error("Error deleting event:", error);
            alert(`Failed to delete event: ${error.message}`);
        } finally {
            setIsDeleting(false);
            setDeleteOptions({ isOpen: false, isRecurring: false });
        }
    };
    
    if (!isOpen) return null;

    return (
        <>
            <CancellationReasonModal 
                isOpen={isCancellationModalOpen}
                onClose={() => setIsCancellationModalOpen(false)}
                onSave={(cancellationInfo) => {
                    executeSave({ cancellationInfo });
                    setIsCancellationModalOpen(false);
                }}
                reasons={cancellationReasons}
            />
            <AddCompletionNoteModal
                isOpen={isCompletionNoteModalOpen}
                onClose={() => setIsCompletionNoteModalOpen(false)}
                onSave={(noteText) => {
                    if (completionNoteCallback) completionNoteCallback(noteText);
                }}
            />
            
            {deleteOptions.isOpen && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
                        <h3 className="text-lg font-bold mb-4">{deleteOptions.isRecurring ? 'Delete Event' : 'Delete Job'}</h3>
                        {deleteOptions.isRecurring ? (
                            <>
                                <p className="mb-6">This is a recurring event. How would you like to delete it?</p>
                                <div className="flex flex-col space-y-2">
                                    <button onClick={() => executeDelete('this')} className="w-full text-left p-2 rounded hover:bg-gray-100">This event only</button>
                                    <button onClick={() => executeDelete('future')} className="w-full text-left p-2 rounded hover:bg-gray-100">This and future events</button>
                                    <button onClick={() => executeDelete('all')} className="w-full text-left p-2 rounded hover:bg-gray-100">All events in this series</button>
                                </div>
                            </>
                        ) : (
                            /* CONVERT-FIX VERIFIER: r1-cvt-jfm-deletecopy-3f8c1b29 */
                            deleteOptions.isQuoteConverted ? (
                                <p className="mb-6">Permanently delete this job? It was created from a quote — deleting it will let you convert that quote into a job again. This can&#39;t be undone.</p>
                            ) : (
                                <p className="mb-6">Permanently delete this job? This can&#39;t be undone.</p>
                            )
                        )}
                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setDeleteOptions({ isOpen: false, isRecurring: false })} className="px-4 py-2 bg-gray-200 rounded-md">Cancel</button>
                            {!deleteOptions.isRecurring && (
                                <button onClick={() => executeDelete('this')} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400">
                                    {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* D1 VERIFIER: r1-d1-shell-fullpage-2d9f6a15
                Modal shell → full-page shell. Removed: fixed-overlay positioning,
                dark backdrop, centering, z-[60] stacking, the md:max-w-4xl column
                cap, md:max-h-[90vh], rounded corners, and drop shadow (all the
                "floating card" affordances). The editor now fills the routed view
                area. Internal layout below this wrapper is unchanged — D1-structural
                only. Visual layout refinement (header, Save placement, use of the
                new horizontal room) is the separate D1-visual follow-up. */}
            <div className="w-full h-full bg-white flex flex-col overflow-hidden">
                <div className="bg-white w-full h-full flex flex-col overflow-hidden">
                    <div className="flex justify-between items-center p-6 pb-4">
                        <div className="flex items-center gap-2">
                            {/* jobnumdisp-6a1e9c34 — "Edit Job #1860". Read off jobToEdit (the
                                saved doc), not jobData (the in-progress form state), because the
                                number is server-assigned and is never an editable field. The
                                CREATE branch is deliberately untouched: no document exists yet,
                                so there is no number to show. */}
                            <h2 className="text-xl font-bold">
                                {jobToEdit
                                    ? `Edit ${jobData.eventType}${formatJobNumber(jobToEdit.jobNumber) ? ` ${formatJobNumber(jobToEdit.jobNumber)}` : ''}`
                                    : `Create New ${jobData.eventType}`}
                            </h2>
                            {isReadOnly && (
                                <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full border">
                                    <Lock size={12}/> View Only
                                </span>
                            )}
                        </div>
                        <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="overflow-y-auto px-6 pb-6 flex-grow space-y-4">
                        
                        {/* noteleak-3a9f2c71 — the provenance banner. The first line is
                            unchanged and renders from fields on the follow-up itself, so it
                            is always available. The note body underneath is resolved from the
                            parent job (quickview path only) and can legitimately be absent —
                            `description` is empty now, so the banner must say so out loud
                            rather than silently showing nothing. */}
                        {jobToEdit?.createdByName && jobToEdit?.linkedJobId && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                                <div className="flex items-start gap-2">
                                    <span className="text-amber-500 mt-0.5">🚩</span>
                                    <span>Follow-up created by <strong>{jobToEdit.createdByName}</strong> from a field note. {jobToEdit.flagType && <span>Type: <strong>{{callback:'Callback Needed',followup:'Follow-up Job',issue:'Issue to Resolve',fyi:'General FYI',price_review:'Pricing Review'}[jobToEdit.flagType] || jobToEdit.flagType}</strong></span>}</span>
                                </div>

                                {/* legnotes9-6b4e8d12 — SEAM 9. The loading / found / missing
                                    branches are gone with the resolver that fed them. The
                                    'missing' one was the live case on every quickview-path
                                    follow-up and it said something untrue. The line above is
                                    accurate on its own; a server-path follow-up still shows
                                    its real note in the Notes & Media panel below. */}
                            </div>
                        )}

                        {/* Customer / Event Type */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Customer</label>
                                {/* custpick-8e5a2c71 — was a native <select> over the whole
                                    customer list, which only browser-type-aheads: typing JUMPED
                                    around instead of narrowing, and on Alberni it mounted ~3,275
                                    option rows. Same `availableCustomers` memo passed straight in
                                    (role filter + ensureCurrentCustomer unchanged), and
                                    handleCustomerChange is used verbatim — the combobox emits the
                                    same { target: { name, value } } a select does, so address
                                    autofill, the sticky note, bill-to reset, service-location
                                    arming and geocode inheritance all still run untouched. */}
                                <CustomerCombobox
                                    name="clientId"
                                    customers={availableCustomers}
                                    value={jobData.clientId}
                                    onChange={handleCustomerChange}
                                    disabled={isReadOnly}
                                />
                                {(() => {
                                    const selCust = customers.find(c => c.id === jobData.clientId);
                                    const tagIds = selCust?.tags || [];
                                    if (tagIds.length === 0) return null;
                                    return (
                                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            {tagIds.map(tId => {
                                                const t = orgTags.find(x => x.id === tId);
                                                if (!t) return null;
                                                // Guard non-hex colors before concatenating alpha suffixes.
                                                const c = /^#[0-9a-fA-F]{6}$/.test(t.color || '') ? t.color : '#4B5563';
                                                return (
                                                    <span key={tId} className="px-2 py-0.5 rounded-full text-xs font-semibold border" style={{ backgroundColor: c + '11', borderColor: c + '66', color: c }}>
                                                        {t.label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Event Type</label>
                                <select 
                                    name="eventType" 
                                    value={jobData.eventType} 
                                    onChange={handleChange} 
                                    disabled={isReadOnly} 
                                    className="mt-1 w-full p-2 border rounded-md bg-white disabled:bg-gray-100 disabled:text-gray-500"
                                >
                                    {eventTypes.map(type => <option key={type} value={type}>{type}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* billto-jfm-ui-6e5a1c83 -- Bill To selector (stored only in Phase 1). */}
                        {jobData.clientId && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Bill to</label>
                                <select
                                    value={pendingThirdParty ? 'thirdParty' : (jobData.billTo?.mode || 'main')}
                                    onChange={handleBillToChoiceChange}
                                    disabled={isReadOnly}
                                    className="mt-1 w-full p-2 border rounded-md bg-white disabled:bg-gray-100 disabled:text-gray-500"
                                >
                                    <option value="main">Main customer</option>
                                    {(serviceLocation && serviceLocation !== 'main') ? <option value="propertyContact">Property contact</option> : null}
                                    <option value="thirdParty">Another customer...</option>
                                </select>
                                {/* custpick-8e5a2c71 — the second full-list select in this modal,
                                    same treatment. handleBillToThirdPartyChange reads only
                                    e.target.value, so it is used verbatim. */}
                                {(pendingThirdParty || jobData.billTo?.mode === 'thirdParty') && !isReadOnly && (
                                    <CustomerCombobox
                                        customers={customers.filter(c => c.id !== jobData.clientId)}
                                        value={jobData.billTo?.customerId || ''}
                                        onChange={handleBillToThirdPartyChange}
                                        emptyLabel="-- Choose the customer to bill --"
                                        invalid={!jobData.billTo}
                                    />
                                )}
                            </div>
                        )}

                        {/* PROPERTIES v2 — Service location selector (only when the customer has properties) */}
                        {(() => {
                            const selCust = customers.find(c => c.id === jobData.clientId);
                            const props = selCust?.properties || [];
                            if (props.length === 0) return null;
                            return (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Service location <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={serviceLocation}
                                        onChange={handleServiceLocationChange}
                                        disabled={isReadOnly}
                                        className={`mt-1 w-full p-2 border rounded-md bg-white disabled:bg-gray-100 disabled:text-gray-500 ${serviceLocation === '' ? 'border-red-400' : ''}`}
                                    >
                                        <option value="">-- Choose a service location --</option>
                                        <option value="main">Main address{selCust ? ` — ${buildAddressString(selCust)}` : ''}</option>
                                        {props.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {(p.name || p.address1 || 'Unnamed property')} — {p.addressCanonical || buildAddressString(p)}
                                            </option>
                                        ))}
                                    </select>
                                    {serviceLocation === '' && (
                                        <p className="mt-1 text-xs text-red-500">This customer has multiple locations — choose where the work is happening.</p>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Template Picker — only shown when creating a new job */}
                        {!jobToEdit?.id && canFillJobForm && jobTemplates.length > 0 && (
                            <div className="mb-4">
                                {!showTemplatePicker ? (
                                    <button
                                        type="button"
                                        onClick={() => setShowTemplatePicker(true)}
                                        className="w-full flex items-center gap-3 px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-white transition-all shadow-sm"
                                    >
                                        <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FileText size={16} />
                                        </div>
                                        <div className="text-left flex-1">
                                            <p className="text-sm font-semibold">Start from a template</p>
                                            <p className="text-xs text-blue-200">{jobTemplates.length} template{jobTemplates.length !== 1 ? 's' : ''} available — fills title, line items &amp; duration</p>
                                        </div>
                                        <svg className="w-4 h-4 text-blue-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                ) : (
                                    <div className="border border-blue-200 rounded-xl overflow-hidden bg-blue-50">
                                        <div className="flex items-center justify-between px-3 py-2 border-b border-blue-200">
                                            <span className="text-sm font-semibold text-blue-800">Choose a template</span>
                                            <button type="button" onClick={() => setShowTemplatePicker(false)} className="text-blue-400 hover:text-blue-600"><X size={16} /></button>
                                        </div>
                                        <div className="max-h-52 overflow-y-auto divide-y divide-blue-100">
                                            {jobTemplates.map(t => (
                                                <button
                                                    key={t.id}
                                                    type="button"
                                                    onClick={() => applyTemplate(t)}
                                                    className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-blue-100 transition-colors"
                                                >
                                                    <FileText size={15} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-800 truncate">{t.name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {t.estimatedDuration ? `${t.estimatedDuration} min` : ''}{t.lineItems?.length ? ` · ${t.lineItems.length} line item${t.lineItems.length > 1 ? 's' : ''}` : ''}
                                                        </p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Title / Status */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Job Title</label>
                                <input 
                                    name="title" 
                                    value={jobData.title} 
                                    onChange={handleChange} 
                                    disabled={isReadOnly} 
                                    placeholder={`e.g., ${jobData.eventType === 'Job' ? 'Spring Cleanup' : 'Follow-up Call'}`} 
                                    className="mt-1 w-full p-2 border rounded-md disabled:bg-gray-100 disabled:text-gray-500" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Status</label>
                                <select 
                                    name="status"
                                    value={jobData.status}
                                    onChange={handleChange}
                                    disabled={isReadOnly || isTechEditing}
                                    className="mt-1 w-full p-2 border rounded-md bg-white disabled:bg-gray-100 disabled:text-gray-500"
                                >
                                    {/* FIX 2: Hide 'Pending' status for non-Job event types.
                                        Pending is a Job-specific concept (needs confirmation before work begins).
                                        Appointments and Follow-Ups use Scheduled as their starting state. */}
                                    {statusOptions
                                        .filter(opt => jobData.eventType === 'Job' || opt !== 'Pending')
                                        // h1job-9c4e7b18 — a new Job from a technician (or with no client) starts unworked.
                                        .filter(opt => jobToEdit || jobData.eventType !== 'Job' || !createMustBeUnworked(jobData.clientId)
                                            || ['Pending', 'Scheduled', 'In Progress'].includes(opt))
                                        .map(opt => <option key={opt} value={opt}>{opt}</option>)
                                    }
                                </select>
                            </div>
                        </div>

                        {/* jobtidy-4d8b1f63 — Description, the scope of work written at booking.
                            MOVED here from inside the `Options` collapsible, which subtitled it
                            "notes" and kept it collapsed by default. It belongs with Job Title:
                            together they are what the job IS. Nothing about it changed but its
                            position — same `description` field, same handleChange, same
                            isReadOnly, so the save path (dataToSave spreads ...jobData) is
                            untouched. Distinct from Work Performed further down, which is what
                            was DONE and is the text the customer reads. */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <textarea
                                name="description"
                                value={jobData.description || ''}
                                onChange={handleChange}
                                disabled={isReadOnly}
                                rows="4"
                                className="mt-1 w-full p-2 border rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                            />
                        </div>

                        {/* Service Address */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Service Address</label>
                            <input 
                                name="address" 
                                value={jobData.address || ''} 
                                onChange={handleChange} 
                                onBlur={handleAddressBlur}
                                disabled={isReadOnly} 
                                placeholder="Address" 
                                className="mt-1 w-full p-2 border rounded-md disabled:bg-gray-100 disabled:text-gray-500" 
                            />

                            {/* ── Verification status ─────────────────────── */}
                            {addressGeocode.status === 'inherited' && (
                                <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2 mt-1">
                                    <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <div className="font-semibold">Verified from customer record</div>
                                        {addressGeocode.canonical && (
                                            <div className="text-green-600 mt-0.5 flex items-start gap-1">
                                                <MapPin size={11} className="flex-shrink-0 mt-0.5" />
                                                <span className="break-words">{addressGeocode.canonical}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {addressGeocode.status === 'checking' && (
                                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                    <Loader2 size={12} className="animate-spin" />
                                    <span>Verifying address…</span>
                                </div>
                            )}
                            {addressGeocode.status === 'ok' && (
                                <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2 mt-1">
                                    <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <div className="font-semibold">Address verified</div>
                                        {addressGeocode.canonical && (
                                            <div className="text-green-600 mt-0.5 flex items-start gap-1">
                                                <MapPin size={11} className="flex-shrink-0 mt-0.5" />
                                                <span className="break-words">{addressGeocode.canonical}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {addressGeocode.status === 'ambiguous' && addressGeocode.alternatives?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-1 space-y-2">
                                    <div className="flex items-center gap-2 text-xs text-amber-800 font-semibold">
                                        <AlertTriangle size={13} />
                                        <span>Did you mean one of these?</span>
                                    </div>
                                    <div className="space-y-1">
                                        {addressGeocode.alternatives.map((alt, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                onClick={() => handlePickJobAddressAlternative(alt)}
                                                className="w-full text-left text-xs px-2 py-1.5 bg-white border border-amber-200 rounded hover:border-amber-400 hover:bg-amber-50 flex items-start gap-2"
                                            >
                                                <MapPin size={11} className="flex-shrink-0 mt-0.5 text-amber-600" />
                                                <span className="break-words flex-1">{alt.canonical}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-[11px] text-amber-700">
                                        Or save as-is — we'll flag it as unverified.
                                    </p>
                                </div>
                            )}
                            {addressGeocode.status === 'not_found' && (
                                <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-1">
                                    <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <div className="font-semibold">Address not verified</div>
                                        <div className="text-red-600 mt-0.5">{addressGeocode.message}</div>
                                    </div>
                                </div>
                            )}
                            {addressGeocode.status === 'error' && (
                                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                                    <span>{addressGeocode.message}</span>
                                </div>
                            )}
                        </div>

                        {jobToEdit && selectedResources.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500 mb-1">Assigned Resources:</h4>
                                <div className="flex flex-wrap gap-2">
                                    {selectedResources.map(resourceId => {
                                        const resource = resources.find(r => r.id === resourceId);
                                        return resource ? (
                                            <span key={resource.id} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                                                {resource.name}
                                            </span>
                                        ) : null;
                                    })}
                                </div>
                            </div>
                        )}

                        {/* ── ZONE: WHEN ─────────────────────────────────────────────── */}
                        {(jobData.eventType === 'Job' || jobData.eventType !== 'Job') && (
                            <div className="flex items-center gap-2 pt-2 pb-1">
                                <Clock size={14} className="text-gray-400" />
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">When</span>
                            </div>
                        )}

                        {/* ── SCHEDULING MODE (Jobs only) ──────────────────────────── */}
                        {jobData.eventType === 'Job' && (
                            <div className="p-4 border border-gray-200 rounded-xl bg-gray-50 space-y-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <Clock size={15} className="text-gray-500" />
                                    <span className="text-sm font-bold text-gray-700">Scheduling</span>
                                </div>

                                {/* Mode toggle */}
                                <div className="flex rounded-md overflow-hidden border border-gray-300 w-fit">
                                    <button
                                        type="button"
                                        onClick={() => { if (!isReadOnly) { setSchedulingMode('window'); setDurationMode('hours'); } }}
                                        className={`px-4 py-2 text-sm font-medium transition-colors ${schedulingMode === 'window' && durationMode !== 'days' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        Arrival Window
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (isReadOnly) return;
                                            // PHASE FU-DUR VERIFIER: r1-fu-dur-7d2c4e89
                                            // When switching window → exact, recompute endAt from
                                            // the current Estimated Duration so the user sees a
                                            // coherent time block (e.g. 4h template → switch to
                                            // exact → endAt = startAt + 4h). Pre-fix behavior left
                                            // endAt stale at "now + 1h" from initial state.
                                            setSchedulingMode('exact');
                                            setDurationMode('hours');
                                            const durMins = (jobDurationHours * 60 + jobDurationMins) || 60;
                                            setJobData(prev => {
                                                const prevStart = prev.startAt instanceof Date ? prev.startAt : new Date(prev.startAt);
                                                const newEnd = new Date(prevStart.getTime() + durMins * 60 * 1000);
                                                return isNaN(newEnd.getTime()) ? prev : { ...prev, endAt: newEnd };
                                            });
                                        }}
                                        className={`px-4 py-2 text-sm font-medium transition-colors ${schedulingMode === 'exact' && durationMode !== 'days' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        Exact Time
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { if (!isReadOnly) { setDurationMode('days'); setSchedulingMode('exact'); } }}
                                        className={`px-4 py-2 text-sm font-medium transition-colors ${durationMode === 'days' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        Multi-day
                                    </button>
                                </div>

                                {durationMode === 'days' ? (
                                    /* ── Multi-day mode ───────────────────────────── */
                                    <div className="space-y-4">
                                        {/* r2-cs-toggle-4f8d2a61 — per-crew sub-toggle (only when 2+ crews on the job) */}
                                        {lineItemCrews.length >= 2 && (
                                            <label className="flex items-center gap-2 p-2.5 rounded-lg bg-purple-50 border border-purple-200 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={perCrewScheduling}
                                                    onChange={e => !isReadOnly && setPerCrewScheduling(e.target.checked)}
                                                    disabled={isReadOnly}
                                                    className="w-4 h-4 accent-purple-600"
                                                />
                                                <span className="text-sm font-semibold text-purple-800">Schedule each crew separately</span>
                                                <span className="text-xs text-purple-600">(e.g. wall crew Tue, sod crew Thu)</span>
                                            </label>
                                        )}

                                        {perCrewScheduling && lineItemCrews.length >= 2 ? (
                                            <PerCrewScheduler
                                                crews={lineItemCrews.map(crewId => ({
                                                    crewGroupId: crewId,
                                                    crewGroupName: routeGroups.find(g => g.id === crewId)?.name || '',
                                                }))}
                                                crewSchedule={crewSchedule}
                                                setCrewSchedule={setCrewSchedule}
                                                isReadOnly={isReadOnly}
                                                getAppearanceConflict={(crewGroupId, appearanceId) =>
                                                    crewAppearanceConflicts[`${crewGroupId}|${appearanceId}`] || null}
                                                onShowConflict={setConflictDetails}
                                                defaultDate={windowDate}
                                                defaultEndDate={(() => {
                                                    // r2-cs-seedend-4d8a1c73 — same math as save (1482) + preview
                                                    if (!windowDate) return '';
                                                    const e = new Date(windowDate + 'T00:00:00');
                                                    e.setDate(e.getDate() + (jobDurationDays - 1));
                                                    return `${e.getFullYear()}-${String(e.getMonth() + 1).padStart(2, '0')}-${String(e.getDate()).padStart(2, '0')}`;
                                                })()}
                                            />
                                        ) : (
                                        <>
                                        <p className="text-xs text-gray-500">Creates an all-day calendar block spanning multiple days. Arrival window and route optimization do not apply.</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                                <input
                                                    type="date"
                                                    value={windowDate}
                                                    onChange={e => !isReadOnly && setWindowDate(e.target.value)}
                                                    disabled={isReadOnly}
                                                    className="w-full p-2.5 border rounded-xl bg-white disabled:bg-gray-100 disabled:text-gray-500 focus:ring-2 focus:ring-blue-500 outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="365"
                                                        value={jobDurationDays}
                                                        onChange={e => !isReadOnly && setJobDurationDays(Math.max(1, parseInt(e.target.value) || 1))}
                                                        disabled={isReadOnly}
                                                        className="w-20 p-2.5 border rounded-xl bg-white disabled:bg-gray-100 text-center text-lg font-bold focus:ring-2 focus:ring-blue-500 outline-none"
                                                    />
                                                    <span className="text-sm text-gray-500">{jobDurationDays === 1 ? 'day' : 'days'}</span>
                                                </div>
                                                {/* Quick picks */}
                                                {!isReadOnly && (
                                                    <div className="flex gap-1.5 flex-wrap mt-2">
                                                        {[[1,'1d'],[3,'3d'],[5,'5d'],[7,'1wk'],[14,'2wk'],[30,'1mo']].map(([d,label]) => (
                                                            <button key={label} type="button"
                                                                onClick={() => setJobDurationDays(d)}
                                                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${jobDurationDays === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                                                {label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {/* Preview */}
                                        {windowDate && (
                                            <p className="text-xs text-blue-600 font-medium">
                                                {(() => {
                                                    const start = new Date(windowDate + 'T00:00:00');
                                                    const end = new Date(windowDate + 'T00:00:00');
                                                    end.setDate(end.getDate() + jobDurationDays - 1);
                                                    const fmt = d => d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
                                                    return jobDurationDays === 1
                                                        ? `All day: ${fmt(start)}`
                                                        : `${fmt(start)} → ${fmt(end)} (${jobDurationDays} days)`;
                                                })()}
                                            </p>
                                        )}
                                        </>
                                        )}
                                    </div>
                                ) : schedulingMode === 'window' ? (
                                    <div className="space-y-4">
                                        <p className="text-xs text-gray-500">Customer sees the window, not an exact time. Route optimization works freely within the window.</p>

                                        {/* Date + Arrival Window */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="date"
                                                        value={windowDate}
                                                        onChange={e => !isReadOnly && setWindowDate(e.target.value)}
                                                        disabled={isReadOnly}
                                                        className="flex-1 p-2.5 border rounded-xl bg-white disabled:bg-gray-100 disabled:text-gray-500 focus:ring-2 focus:ring-blue-500 outline-none"
                                                    />
                                                    {!isReadOnly && (
                                                        <button
                                                            type="button"
                                                            onClick={findFirstAvailable}
                                                            title="Find the first available date based on your schedule and travel buffer"
                                                            className="flex-shrink-0 px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl text-xs font-semibold hover:bg-blue-100 transition-colors whitespace-nowrap"
                                                        >
                                                            ⚡ First Open
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Window</label>
                                                <div className="relative">
                                                    <select
                                                        value={arrivalWindow}
                                                        onChange={e => !isReadOnly && setArrivalWindow(e.target.value)}
                                                        disabled={isReadOnly}
                                                        className="w-full appearance-none p-2.5 pr-8 border rounded-xl bg-white disabled:bg-gray-100 disabled:text-gray-500 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                                                    >
                                                        {getArrivalWindows(companySettings).map(w => <option key={w.key} value={w.key}>{w.label}</option>)}{/* aw-seam4 */}
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Job Duration — explicit, separate from billing */}
                                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3.5">
                                            <label className="block text-sm font-semibold text-blue-800 mb-0.5">Job Duration</label>
                                            <p className="text-xs text-blue-600 mb-3">How long will this job take on site? Sets the calendar block. Independent from billing line items.</p>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1.5 bg-white border border-blue-200 rounded-xl px-3 py-2 flex-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="23"
                                                        value={jobDurationHours}
                                                        onChange={e => !isReadOnly && setJobDurationHours(Math.max(0, parseInt(e.target.value) || 0))}
                                                        disabled={isReadOnly}
                                                        className="w-12 text-center text-lg font-bold text-gray-800 bg-transparent border-none outline-none disabled:text-gray-400"
                                                    />
                                                    <span className="text-sm text-gray-500 font-medium">hrs</span>
                                                </div>
                                                <span className="text-gray-400 font-bold text-lg">:</span>
                                                <div className="flex items-center gap-1.5 bg-white border border-blue-200 rounded-xl px-3 py-2 flex-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="59"
                                                        step="15"
                                                        value={jobDurationMins}
                                                        onChange={e => !isReadOnly && setJobDurationMins(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                                                        disabled={isReadOnly}
                                                        className="w-12 text-center text-lg font-bold text-gray-800 bg-transparent border-none outline-none disabled:text-gray-400"
                                                    />
                                                    <span className="text-sm text-gray-500 font-medium">min</span>
                                                </div>
                                                {/* Quick-pick buttons */}
                                                {!isReadOnly && (
                                                    <div className="flex gap-1.5 flex-wrap">
                                                        {[[0,30,'30m'],[1,0,'1h'],[2,0,'2h'],[3,0,'3h'],[4,0,'4h']].map(([h,m,label]) => (
                                                            <button key={label} type="button"
                                                                onClick={() => { setJobDurationHours(h); setJobDurationMins(m); }}
                                                                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${jobDurationHours === h && jobDurationMins === m ? 'bg-blue-600 text-white' : 'bg-white border border-blue-200 text-blue-700 hover:bg-blue-100'}`}>
                                                                {label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {/* Live preview */}
                                            {(jobDurationHours > 0 || jobDurationMins > 0) && windowDate && (
                                                <p className="text-xs text-blue-600 mt-2.5 font-medium">
                                                    {(() => {
                                                        const awWin = getArrivalWindow(arrivalWindow, companySettings); // aw-seam4
                                                        const startH = awWin.startH;
                                                        const startM = awWin.startM;
                                                        const startStr = `${startH > 12 ? startH - 12 : (startH === 0 ? 12 : startH)}:${String(startM).padStart(2,'0')} ${startH >= 12 ? 'PM' : 'AM'}`;
                                                        const endMinsTotal = startH * 60 + startM + jobDurationHours * 60 + jobDurationMins;
                                                        const endH = Math.floor(endMinsTotal / 60) % 24;
                                                        const endM = endMinsTotal % 60;
                                                        const endStr = `${endH > 12 ? endH - 12 : endH || 12}:${String(endM).padStart(2,'0')} ${endH >= 12 ? 'PM' : 'AM'}`;
                                                        return `Calendar block: ${startStr} → ${endStr}`;
                                                    })()}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <p className="text-xs text-gray-500">Use for time-sensitive appointments. This job won't be reordered by route optimization.</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Start Time</label>
                                                {/* PHASE FU-DUR2 VERIFIER: r1-fu-dur2-3e7a1c84
                                                    Custom DateTimePicker replaces datetime-local because
                                                    Chromium/Firefox on macOS ignore the step attribute for
                                                    the minute picker. Bridge: synthesize an event-shaped
                                                    payload so handleDateChange (which also auto-sets endAt
                                                    when startAt changes) keeps working unchanged. */}
                                                <DateTimePicker
                                                    value={jobData.startAt}
                                                    onChange={(d) => handleDateChange({ target: { name: 'startAt', value: d.toISOString() } })}
                                                    disabled={isReadOnly}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">End Time</label>
                                                <DateTimePicker
                                                    value={jobData.endAt}
                                                    onChange={(d) => handleDateChange({ target: { name: 'endAt', value: d.toISOString() } })}
                                                    disabled={isReadOnly}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* For non-Job events: start/end time pickers */}
                        {jobData.eventType !== 'Job' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Start Time</label>
                                <DateTimePicker
                                    value={jobData.startAt}
                                    onChange={(d) => handleDateChange({ target: { name: 'startAt', value: d.toISOString() } })}
                                    disabled={isReadOnly}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">End Time</label>
                                <DateTimePicker
                                    value={jobData.endAt}
                                    onChange={(d) => handleDateChange({ target: { name: 'endAt', value: d.toISOString() } })}
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                        )}

                        {/* ── Recurrence — moved adjacent to scheduling ─────────── */}
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                            <div className="flex items-center gap-2 mb-3">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                                <span className="text-sm font-semibold text-gray-700">Repeats</span>
                            </div>
                            {/* One coherent vertical block: Frequency -> Every N unit -> end control -> live summary. */}
                            <div className="space-y-3">
                                <div className="relative md:max-w-xs">
                                    <select
                                        name="frequency"
                                        value={jobData.recurrence.frequency}
                                        onChange={handleRecurrenceChange}
                                        disabled={isReadOnly}
                                        className="w-full appearance-none p-2.5 pr-8 border rounded-xl bg-white disabled:bg-gray-100 disabled:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                    >
                                        {recurrenceOptions.map(opt => <option key={opt} value={opt}>{opt === 'Custom' ? 'Custom…' : opt}</option>)}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                    </div>
                                </div>

                                {/* Custom: Every [N] [unit] */}
                                {jobData.recurrence.frequency === 'Custom' && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium text-gray-700">Every</span>
                                        <input
                                            type="number"
                                            name="interval"
                                            min="1"
                                            max="999"
                                            value={jobData.recurrence.interval}
                                            onChange={handleRecurrenceChange}
                                            disabled={isReadOnly}
                                            className="w-20 p-2.5 border rounded-xl bg-white disabled:bg-gray-100 disabled:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div className="relative">
                                            <select
                                                name="unit"
                                                value={jobData.recurrence.unit}
                                                onChange={handleRecurrenceChange}
                                                disabled={isReadOnly}
                                                className="appearance-none p-2.5 pr-8 border rounded-xl bg-white disabled:bg-gray-100 disabled:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                            >
                                                <option value="days">{parseInt(jobData.recurrence.interval, 10) === 1 ? 'day' : 'days'}</option>
                                                <option value="weeks">{parseInt(jobData.recurrence.interval, 10) === 1 ? 'week' : 'weeks'}</option>
                                                <option value="months">{parseInt(jobData.recurrence.interval, 10) === 1 ? 'month' : 'months'}</option>
                                                <option value="years">{parseInt(jobData.recurrence.interval, 10) === 1 ? 'year' : 'years'}</option>
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Never ends (Custom only) */}
                                {jobData.recurrence.frequency === 'Custom' && (
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            name="neverEnds"
                                            checked={!!jobData.recurrence.neverEnds}
                                            onChange={handleRecurrenceChange}
                                            disabled={isReadOnly}
                                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        />
                                        Never ends
                                    </label>
                                )}

                                {/* Ends on — for presets, and for Custom unless "Never ends". */}
                                {jobData.recurrence.frequency !== 'Never' && !(jobData.recurrence.frequency === 'Custom' && jobData.recurrence.neverEnds) && (
                                    <div className="md:max-w-xs">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Ends on</label>
                                        <input
                                            type="date"
                                            name="endDate"
                                            value={toOrgDateInput(jobData.recurrence.endDate)}
                                            onChange={handleRecurrenceChange}
                                            disabled={isReadOnly}
                                            className="w-full p-2.5 border rounded-xl bg-white disabled:bg-gray-100 disabled:text-gray-500"
                                        />
                                    </div>
                                )}

                                {/* Live summary — does the mental math for the user. */}
                                {jobData.recurrence.frequency === 'Custom' && (() => {
                                    const occ = enumerateOccurrences(jobData.startAt, jobData.recurrence, { max: jobData.recurrence.neverEnds ? 4 : 500, tz: orgTz });
                                    if (occ.length === 0) return null;
                                    const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: orgTz });
                                    const firstFew = occ.slice(0, 4).map(fmt).join(', ');
                                    const text = jobData.recurrence.neverEnds
                                        ? `Ongoing — ${firstFew}…`
                                        : `${occ.length} visit${occ.length === 1 ? '' : 's'}: ${firstFew}${occ.length > 4 ? '…' : ''} — last visit ${fmt(occ[occ.length - 1])}`;
                                    return <p className="text-xs text-gray-600 bg-white rounded-lg px-3 py-2 border border-gray-200">{text}</p>;
                                })()}
                            </div>
                        </div>

                        {jobData.eventType === 'Job' && (
                            <>
                                <div className="flex items-center gap-2 pt-2 pb-1">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"><rect x="2" y="3" width="6" height="6" rx="1"/><rect x="9" y="3" width="13" height="6" rx="1"/><rect x="2" y="13" width="6" height="6" rx="1"/><rect x="9" y="13" width="13" height="6" rx="1"/></svg>
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Services</span>
                                </div>
                                <div className="p-4 border-t border-b bg-gray-50 -mx-6">
                                    <h3 className="text-sm font-bold text-gray-700 mb-4 px-2">Line Items</h3>
                                    {/* PHASE FO-3 VERIFIER: r1-fo-jfm-banner-4f7b2c91 */}
                                    {pendingReviewIndices.length > 0 && (
                                        <div className="mb-4 mx-2 p-3 rounded-lg bg-amber-50 border border-amber-300 flex items-start gap-3">
                                            <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-amber-800">
                                                    {pendingReviewIndices.length} item{pendingReviewIndices.length > 1 ? 's' : ''} added on-site need{pendingReviewIndices.length > 1 ? '' : 's'} pricing review before invoicing
                                                </p>
                                                <p className="text-xs text-amber-700 mt-0.5">
                                                    Confirm the price on each flagged item below, then mark it reviewed.
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const el = document.getElementById(`lineitem-${pendingReviewIndices[0]}`);
                                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                }}
                                                className="text-xs font-semibold text-amber-800 underline hover:text-amber-900 shrink-0 mt-0.5"
                                            >
                                                Jump to first
                                            </button>
                                        </div>
                                    )}
                                    <div className="space-y-4">
                                        {jobData.lineItems.map((item, index) => (
                                            <div
                                                key={index}
                                                id={`lineitem-${index}`}
                                                className={`p-4 rounded-xl shadow-sm relative group ${
                                                    item.reviewStatus === 'pending_review'
                                                        ? 'bg-amber-50 border-2 border-amber-400'
                                                        : 'bg-white border border-gray-200'
                                                }`}
                                            >
                                                {/* PHASE FO-3 VERIFIER: r1-fo-jfm-itembadge-4f7b2c91 */}
                                                {item.addedByTech && (
                                                    <div className={`mb-3 -mt-1 ${item.reviewStatus === 'pending_review' ? '' : 'opacity-80'}`}>
                                                        <div className="flex items-center gap-1.5 pr-10">
                                                            {item.reviewStatus === 'pending_review' ? (
                                                                <>
                                                                    <AlertTriangle size={14} className="text-amber-600 shrink-0" />
                                                                    <span className="text-xs font-bold text-amber-700">
                                                                        Added on-site by {item.addedByName || 'a technician'} — needs pricing review
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <CheckCircle size={14} className="text-green-600 shrink-0" />
                                                                    <span className="text-xs font-semibold text-green-700">
                                                                        Added on-site by {item.addedByName || 'a technician'} — reviewed
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                        {item.reviewStatus === 'pending_review' && !isReadOnly && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleMarkItemReviewed(index)}
                                                                disabled={reviewingIndex === index}
                                                                className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                                                            >
                                                                {reviewingIndex === index ? (
                                                                    <Loader2 size={13} className="animate-spin" />
                                                                ) : (
                                                                    <CheckCircle size={13} />
                                                                )}
                                                                Mark as reviewed
                                                            </button>
                                                        )}
                                                    </div>
                                                )}

                                                {!isReadOnly && (
                                                    <button 
                                                        onClick={() => removeLineItem(index)} 
                                                        className="absolute top-2 right-2 p-1.5 text-gray-300 hover:text-red-500 rounded-full"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}

                                                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
                                                    
                                                    <div className="col-span-12 md:col-span-4">
                                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Catalog Item</label>
                                                        {!isReadOnly ? (
                                                            <div className="relative">
                                                                <Search size={14} className="absolute left-2.5 top-3 text-gray-400" />
                                                                <select 
                                                                    value={item.serviceId || ''} 
                                                                    onChange={(e) => handleLineItemChange(index, 'service', e.target.value)} 
                                                                    className="w-full pl-8 p-2 border rounded-lg text-sm bg-gray-50 focus:bg-white transition-colors"
                                                                >
                                                                    <option value="">Custom Item...</option>
                                                                    {pickerItems(services, item.serviceId).map(s => <option key={s.id} value={s.id}>{s.pickerLabel}</option>)/* catscreen-7a2c4e91 */}
                                                                </select>
                                                            </div>
                                                        ) : (
                                                            <input value={item.name} disabled className="w-full p-2 border rounded-lg bg-gray-100" />
                                                        )}
                                                    </div>

                                                    <div className="col-span-12 md:col-span-4">
                                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Item</label>
                                                        <input 
                                                            value={item.name} 
                                                            onChange={(e) => handleLineItemChange(index, 'name', e.target.value)} 
                                                            disabled={isReadOnly || !!item.serviceId} 
                                                            placeholder="Item Name (required)" 
                                                            className={`w-full p-2 border rounded-lg text-sm ${
                                                                item.serviceId ? 'bg-gray-100 text-gray-600'
                                                                : !item.name?.trim() ? 'bg-white font-medium border-red-300'
                                                                : 'bg-white font-medium'
                                                            }`} 
                                                        />
                                                        <LineDescriptionInput
                                                            value={item.description} 
                                                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)} 
                                                            disabled={isReadOnly} 
                                                            labelClassName="text-[10px] font-bold text-gray-400 uppercase mb-1 block" 
                                                        />
                                                    </div>

                                                    <div className="col-span-6 md:col-span-2">
                                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Qty</label>
                                                        <input 
                                                            type="number" 
                                                            value={item.quantity}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                if (v === '' || parseFloat(v) <= 99999) handleLineItemChange(index, 'quantity', v);
                                                            }}
                                                            disabled={isReadOnly}
                                                            min="0" max="99999" step="0.01"
                                                            className="w-full p-2 border rounded-lg text-center font-semibold text-gray-700" 
                                                        />
                                                    </div>

                                                    <div className="col-span-6 md:col-span-2">
                                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Price</label>
                                                        <div className="relative">
                                                            <span className="absolute left-2 top-2 text-gray-400 text-xs">$</span>
                                                            <input 
                                                                type="number" 
                                                                value={item.price}
                                                                onChange={(e) => {
                                                                    const v = e.target.value;
                                                                    if (v === '' || parseFloat(v) <= 999999.99) handleLineItemChange(index, 'price', v);
                                                                }}
                                                                disabled={isReadOnly}
                                                                min="0" max="999999.99" step="0.01"
                                                                className="w-full pl-5 p-2 border rounded-lg text-right font-semibold text-gray-700" 
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {!isReadOnly && (
                                                    <div className="mt-3 pt-2 border-t border-gray-100 flex flex-wrap gap-4 items-center">
                                                        
                                                        <div className="flex items-center gap-2">
                                                            <div className={`p-1 rounded ${item.type === 'Material' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                                                                {item.type === 'Material' ? <Package size={14} /> : <Wrench size={14} />}
                                                            </div>
                                                            <select 
                                                                value={item.type || 'Service'} 
                                                                onChange={(e) => handleLineItemChange(index, 'type', e.target.value)}
                                                                className="text-xs border-0 bg-transparent font-medium text-gray-700 cursor-pointer hover:bg-gray-50 rounded px-1"
                                                            >
                                                                <option value="Service">Service</option>
                                                                <option value="Material">Material</option>
                                                            </select>
                                                        </div>

                                                        {!item.taxExempt && (
                                                            <select
                                                                value={item.taxCodeId || ''}
                                                                onChange={(e) => handleLineItemChange(index, 'taxCodeId', e.target.value)}
                                                                className="p-1 border rounded-md text-xs w-24 bg-white ml-auto md:ml-0"
                                                                disabled={isLoadingTax}
                                                            >
                                                                <option value="">Default Tax</option>
                                                                {taxOptions.map(code => (
                                                                    <option key={code.Id} value={code.Id}>{code.Name}</option>
                                                                ))}
                                                            </select>
                                                        )}

                                                        <div className="flex items-center ml-auto md:ml-2">
                                                            <input
                                                                type="checkbox"
                                                                id={`taxExempt-${index}`}
                                                                checked={item.taxExempt || false}
                                                                onChange={(e) => handleLineItemChange(index, 'taxExempt', e.target.checked)}
                                                                className="h-3 w-3 text-gray-500 border-gray-300 rounded"
                                                            />
                                                            <label htmlFor={`taxExempt-${index}`} className="ml-1 text-xs text-gray-500 select-none">
                                                                Tax Exempt
                                                            </label>
                                                        </div>

                                                        {!item.serviceId && item.name && (
                                                            <div className="flex items-center ml-2">
                                                                <input
                                                                    type="checkbox"
                                                                    id={`addToCatalog-${index}`}
                                                                    checked={item.addToCatalog || false}
                                                                    onChange={(e) => handleLineItemChange(index, 'addToCatalog', e.target.checked)}
                                                                    className="h-3 w-3 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                                                />
                                                                <label htmlFor={`addToCatalog-${index}`} className="ml-1 text-xs text-green-700 font-bold cursor-pointer select-none flex items-center gap-1">
                                                                    <Save size={12} /> Save
                                                                </label>
                                                            </div>
                                                        )}

                                                        {/* ── Crew badge — per line item crew assignment ── */}
                                                        {jobData.eventType === 'Job' && !isReadOnly && routeGroups.length > 0 && (
                                                            <div className="flex items-center gap-2 ml-auto">
                                                                <select
                                                                    value={item.crewGroupId || ''}
                                                                    onChange={(e) => {
                                                                        const newCrewId = e.target.value;
                                                                        const newCrewName = routeGroups.find(g => g.id === newCrewId)?.name || '';
                                                                        handleLineItemChange(index, 'crewGroupId', newCrewId);
                                                                        handleLineItemChange(index, 'crewGroupName', newCrewName);
                                                                        // If this is the first crew being set, auto-apply to all unassigned items
                                                                        if (newCrewId) {
                                                                            setJobData(prev => ({
                                                                                ...prev,
                                                                                lineItems: prev.lineItems.map((li, i) =>
                                                                                    i === index ? { ...li, crewGroupId: newCrewId, crewGroupName: newCrewName }
                                                                                    : li.crewGroupId ? li  // already has a crew, keep it
                                                                                    : { ...li, crewGroupId: newCrewId, crewGroupName: newCrewName }  // inherit
                                                                                )
                                                                            }));
                                                                        }
                                                                    }}
                                                                    className={`text-xs rounded-full px-2.5 py-1 border font-medium cursor-pointer transition-colors ${
                                                                        item.crewGroupId
                                                                            ? 'bg-blue-50 border-blue-200 text-blue-700'
                                                                            : 'bg-gray-50 border-gray-200 text-gray-500'
                                                                    }`}
                                                                >
                                                                    <option value="">No crew assigned</option>
                                                                    {routeGroups.map(g => (
                                                                        <option key={g.id} value={g.id}>{g.name}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                        {jobData.eventType === 'Job' && isReadOnly && item.crewGroupId && (
                                                            <span className="ml-auto text-xs bg-blue-50 border border-blue-200 text-blue-700 px-2.5 py-1 rounded-full font-medium">
                                                                {routeGroups.find(g => g.id === item.crewGroupId)?.name || 'Crew assigned'}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    {!isReadOnly && (
                                        <button 
                                            type="button" 
                                            onClick={addLineItem} 
                                            className="mt-4 ml-2 flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                                        >
                                            + Add Line Item
                                        </button>
                                    )}
                                    <div className="text-right mt-4 pt-4 border-t px-2">
                                        {(() => {
                                            const subtotal = jobData.lineItems.reduce((acc, item) => acc + (parseFloat(item.quantity || 0) * parseFloat(item.price || 0)), 0);
                                            const taxVal = jobData.invoiceTotal - subtotal;
                                            return (
                                                <>
                                                    <p className="text-sm text-gray-600">Subtotal: ${subtotal.toFixed(2)}</p>
                                                    <p className="text-sm text-gray-600">
                                                        Tax {subtotal > 0 ? `(${((taxVal / subtotal) * 100).toFixed(1)}%)` : ''}: ${taxVal.toFixed(2)}
                                                    </p>
                                                    <span className="text-gray-600">Total Job Value: </span>
                                                    <span className="font-bold text-xl">${jobData.invoiceTotal.toFixed(2)}</span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                {/* Job Costing — only meaningful after job is complete; financial readout, hidden from techs */}
                                {canViewFinancials && jobToEdit?.id && jobToEdit?.status === 'Completed' && (
                                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-4">
                                    <div className="flex items-center gap-2">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                                        <h3 className="text-sm font-bold text-gray-700">Job Costing</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
                                        {/* s6costing-4d8a2f63 — labour cost is pay: Owner/Admin/Manager only. */}
                                        {canSeeLaborCostHere && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Total Labor Cost</label>
                                            <p className="mt-1 text-lg font-semibold text-gray-800">{formatCurrency(totalLaborCost)}</p>
                                        </div>
                                        )}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Material Cost (from Expenses)</label>
                                            <p className="mt-1 text-lg font-semibold text-gray-800">{formatCurrency(calculatedMaterialCost)}</p>
                                        </div>
                                    </div>
                                    {linkedExpenses.length > 0 && (
                                        <div className="pt-2 border-t">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Linked Expenses</label>
                                            <ul className="space-y-1 text-sm max-h-24 overflow-y-auto">
                                                {linkedExpenses.map(exp => (
                                                    <li key={exp.id} className="flex justify-between items-center text-gray-600 pr-2">
                                                        <span className="flex items-center gap-2">
                                                            <LinkIcon size={14} />{exp.category} ({exp.vendor || 'N/A'})
                                                        </span>
                                                        <span className="font-medium">{formatCurrency(exp.amount)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                                )}
                            </>
                        )}
                        

                        {/* r5-b2b-joblabor-ui2-8b4e1f37 — per-job labor-billing override (Increment 1, inert). Relocated out of Admin Options → always-visible in the form body. */}
                        {/* Bill-out control is financial — gate on VIEW_FINANCIALS (was !isReadOnly, which would expose it once techs can fill the form). */}
                        {jobData.eventType === 'Job' && canViewFinancials && (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Labor billing for this job</label>
                                <p className="text-xs text-gray-500 mb-2">Controls whether tracked labor is billed to the customer for this job.</p>
                                <select
                                    name="billLaborToCustomer"
                                    value={billLaborToCustomer === null ? 'inherit' : billLaborToCustomer ? 'bill' : 'nobill'}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setBillLaborToCustomer(v === 'inherit' ? null : v === 'bill');
                                    }}
                                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="inherit">Use org default</option>
                                    <option value="bill">Bill labor on this job</option>
                                    <option value="nobill">Do not bill labor on this job</option>
                                </select>
                            </div>
                        )}

                        {/* r3-b3-ui-7e2a9c41 — B3: per-person tracked-labor readout (office-facing preview of billable
                            labor). Pure DISPLAY of saved punches; shows for any job with punches (in-progress or completed)
                            so the office can review accumulated time before generating the invoice. No writes, no backend. */}
                        {/* jobtimefix-4a9e2d73-b — SEAM B GATE SPLIT. The block itself is no longer behind
                            canViewFinancials: recorded TIME is not money, and a Field Technician seeing
                            nothing at all meant a capped 0h punch on their own job was invisible to the one
                            person who knows what actually happened. Cost/Billed stay behind
                            canViewFinancials below; the correction controls use canCorrectTime. */}
                        {punchReadout.length > 0 && (
                            <div className="rounded-xl border border-gray-200 overflow-hidden mb-4">
                                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <Clock size={15} className="text-gray-500" />
                                    <span className="text-sm font-semibold text-gray-700">Tracked Labor</span>
                                    <span className="text-xs text-gray-400 font-normal">— logged time on this job</span>
                                    {/* jobtimefix-4a9e2d73-b — op=add. Same visibility rules as the per-row
                                        controls: absent for techs, disabled with the reason for Office Staff. */}
                                    {canSeeCorrectControls && occurrenceJobId && (
                                        <button
                                            type="button"
                                            disabled={!canCorrectTime}
                                            title={canCorrectTime ? 'Add a time entry' : 'Only an Admin, Manager or Owner can correct job time.'}
                                            onClick={() => openPunchCorrection('add', null, null)}
                                            className={`ml-auto flex items-center gap-1 text-xs font-medium ${canCorrectTime ? 'text-blue-600 hover:text-blue-700' : 'text-gray-300 cursor-not-allowed'}`}
                                        >
                                            <Plus size={13} />Add time entry
                                        </button>
                                    )}
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {punchReadout.map(row => {
                                        const isExpanded = expandedPunchUserIds.includes(row.userId);
                                        const hoursLabel = Number.isInteger(row.hours) ? `${row.hours}` : row.hours.toFixed(2);
                                        return (
                                            <div key={row.userId}>
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedPunchUserIds(prev =>
                                                        prev.includes(row.userId) ? prev.filter(id => id !== row.userId) : [...prev, row.userId]
                                                    )}
                                                    className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                                                >
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <svg className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                        <span className="text-sm font-medium text-gray-800 truncate">{row.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-3 text-xs shrink-0">
                                                        {/* jobtimefix-4a9e2d73-b — MARK 1 of 3: needsReview, on the
                                                            COLLAPSED row so a capped punch is visible without expanding. */}
                                                        {row.needsReviewCount > 0 && (
                                                            <span className="flex items-center gap-1 text-amber-600 font-medium">
                                                                <AlertTriangle size={13} />
                                                                {row.needsReviewCount === 1 ? 'needs review' : `${row.needsReviewCount} need review`}
                                                            </span>
                                                        )}
                                                        <span className="font-semibold text-gray-700">{hoursLabel}h</span>
                                                        {/* Cost and bill-out are financial — still gated. s6costing-4d8a2f63:
                                                            Cost (pay) is Owner/Admin/Manager only; Billed keeps VIEW_FINANCIALS. */}
                                                        {canViewFinancials && (
                                                            <>
                                                                {canSeeLaborCostHere && (
                                                                    <span className="text-gray-500">Cost {formatCurrency(row.costAmount)}</span>
                                                                )}
                                                                {/* laborbill-one-rule-6c2a9e47 — exactly what the next invoice will charge. */}
                                                                {(() => {
                                                                    const b = laborBillingPreview[row.userId];
                                                                    if (!b) return <span className="text-gray-500">Not billed yet (no closed time)</span>;
                                                                    if (b.status === 'billed') return <span className="text-gray-500">Next invoice {formatCurrency(b.amount)} ({b.delta}h)</span>;
                                                                    if (b.status === 'alreadyInvoiced') return <span className="text-gray-500">Already invoiced</span>;
                                                                    if (b.status === 'noRate') return <span className="text-amber-600">Not billed: no bill-out rate set</span>;
                                                                    if (b.status === 'notBilledSwitchOff') return <span className="text-gray-500">Not billed — labor billing is off for this job</span>;
                                                                    return <span className="text-gray-500">Not billed yet (no closed time)</span>;
                                                                })()}
                                                            </>
                                                        )}
                                                    </div>
                                                </button>
                                                {isExpanded && (
                                                    <div className="px-4 pb-3 pt-1 bg-gray-50">
                                                        <ul className="space-y-1">
                                                            {row.punches.map((p, i) => {
                                                                const inDate = toJsDate(p.punchIn);
                                                                const outDate = toJsDate(p.punchOut);
                                                                const dateLabel = inDate ? inDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
                                                                const inLabel = inDate ? inDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—';
                                                                const outLabel = outDate ? outDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null;
                                                                const pHours = (p.hours != null) ? `${(parseFloat(p.hours) || 0).toFixed(2)}h` : null;
                                                                // jobtimefix-4a9e2d73-b — the three marks, and the controls.
                                                                const isOpenPunch = p.punchOut == null;
                                                                const isVoided = p.voided === true;
                                                                const wasEdited = p.editedByAdmin === true;
                                                                const wasAdded = p.addedByAdmin === true;
                                                                const whoName = (uid) => employees?.find(u => u.id === uid)?.name || 'an administrator';
                                                                const whenLabel = (v) => { const d = toJsDate(v); return d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'an unknown time'; };
                                                                const auditTitle = isVoided
                                                                    ? `Voided by ${whoName(p.voidedBy)} on ${whenLabel(p.voidedAt)} — ${p.voidReason || 'no reason recorded'}${p.voidNote ? ` (${p.voidNote})` : ''}`
                                                                    : wasEdited
                                                                        ? `Edited by ${whoName(p.editedBy)} on ${whenLabel(p.editedAt)} — ${p.editReason || 'no reason recorded'}${p.editNote ? ` (${p.editNote})` : ''}`
                                                                        : wasAdded
                                                                            ? `Added by ${whoName(p.addedBy)} on ${whenLabel(p.addedAt)} — ${p.editReason || 'no reason recorded'}${p.editNote ? ` (${p.editNote})` : ''}`
                                                                            : undefined;
                                                                return (
                                                                    <li key={i} className="flex items-start justify-between gap-2 text-xs text-gray-600">
                                                                        <span className="flex items-center gap-1.5 flex-wrap min-w-0">
                                                                            <span className="text-gray-400">{dateLabel}</span>
                                                                            {/* MARK 3 of 3: voided — struck through, never removed. A void
                                                                                that disappears is a deletion, and the whole point of
                                                                                void-not-delete is that the record stays visible. */}
                                                                            <span className={isVoided ? 'line-through text-gray-400' : undefined}>
                                                                                {inLabel} → {outLabel || <span className="text-blue-600 font-medium">in progress</span>}
                                                                            </span>
                                                                            {isVoided && (
                                                                                <span title={auditTitle} className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-medium cursor-help">Voided</span>
                                                                            )}
                                                                            {/* MARK 2 of 3: edited / added — quiet pill, who/when/why on hover. */}
                                                                            {!isVoided && (wasEdited || wasAdded) && (
                                                                                <span title={auditTitle} className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-medium cursor-help">
                                                                                    {wasEdited ? 'Edited' : 'Added'}
                                                                                </span>
                                                                            )}
                                                                            {/* MARK 1 of 3: needsReview — the sentence says what actually
                                                                                happened, because "needs review" alone tells the office nothing. */}
                                                                            {p.needsReview === true && (
                                                                                <span className="flex items-center gap-1 text-amber-600 font-medium">
                                                                                    <AlertTriangle size={12} />
                                                                                    Left running — closed at completion, 0h recorded
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                        <span className="flex items-center gap-2 shrink-0">
                                                                            {pHours && <span className="text-gray-500">{pHours}</span>}
                                                                            {/* The correction controls. Absent for a Field Technician; disabled
                                                                                with the server's own sentence for Office Staff / Sales
                                                                                Consultant. No control on an OPEN punch (the callable refuses
                                                                                it: "That timer is still running") or a voided one (ditto).
                                                                                occurrenceJobId gates it because a recurring visit with no job
                                                                                document has nothing to correct. */}
                                                                            {canSeeCorrectControls && occurrenceJobId && !isOpenPunch && !isVoided && (
                                                                                <>
                                                                                    <button
                                                                                        type="button"
                                                                                        disabled={!canCorrectTime}
                                                                                        title={canCorrectTime ? 'Correct this punch' : 'Only an Admin, Manager or Owner can correct job time.'}
                                                                                        onClick={() => openPunchCorrection('edit', p, p.idx)}
                                                                                        className={canCorrectTime ? 'text-gray-400 hover:text-blue-600' : 'text-gray-300 cursor-not-allowed'}
                                                                                    >
                                                                                        <Pencil size={13} />
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        disabled={!canCorrectTime}
                                                                                        title={canCorrectTime ? 'Void this punch' : 'Only an Admin, Manager or Owner can correct job time.'}
                                                                                        onClick={() => openPunchCorrection('void', p, p.idx)}
                                                                                        className={canCorrectTime ? 'text-gray-400 hover:text-red-600' : 'text-gray-300 cursor-not-allowed'}
                                                                                    >
                                                                                        <Ban size={13} />
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* ── ZONE: TEAM & RESOURCES ─────────────────────────────── */}
                        <div className="flex items-center gap-2 pt-2 pb-1">
                            <Users size={14} className="text-gray-400" />
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Team &amp; Resources</span>
                        </div>

                        {/* ── ASSIGNMENT — optional at job creation ──────────────── */}
                        <div className="border rounded-xl overflow-hidden">
                            <button
                                type="button"
                                onClick={() => !isReadOnly && setShowAssignment(p => !p)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left rounded-t-xl"
                            >
                                <div className="flex items-center gap-2">
                                    <Users size={15} className="text-gray-500" />
                                    <span className="text-sm font-semibold text-gray-700">Assign Crew / Employees</span>
                                    <span className="text-xs text-gray-400 font-normal">— optional</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {!showAssignment && (assignmentMode === 'group' && selectedGroupId ? (
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                            {routeGroups.find(g => g.id === selectedGroupId)?.name || 'Group assigned'}
                                        </span>
                                    ) : jobData.jobCosting.laborEntries.length > 0 ? (
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                            {jobData.jobCosting.laborEntries.length} assigned
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-400">Assign via Weekly Planner or Routes</span>
                                    ))}
                                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${showAssignment ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </div>
                            </button>

                            {showAssignment && (
                                <div className="p-4 space-y-3 bg-white">
                                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                        💡 For weekly recurring jobs, crew assignment happens in the Weekly Planner. Assign here for one-off jobs, or to configure multiple crews — crews assigned to line items above are reflected automatically.
                                    </p>

                                    {/* Mode toggle — Jobs only */}
                                    {jobData.eventType === 'Job' && !isReadOnly && (
                                        <div className="flex rounded-md overflow-hidden border border-gray-300 w-fit">
                                            <button
                                                type="button"
                                                onClick={() => setAssignmentMode('individual')}
                                                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${assignmentMode === 'individual' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                            >
                                                <User size={13} /> Individual
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setAssignmentMode('group')}
                                                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${assignmentMode === 'group' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                            >
                                                <Users size={13} /> Route Group
                                            </button>
                                        </div>
                                    )}

                                    {/* GROUP mode */}
                                    {/* Auto-populate from line item crews */}
                                    {lineItemCrews.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mb-3 p-2.5 bg-blue-50 border border-blue-200 rounded-xl">
                                            <span className="text-xs font-semibold text-blue-700 w-full mb-1">Crews from line items:</span>
                                            {lineItemCrews.map(crewId => {
                                                const grp = routeGroups.find(g => g.id === crewId);
                                                return grp ? (
                                                    <span key={crewId} className="text-xs bg-blue-600 text-white px-2.5 py-1 rounded-full font-medium">
                                                        {grp.name}
                                                    </span>
                                                ) : null;
                                            })}
                                        </div>
                                    )}

                                    {assignmentMode === 'group' ? (
                                        <div className="space-y-2">
                                            <select
                                                value={selectedGroupId}
                                                onChange={e => !isReadOnly && setSelectedGroupId(e.target.value)}
                                                disabled={isReadOnly}
                                                className="w-full p-2 border rounded-md bg-white disabled:bg-gray-100 disabled:text-gray-500"
                                            >
                                                <option value="">— Select a Route Group —</option>
                                                {routeGroups.map(g => (
                                                    <option key={g.id} value={g.id}>{g.name}</option>
                                                ))}
                                            </select>
                                            {selectedGroupId && (() => {
                                                const grp = routeGroups.find(g => g.id === selectedGroupId);
                                                const members = (grp?.memberIds || []).map(id => {
                                                    const u = employees?.find(e => e.id === id) || fieldWorkers.find(f => f.id === id);
                                                    return u ? u.name : null;
                                                }).filter(Boolean);
                                                return members.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1.5 mt-1">
                                                        {members.map(n => (
                                                            <span key={n} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full">{n}</span>
                                                        ))}
                                                    </div>
                                                ) : null;
                                            })()}
                                            {routeGroups.length === 0 && (
                                                <p className="text-xs text-gray-400 italic">No route groups yet. Create them in Settings → Route Groups.</p>
                                            )}
                                        </div>
                                    ) : (
                                        /* INDIVIDUAL mode */
                                        <div className="space-y-2 p-2 rounded-md bg-white">
                                            {employees?.map(emp => {
                                                const isAssigned = jobData.jobCosting.laborEntries.some(e => e.userId === emp.id);
                                                // jobedit-nostomp-7c3f1a58 — someone with clocked time on this job
                                                // is locked on. The job's real cost stays visible; it just stops
                                                // being removable here. Matches the handler guard above.
                                                const clockedRow = jobData.jobCosting.laborEntries.find(
                                                    e => e.userId === emp.id && e.source === 'clocked'
                                                );
                                                return (
                                                    <div key={emp.id} className="flex items-center py-0.5">
                                                        <input
                                                            type="checkbox"
                                                            id={`emp-assign-${emp.id}`}
                                                            checked={isAssigned}
                                                            onChange={() => handleEmployeeAssignToggle(emp)}
                                                            disabled={isReadOnly || !!clockedRow}
                                                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-200"
                                                        />
                                                        <label htmlFor={`emp-assign-${emp.id}`} className="ml-3 text-sm text-gray-700 flex items-center gap-1.5">
                                                            {emp.name}
                                                            {clockedRow && (
                                                                <span
                                                                    className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full"
                                                                    title="This person has clocked time on this job, so it can't be changed here."
                                                                >
                                                                    {(parseFloat(clockedRow.hours) || 0).toFixed(2)}h clocked
                                                                </span>
                                                            )}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                            {jobData.jobCosting.laborEntries.some(e => e?.source === 'clocked') && (
                                                <p className="text-xs text-gray-400 italic pt-1">
                                                    Clocked time can't be changed here — it comes from the job timer.
                                                </p>
                                            )}
                                            {fieldWorkers.length > 0 && (
                                                <>
                                                    <div className="text-xs font-medium text-gray-400 uppercase tracking-wide pt-2 pb-0.5 px-1">Field Workers</div>
                                                    {fieldWorkers.map(fw => {
                                                        const isAssigned = assignedFieldWorkerIds.includes(fw.id);
                                                        return (
                                                            <div key={fw.id} className="flex items-center py-0.5">
                                                                <input
                                                                    type="checkbox"
                                                                    id={`fw-assign-${fw.id}`}
                                                                    checked={isAssigned}
                                                                    onChange={() => {
                                                                        if (isReadOnly) return;
                                                                        setAssignedFieldWorkerIds(prev =>
                                                                            isAssigned ? prev.filter(id => id !== fw.id) : [...prev, fw.id]
                                                                        );
                                                                    }}
                                                                    disabled={isReadOnly}
                                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-200"
                                                                />
                                                                <label htmlFor={`fw-assign-${fw.id}`} className="ml-3 text-sm text-gray-700 flex items-center gap-1.5">
                                                                    {fw.name}
                                                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full uppercase">Field</span>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>{/* end assignment collapsible */}

                        {/* Assign Resources */}
                        <div className="p-4 border rounded-xl">
                            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>Resources</label>
                            
                            {!canEditResources ? (
                                <div className="p-2 bg-gray-50 rounded-md">
                                    {selectedResources.length === 0 ? (
                                        <p className="text-sm text-gray-500 italic">No resources assigned.</p>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {selectedResources.map(resourceId => {
                                                 const resource = resources.find(r => r.id === resourceId);
                                                 return resource ? (
                                                     <span key={resource.id} className="px-2 py-1 bg-gray-200 text-gray-700 text-sm rounded-full">
                                                         {resource.name}
                                                     </span>
                                                 ) : null;
                                            })}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="border rounded-md p-2 h-40 overflow-y-auto bg-white">
                                    {availableResources.length === 0 && (
                                        <p className="text-sm text-gray-500">No available resources found.</p>
                                    )}
                                    {availableResources.map(resource => {
                                        const isSelected = selectedResources.includes(resource.id);
                                        return (
                                            <div key={resource.id} className="flex items-center mb-1">
                                                <input
                                                    type="checkbox"
                                                    id={`resource-${resource.id}`}
                                                    checked={isSelected}
                                                    onChange={() => {
                                                        setSelectedResources(prev =>
                                                            isSelected
                                                                ? prev.filter(id => id !== resource.id)
                                                                : [...prev, resource.id]
                                                        );
                                                    }}
                                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    disabled={resource.conflict?.type === 'inUse'} 
                                                />
                                                <label htmlFor={`resource-${resource.id}`} className="ml-2 text-sm flex items-center">
                                                    {resource.conflict ? (
                                                        <button
                                                            type="button" 
                                                            onClick={() => setConflictDetails({ 
                                                                resourceName: resource.name, 
                                                                conflictingJob: resource.conflict.job, 
                                                                type: resource.conflict.type 
                                                            })}
                                                            className={`underline cursor-pointer ${
                                                                resource.conflict.type === 'inUse' ? 'text-red-600 line-through' : 'text-orange-500'
                                                            }`}
                                                            title="Click to see conflict details"
                                                        >
                                                            {resource.name}
                                                        </button>
                                                    ) : (
                                                        <span className="text-gray-800">{resource.name}</span>
                                                    )}
                                                    {resource.conflict && (
                                                        <span className={`ml-1 font-semibold ${
                                                            resource.conflict.type === 'inUse' ? 'text-red-600' : 'text-orange-500'
                                                        }`}>
                                                            {resource.conflict.type === 'inUse' ? '(In Use)' : '(Potential Conflict)'}
                                                        </span>
                                                    )}
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>


                    {/* ── ADMIN OPTIONS — collapsible ─────────────────── */}
                        <div className="border rounded-xl overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setShowAdminOptions(p => !p)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                            >
                                <div className="flex items-center gap-2">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                                    <span className="text-sm font-semibold text-gray-700">Options</span>
                                    {/* jobtidy-4d8b1f63 — the subtitle describes what is ACTUALLY in
                                        here. It said "reminder & notes" while holding Description
                                        (scope of work, not notes) and a read-only list of legacy job
                                        notes. Description has moved out.
                                        legnotes9-6b4e8d12 — SEAM 9. It was a ternary on
                                        jobToEdit.techNotes; with the array gone it could only ever
                                        take the '— reminder' arm, so it is now that literal. */}
                                    <span className="text-xs text-gray-400 font-normal">— reminder</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {reminderEnabled && (
                                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                            <Bell size={10} /> Reminder set
                                        </span>
                                    )}
                                    {/* legnotes9-6b4e8d12 — SEAM 9. The "Has notes" badge is gone.
                                        It was gated on the legacy techNotes array, so it could no
                                        longer appear. Notes now live in the Notes & Media strip,
                                        which carries its own count. */}
                                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${showAdminOptions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </div>
                            </button>

                            {showAdminOptions && (
                                <div className="p-4 space-y-4 bg-white border-t border-gray-100">

                        {/* JOB CONFIRMATION REMINDER */}
                        {jobData.eventType === 'Job' && !isReadOnly && (
                            <div className={`rounded-lg border p-4 transition-all ${
                                reminderEnabled
                                    ? 'bg-amber-50 border-amber-200'
                                    : 'bg-gray-50 border-gray-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        {reminderEnabled
                                            ? <Bell size={16} className="text-amber-600" />
                                            : <BellOff size={16} className="text-gray-400" />
                                        }
                                        <span className={`text-sm font-semibold ${reminderEnabled ? 'text-amber-800' : 'text-gray-500'}`}>
                                            Confirmation Reminder
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = !reminderEnabled;
                                            if (next) {
                                                const orgSettings = companySettings?.jobReminderSettings;
                                                const calc = calculateReminderDate(jobData.startAt, orgSettings, orgTz);
                                                if (calc && isDateInPast(calc, orgTz)) {
                                                    setReminderPastDateWarning(true);
                                                    return;
                                                }
                                                setReminderEnabled(true);
                                                if (!reminderDate) {
                                                    setReminderDate(calc);
                                                    setReminderDateOverridden(false);
                                                }
                                                setReminderPastDateWarning(false);
                                            } else {
                                                setReminderEnabled(false);
                                                setReminderPastDateWarning(false);
                                            }
                                        }}
                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                                            reminderEnabled ? 'bg-amber-500' : 'bg-gray-300'
                                        }`}
                                    >
                                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                            reminderEnabled ? 'translate-x-5' : 'translate-x-0'
                                        }`} />
                                    </button>
                                </div>

                                {reminderPastDateWarning && !reminderEnabled && (
                                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg space-y-2">
                                        <p className="text-xs font-semibold text-red-700">
                                            ⚠️ Reminder disabled — the auto-calculated date has already passed.
                                        </p>
                                        <p className="text-xs text-red-600">
                                            Pick a future date below to re-enable the reminder.
                                        </p>
                                        <div>
                                            <label className="block text-xs font-medium text-red-700 mb-1">Choose a new reminder date</label>
                                            <input
                                                type="date"
                                                min={toOrgDateInput(new Date())}
                                                onChange={(e) => {
                                                    const picked = orgDayStart(e.target.value, orgTz); // orgtime-7c2e4b19
                                                    if (picked && !isDateInPast(picked, orgTz)) {
                                                        setReminderDate(picked);
                                                        setReminderEnabled(true);
                                                        setReminderDateOverridden(true);
                                                        setReminderPastDateWarning(false);
                                                    }
                                                }}
                                                className="p-1.5 border border-red-300 rounded-md text-sm bg-white focus:ring-red-400 focus:border-red-400"
                                            />
                                        </div>
                                    </div>
                                )}

                                {reminderEnabled && (
                                    <div className="mt-3 pt-3 border-t border-amber-200 space-y-2">
                                        <p className="text-xs text-amber-700">
                                            An in-app notification will be sent to all admins and managers on this date as a reminder to confirm the job with the customer.
                                        </p>

                                        <div className="flex flex-wrap items-center gap-3 mt-2">
                                            <div>
                                                <label className="block text-xs font-medium text-amber-800 mb-1">
                                                    Remind on
                                                </label>
                                                <input
                                                    type="date"
                                                    value={reminderDate ? toOrgDateInput(reminderDate) : ''}
                                                    onChange={handleReminderDateChange}
                                                    className="p-1.5 border border-amber-300 rounded-md text-sm bg-white focus:ring-amber-400 focus:border-amber-400"
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                {reminderDateOverridden && (
                                                    <span className="text-xs font-semibold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1 w-fit">
                                                        ✏️ Custom date
                                                    </span>
                                                )}
                                                {reminderDateOverridden && (
                                                    <button
                                                        type="button"
                                                        onClick={handleResetReminderDate}
                                                        className="text-xs text-amber-700 underline hover:text-amber-900"
                                                    >
                                                        Reset to org default
                                                    </button>
                                                )}
                                                {!reminderDateOverridden && (
                                                    <span className="text-xs text-amber-600 italic">
                                                        Auto-calculated from org settings
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {jobToEdit?.reminderSent && (
                                            <div className="flex items-center gap-1.5 mt-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 w-fit">
                                                <span>✓</span>
                                                <span>Reminder notification already sent</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {jobData.eventType === 'Job' && isReadOnly && jobToEdit?.reminderEnabled && jobToEdit?.reminderDate && (
                            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                <Bell size={13} />
                                <span>Reminder scheduled for <strong>{formatDateDisplay(
                                    typeof jobToEdit.reminderDate?.toDate === 'function'
                                        ? jobToEdit.reminderDate.toDate()
                                        : new Date(jobToEdit.reminderDate)
                                )}</strong></span>
                            </div>
                        )}

                                    {/* jobtidy-4d8b1f63 — Description MOVED OUT of this collapsible,
                                        up beside Job Title. It is the scope of work — the one field
                                        that says what the job IS — and it was sitting behind a
                                        disclosure triangle subtitled "notes", which is both hidden
                                        and mislabelled. Markup moved verbatim: same `description`
                                        field, same handleChange, same isReadOnly. No writer changed.
                                        What remains in here is the reminder block and the read-only
                                        legacy Job Notes list below, which is what the subtitle now
                                        says. */}

                                    {/* legnotes9-6b4e8d12 — SEAM 9. The read-only "Job Notes"
                                        block (PHASE FO-3, r1-fo-jfm-technotes-4f7b2c91) is GONE.
                                        It rendered jobToEdit.techNotes[] with a customer/internal
                                        pill and already showed nothing: Seam 8 moved every entry
                                        into organizations/{orgId}/notes and deleted the array.
                                        The same notes render in the Notes & Media strip below,
                                        with their original author and date. */}

                                </div>
                            )}
                        </div>

                    {/* wpauthor-5d3f8a26 — THE WORK PERFORMED TEXTAREA WAS REMOVED HERE
                        (it was s2-jobwp-9d4f2a61's field).

                        Work Performed is a list of authored entries now, and an entry needs
                        an author and a saved job. This form is where a job is CREATED —
                        before anyone has been to site — so there is nothing yet to attribute,
                        and its one global Save writes the whole document at once, which is the
                        wrong shape for a list whose rows belong to different people.

                        It lives in the quick view (JobQuickViewModal, renderWorkPerformed),
                        which is where staff actually work a job, and every write there is
                        attributed. This form's Notes & Media strip below is unchanged.

                        The other half of this removal is the `workPerformed` strip in the
                        dataToSave destructure — see the comment there. Removing only this
                        textarea would have left every unrelated job save silently rewriting
                        the mirror from stale form state. */}

                    {/* chklstweb-3a7f1c58 — Checklists, beside Notes & Media. Saved Jobs only. */}
                    {showChecklists && currentUserProfile?.organizationId && (
                        <JobChecklistsPanel
                            job={{ ...jobToEdit, id: occurrenceJobId }}
                            orgId={currentUserProfile.organizationId}
                            currentUserProfile={currentUserProfile}
                            currentUserId={currentUserProfile?.id}
                            checklists={jobChecklists}
                            state={checklistState}
                            focusRequest={null}
                        />
                    )}
                    {/* chklstnew-5a8c2e71 — Seam 6c: choose checklists on a NEW one-off Job; attached after Save. */}
                    {showNewJobChecklists && currentUserProfile?.organizationId && (
                        <NewJobChecklistsSection
                            orgId={currentUserProfile.organizationId}
                            selected={newJobChecklists}
                            onChange={setNewJobChecklists}
                            disabled={isSaving}
                        />
                    )}

                    {/* r1-3i-jfm-strip-2e9b4c76 — Notes & Media, mirror of QuoteDetailsView
                        r1-1a strip. Always-visible standalone strip in the form body (NOT inside
                        the Options collapsible) — collapsed to one line with a live count, so the
                        feature is discoverable without expanding anything. Flagging ON so
                        `employees` populates the Notify picker (writes FO-4 flagReviewerIds).
                        legnotes9-6b4e8d12 — FO-3's read-only legacy notes block above was
                        removed by Seam 9; this strip is now the only note surface here.
                        recurkey-7b2f9d34 — gate WAS `jobToEdit?.id`, which is truthy for a
                        PROJECTED recurring occurrence (it is the template id) and so offered a
                        note surface for a visit with no document. Now gated on the occurrence
                        itself: no document, no strip. Mobile has refused this case since
                        r1-3c-fieldtech-*; web now matches. The explanatory line below replaces
                        the strip so the absence is explained rather than mysterious. */}
                    {occurrenceJobId && currentUserProfile?.organizationId && (
                        <div>
                            <button
                                type="button"
                                onClick={() => setJobNotesExpanded(v => !v)}
                                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50"
                            >
                                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                    <MessageSquare size={16} /> Notes &amp; Media
                                    <span className="text-xs font-normal text-gray-400">({jobNotes.length})</span>
                                </span>
                                {jobNotesExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                            </button>
                            {jobNotesExpanded && (
                                <div className="mt-2 bg-white rounded-lg shadow-sm border border-gray-100 p-4 space-y-3">
                                    {/* recurkey-7b2f9d34 — attach to the occurrence, not the series */}
                                    <NoteComposer
                                        attachedTo={[{ type: 'job', id: occurrenceJobId }]}
                                        orgId={currentUserProfile.organizationId}
                                        currentUserProfile={currentUserProfile}
                                        orgUsers={employees}
                                        placeholder="Add a note or photo about this job..."
                                    />
                                    <div className="space-y-2 pt-1">
                                        {jobNotes.length === 0 ? (
                                            <p className="text-sm text-gray-500 text-center py-4">No notes yet — add the first one above.</p>
                                        ) : (
                                            /* wpauthor-5d3f8a26 — onPromote/promotePending REMOVED with this
                                               form's Work Performed box. Omitting onPromote is exactly how
                                               NoteCard hides the button (its own `promoteAvailable` gate), so
                                               no NoteCard change was needed. The "Added to Work Performed"
                                               chip still renders here — it reads the marker on the note, which
                                               the quick view writes. Promote itself lives in the quick view. */
                                            jobNotes.map(note => (
                                                <NoteCard
                                                    key={note.id}
                                                    note={note}
                                                    currentUserProfile={currentUserProfile}
                                                    orgUsers={employees}
                                                    orgId={currentUserProfile.organizationId}
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* recurkey-7b2f9d34 — the projected-occurrence case. This visit is a
                        future date of a recurring series that has no job document yet, so
                        there is nothing for a note to attach to. Saying so beats a silently
                        missing section. The "about two weeks" is not a guess: it is
                        scheduledJobGenerator's `lookaheadDays = 14`, which runs nightly, so
                        every visit inside that window already has its document (verified
                        against production: 19 occurrences in the next 14 days, 0 projected).
                        A tech standing at today's job is therefore never shown this. */}
                    {!occurrenceJobId && (isRecurringOccurrence || isSeriesTemplate) && currentUserProfile?.organizationId && (
                        <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-100">
                            <span className="flex items-center gap-2 text-sm font-semibold text-gray-500">
                                <MessageSquare size={16} /> Notes &amp; Media
                            </span>
                            <p className="mt-1 text-xs text-gray-500">
                                This repeat visit is created automatically about two weeks before the date.
                                Notes and photos can be added once it is.
                            </p>
                        </div>
                    )}

                    </div>

                    <div className="flex justify-between items-center p-6 pt-4 border-t mt-auto">
                         <div>
                            {/* CONVERT-FIX VERIFIER: r1-cvt-jfm-buttons-9d4a7e63 */}
                            {!isReadOnly && canManageJobs && jobToEdit && jobToEdit.id && (
                                jobData.recurrence?.frequency !== 'Never' ? (
                                    // Recurring: single Delete Series button (existing scope dialog, hard delete)
                                    <button
                                        onClick={handleDeleteClick}
                                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                                    >
                                        Delete Series
                                    </button>
                                ) : (
                                    // Non-recurring: Cancel Event (soft, keeps record) + Delete Job (hard).
                                    // Parity with mobile, which offers a true delete on standalone jobs.
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleDeleteClick}
                                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm"
                                        >
                                            Cancel Event
                                        </button>
                                        <button
                                            onClick={handleDeleteJobClick}
                                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                                        >
                                            Delete Job
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                        <div className="flex space-x-3">
                            <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md">
                                {isReadOnly ? 'Close' : 'Cancel'}
                            </button>
                            {!isReadOnly && (
                                <>
                                    {/* recurkey-7b2f9d34 — SAME BUG, IN MONEY. jobData spreads jobToEdit, so
                                        jobData.id is the TEMPLATE id on a recurring occurrence, and
                                        createNewInvoiceFromSource writes it straight through as the invoice's
                                        linkedJobId + linkedJobNumber. Production already carries one such
                                        invoice (#5390, linkedJobId = a recurringJobs template id). That link
                                        drives the Job # link on the invoice, the invoiceTotal write-back, the
                                        interim-labor `where('linkedJobId','==',jobId)` query, the note-group
                                        fetch, and the invoice modal's own Notes & Media key — all of which
                                        addressed the series instead of the visit.
                                        Overriding ONLY `id` keeps every other hydrated field as-is. The extra
                                        occurrenceJobId gate is belt-and-braces: a Completed visit always has a
                                        document, so it never actually hides the button. */}
                                    {jobData.eventType === 'Job' && jobToEdit && jobData.status === 'Completed' && occurrenceJobId && (
                                        <button
                                            type="button"
                                            onClick={() => onGenerateInvoice({ ...jobData, id: occurrenceJobId, ...(jobData.eventType === 'Job' ? { billLaborToCustomer } : {}) })} // laborbill-one-rule-6c2a9e47 — the readout's selection travels with the job
                                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                                        >
                                            <FileText size={16} className="mr-2" />Generate Invoice
                                        </button>
                                    )}
                                    <button 
                                        onClick={handleSave} 
                                        disabled={isSaving} 
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                    >
                                        {attachingChecklists ? 'Adding checklists…' : isSaving ? 'Saving...' : `Save ${jobData.eventType}`}{/* chklstnew-5a8c2e71 */}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* jobtimefix-4a9e2d73-b — the correction door. jobId is occurrenceJobId, never
                        jobData.id: on a recurring occurrence jobData.id is the SERIES template id
                        (recurkey-7b2f9d34), and correcting a punch against the template would write
                        to the wrong document. */}
                    {punchCorrection && (
                        <JobPunchCorrectionModal
                            isOpen={true}
                            op={punchCorrection.op}
                            orgId={currentUserProfile?.organizationId}
                            jobId={occurrenceJobId}
                            punch={punchCorrection.punch}
                            punchIndex={punchCorrection.punchIndex}
                            employees={employees}
                            billedThroughHours={billedThroughByUser[punchCorrection.punch?.userId] || 0}
                            otherClosedHours={punchCorrection.otherClosedHours}
                            currentUserProfile={currentUserProfile} /* s9harden-3c7e1a59 — own-time check */
                            onDone={refreshAfterCorrection}
                            onCancel={() => setPunchCorrection(null)}
                        />
                    )}

                    {showRecurrencePrompt && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-lg font-bold mb-4">Edit recurring event</h3>
                                <p className="mb-6">Do you want to change this event, or this and all future events in the series?</p>

                                <div className="flex flex-col space-y-2">
                                    <button 
                                        onClick={() => executeSave({ editChoice: 'only_this' })} 
                                        className="w-full text-left p-2 rounded hover:bg-gray-100"
                                    >
                                        This event only
                                    </button>
                                    <button 
                                        onClick={() => executeSave({ editChoice: 'this_and_future' })} 
                                        className="w-full text-left p-2 rounded hover:bg-gray-100"
                                    >
                                        This and future events
                                    </button>
                                    <button 
                                        onClick={() => executeSave({ editChoice: 'all' })} 
                                        className="w-full text-left p-2 rounded hover:bg-gray-100"
                                    >
                                        All events
                                    </button>
                                    <button 
                                        onClick={() => setShowRecurrencePrompt(false)} 
                                        className="mt-4 text-center p-2 text-red-600 rounded hover:bg-gray-100"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            <ResourceConflictModal
                isOpen={!!conflictDetails}
                onClose={() => setConflictDetails(null)}
                conflictDetails={conflictDetails}
            />
        </>
    );
};

export default JobFormModal;